import React, { useState, useEffect, useCallback } from 'react'
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { getLocalApiUrl, authFetch } from '../../config/api'
import {
  Search, AlertTriangle, CheckCircle2, Clock, FileText, Upload,
  User, MessageSquare, Plus, Paperclip, ChevronLeft,
  Check, X, Shield, Eye, ArrowRight, Flag,
  Activity, Users, Folder, ChevronDown, ChevronRight,
  Zap, BookOpen, Fingerprint, BarChart2, LayoutGrid, Scale, GitBranch, UserPlus, Video, PanelRightOpen,
  Table2, Banknote, ScanFace, Loader2, Trash2, Link as LinkIcon,
} from 'lucide-react'
import { getStepBrowseConfig } from '../../utils/investigationStepBrowse'
import InvestigationCanvasStrategy from './strategies/InvestigationCanvasStrategy'
import InvestigationScoredStrategy from './strategies/InvestigationScoredStrategy'
import InvestigationTreeStrategy from './strategies/InvestigationTreeStrategy'
import TypologySelector from './components/TypologySelector'
import SmrFormPanelV2 from './components/SmrFormPanelV2'
import EcddInlinePanel from './components/EcddInlinePanel'
import QuickChecksPanel from './components/QuickChecksPanel'
import CaseWorkflowTransitions from './components/CaseWorkflowTransitions'
import HelpScreenButton from '../../components/help/HelpScreenButton'
import ScreenHelpPanel from '../../components/help/ScreenHelpPanel'
import { primaryHelpSectionForTab } from './content/investigationScreenHelp'
import {
  evidenceLinkedTypeLabel,
  evidenceLinkReasonFromItem,
  formatEvidenceRowId,
} from '../../utils/evidenceBrowserLabels'

const CASE_HELP_SCREEN_KEY = 'page:compliance-safe/investigations/case'
import CddTierCard from '../profile-safe/components/CddTierCard'
import PatronComplianceStrip from '../../components/patron/PatronComplianceStrip'
import ProfileAmlMatchReviewPanel from '../../components/aml/ProfileAmlMatchReviewPanel'
import { FormsModuleGate, RecordFormsPanel } from '../forms'
import { useAuth } from '../../contexts/AuthContext'
import SofVerifyModal from './components/SofVerifyModal'
import { inferTypologyFromCase } from './typologyData/typologies'
import LinkTransactionModal from '../../components/LinkTransactionModal'
import CaseBrowseData from '../../components/CaseBrowseData'
import { useStaffMembers } from '../../hooks/useStaffMembers'
import { dedupePatronTitleLabel, patronDirectoryFullName, resolveCasePatronDisplayName, resolvePipelineStage, resolveStageLabel } from '../../utils/workspaceCaseStage'
import {
  pickStageEnteredAt,
  getStageStaleInfo,
  formatDaysInStageLabel,
  stageAgingTooltip,
  pickIncidentDate,
  getCctvRetentionStatus,
  resolveCctvEvidenceTiming,
  CCTV_RETENTION_DAYS,
} from './investigationPipelineConfig'
import { resolvePatronIdentity, subjectTypeLabel, clubOrVisitorIdLine } from '../../utils/patronIdentity'
import { usePatronLabels } from '../../contexts/FeatureFlagContext'
import OcrExtractPanel from '../../components/OcrExtractPanel'
import SmartImage from '../../components/SmartImage'
import PasteScreenshotZone, { handleClipboardImagePaste, normalizePastedScreenshotFile } from './components/PasteScreenshotZone'
import PatronActivityTimeline from './components/PatronActivityTimeline'
import AddActivityObservationModal from './components/AddActivityObservationModal'
import FloorActivityPickerModal from './components/FloorActivityPickerModal'
import { toLocalDateYmd } from '../../utils/patronActivityTimeline'
import FaceIdentifyModal from './components/FaceIdentifyModal'
import ChangeLinkedProfileModal from './components/ChangeLinkedProfileModal'
import { formatSydneyDate, formatSydneyDateTime } from '../../utils/sydneyDateTime'
import {
  fetchPatronMatches,
  patronMembershipNumber,
  patronSearchLabel,
  patronSearchKey,
  PATRON_SEARCH_MIN_CHARS,
} from '../../utils/patronProfileSearch'

const P = { pri: '#0a5169', priD: '#07384a', priM: '#e8f2f6' }

function parseCaseSnapshot(caseData) {
  const raw = caseData?.source_data_snapshot
  if (!raw) return {}
  if (typeof raw === 'object') return raw
  try {
    return JSON.parse(raw)
  } catch {
    return {}
  }
}

function VmsFaceThumbnail({ b64, label = 'VMS entry face', className = 'w-20 h-20', onSearchPatrons }) {
  if (!b64 || String(b64).trim().length < 20) return null
  return (
    <div className="shrink-0">
      <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">{label}</div>
      <SmartImage
        base64Data={b64}
        alt={label}
        className={`${className} object-cover rounded border border-slate-200 bg-white`}
        clickable
        modalTitle={label}
      />
      {onSearchPatrons ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onSearchPatrons()
          }}
          className="mt-1.5 w-full py-1 px-2 text-[10px] font-semibold rounded-md border flex items-center justify-center gap-1 bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
        >
          <ScanFace size={11} /> Search patrons
        </button>
      ) : null}
      <p className="text-[10px] text-slate-400 mt-1 max-w-[8rem]">
        {onSearchPatrons
          ? 'VMS capture — search enrolled ProfileSafe profiles.'
          : 'Visitor system capture — compare manually (not DVS).'}
      </p>
    </div>
  )
}

/** Statuses where the SMR draft / filing UI applies (matches lifecycle “SMR” phase + legacy PENDING_REVIEW). */
const SMR_WORKSPACE_STATUSES = ['SMR_DRAFT', 'SUSPICION_FORMED', 'FILED', 'PENDING_APPROVAL', 'PENDING_REVIEW']

/** Tabs that hide the patron sidebar so the main panel can use full width. */
const FULL_WIDTH_CASE_TABS = new Set(['patterns', 'canvas', 'scored', 'tree', 'ecdd_tasks'])

function caseStatusUpper(c) {
  if (!c) return ''
  const s = c.status ?? c.case_status
  return String(s != null ? s : '').trim().toUpperCase()
}

/** Show SMR Form in nav: escalated, legacy SMR case_type, SMR-pipeline status, or suspicion clock started */
function showSmrFormNav(c) {
  if (!c) return false
  if (c.outcome === 'escalate_smr' || c.outcome === 'escalate_both') return true
  if (String(c.case_type || '').toUpperCase() === 'SMR') return true
  const st = caseStatusUpper(c)
  if (SMR_WORKSPACE_STATUSES.includes(st)) return true
  if (c.suspicion_formed_at) return true
  return false
}

function showEcddTasksNav(c) {
  if (!c) return false
  if (c.linked_ecdd) return true
  if (c.outcome === 'escalate_ecdd' || c.outcome === 'escalate_both') return true
  return String(c.case_type || '').toUpperCase() === 'ECDD'
}

/** Workflow phases for the lifecycle banner (Investigation → ECDD? → SMR? → Closed). */
function buildCaseWorkflowPhases(caseData, outcomeVal, caseStatus) {
  const ecddOnPath =
    outcomeVal === 'escalate_ecdd' ||
    outcomeVal === 'escalate_both' ||
    !!caseData?.linked_ecdd ||
    String(caseData?.case_type || '').toUpperCase() === 'ECDD'

  const smrFiled =
    caseStatus === 'FILED' ||
    (caseData?.austrac_filed_at != null && String(caseData.austrac_filed_at).trim() !== '')
  const smrOnPath =
    smrFiled ||
    outcomeVal === 'escalate_smr' ||
    outcomeVal === 'escalate_both' ||
    String(caseData?.case_type || '').toUpperCase() === 'SMR' ||
    SMR_WORKSPACE_STATUSES.includes(caseStatus) ||
    !!caseData?.suspicion_formed_at

  const caseClosed = ['CLOSED', 'RESOLVED', 'FILED'].includes(caseStatus)
  const investigationDone = !!outcomeVal || caseClosed
  const ecddDone =
    ecddOnPath &&
    (caseClosed || (smrOnPath && outcomeVal !== 'escalate_ecdd'))
  const smrDone = smrFiled

  const phases = [
    {
      key: 'investigation',
      label: 'Investigation',
      active: !investigationDone,
      done: investigationDone,
    },
  ]

  if (ecddOnPath) {
    phases.push({
      key: 'ecdd',
      label: 'ECDD',
      active: !ecddDone && !caseClosed,
      done: ecddDone,
      color: '#7c3aed',
    })
  }

  if (smrOnPath) {
    phases.push({
      key: 'smr',
      label: 'SMR',
      active: !smrDone && !caseClosed,
      done: smrDone,
      color: '#dc2626',
    })
  }

  phases.push({
    key: 'closed',
    label: smrFiled ? 'Filed' : 'Closed',
    active: caseClosed,
    done: caseClosed,
  })

  return phases
}

function usesWorkflowBannerProgress(caseData, outcomeVal) {
  return Boolean(outcomeVal) || Boolean(caseData?.linked_ecdd) || showSmrFormNav(caseData)
}

function computeCaseBannerProgress({ caseData, outcomeVal, caseStatus, checklistSteps }) {
  if (!usesWorkflowBannerProgress(caseData, outcomeVal)) {
    const steps = checklistSteps || []
    const completed = steps.filter(s => s.status === 'done' || s.status === 'skipped').length
    const total = steps.length
    return {
      mode: 'checklist',
      label: 'Investigation steps',
      done: completed,
      total,
      progress: total > 0 ? Math.round((completed / total) * 100) : 0,
    }
  }

  const phases = buildCaseWorkflowPhases(caseData, outcomeVal, caseStatus)
  const phasesDone = phases.filter(p => p.done).length
  return {
    mode: 'workflow',
    label: 'Case phases',
    done: phasesDone,
    total: phases.length,
    progress: phases.length > 0 ? Math.round((phasesDone / phases.length) * 100) : 0,
    phases,
  }
}

// KYC/AML & Patterns styling (aligned with WatchlistDetail)
const C = {
  pri: '#0a5169', priL: '#1a7a9b', priM: '#e8f2f6',
  red: '#dc2626', redBg: '#fef2f2', redBd: '#fecaca',
  amber: '#d97706', amberBg: '#fffbeb', amberBd: '#fde68a',
  green: '#16a34a', greenBg: '#f0fdf4', greenBd: '#bbf7d0',
  purple: '#7c3aed', purpleBg: '#f5f3ff', purpleBd: '#ddd6fe',
  slate: '#64748b', slateL: '#94a3b8', slateBg: '#f8fafc',
}
const SC = { clear: { color: C.green, bg: C.greenBg, bd: C.greenBd, label: 'Clear' }, review: { color: C.amber, bg: C.amberBg, bd: C.amberBd, label: 'Review' }, verified: { color: C.green, bg: C.greenBg, bd: C.greenBd, label: 'Verified' } }
const VENUE_CODE_MAP = { diggers: 'TwinStars Diggers', leagues: 'TwinStars Leagues', golf: 'TwinStars Golf', sports: 'TwinStars Sports Club' }
function normalizeVenueForDisplay(v) {
  if (!v || typeof v !== 'string') return '—'
  const trimmed = v.trim()
  if (!trimmed) return '—'
  const code = trimmed.toLowerCase()
  if (VENUE_CODE_MAP[code]) return VENUE_CODE_MAP[code]
  return trimmed
}
function getDefaultVenue(c) {
  if (!c) return null
  if (c.venue_name) return c.venue_name
  const snap = c.source_data_snapshot || {}
  if (snap.Venue) return snap.Venue
  if (snap.venue) return snap.venue
  const code = (snap.venue_code || '').toLowerCase()
  if (code && VENUE_CODE_MAP[code]) return VENUE_CODE_MAP[code]
  return null
}

function humanizeSourceType(type) {
  const t = String(type || 'event').toLowerCase()
  if (t === 'payout') return 'Payout'
  if (t === 'transaction') return 'Transaction'
  if (t === 'session') return 'Session'
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : 'Event'
}

/** Prefer payout / transaction / session PK over member id for trigger reference. */
function resolveSourceTriggerRef(caseSource, snap) {
  if (snap?.payout_id) return { label: 'Payout ID', value: String(snap.payout_id) }
  const tx = snap?.TransactionPK || snap?.transaction_id
  if (tx) return { label: 'Transaction', value: String(tx) }
  const sess = snap?.SessionPK || snap?.session_id
  if (sess) return { label: 'Session', value: String(sess) }
  const raw = caseSource?.sourceDataId
  if (raw && raw !== '—') return { label: 'Source ref', value: String(raw) }
  return null
}

function memberIdMatchesHeader(memberId, patronIdentity) {
  if (memberId == null || String(memberId).trim() === '') return false
  const mid = String(memberId).trim()
  return [
    patronIdentity?.member_id,
    patronIdentity?.patron_id,
    patronIdentity?.master_profile_id,
  ].some((v) => v != null && String(v).trim() === mid)
}

/** Align seed/demo snake_case and API snapshots with Source / Trigger UI (matches backend normalizeInvestigationSnapshotKeys). */
function normalizeSourceSnapshotForDisplay(snap, caseRow) {
  if (!snap || typeof snap !== 'object') return {}
  const o = { ...snap }
  if (!o.TransactionPK && o.transaction_id) o.TransactionPK = o.transaction_id
  if (!o.SessionPK && o.session_id) o.SessionPK = o.session_id
  if (!o.MemberID && o.member_id) o.MemberID = o.member_id
  if (!o.Venue && o.venue) o.Venue = o.venue
  if (!o.EgmHouseNo && o.egm_house_no) o.EgmHouseNo = o.egm_house_no
  if (!o.EgmName && o.egm_name) o.EgmName = o.egm_name
  if (o.CashIn == null && o.cash_in != null) o.CashIn = Number(o.cash_in)
  if (o.CashOut == null && o.cash_out != null) o.CashOut = Number(o.cash_out)
  if (!o.SnapShotTime && !o.TransactionTime && o.timestamp) o.SnapShotTime = o.timestamp
  if (!o.slip_date && o.SlipDate) o.slip_date = o.SlipDate
  if (!o.SlipDate && o.slip_date) o.SlipDate = o.slip_date
  if (!o.incident_reported_at && o.reported_at) o.incident_reported_at = o.reported_at
  if (o.payout_transaction_linked == null && caseRow?.payout_transaction_link?.linked != null) {
    o.payout_transaction_linked = Boolean(caseRow.payout_transaction_link.linked)
  }
  // Do not backfill SnapShotTime from case opened — CCTV timing uses resolveCctvEvidenceTiming.
  if (!o.TransactionType && o.transaction_type) o.TransactionType = o.transaction_type
  if (!o.MemberName && o.payout_capture_name) o.MemberName = o.payout_capture_name
  return o
}

/** One row per member or visitor id (API may return repeat visits as duplicate rows). */
function dedupeVisitorsByIdentity(rows) {
  if (!Array.isArray(rows)) return []
  const seen = new Set()
  const out = []
  for (const v of rows) {
    const mid = v.member_id != null && String(v.member_id).trim() !== ''
    const key = mid ? `m:${String(v.member_id).trim()}` : v.id != null ? `v:${v.id}` : null
    if (key) {
      if (seen.has(key)) continue
      seen.add(key)
    }
    out.push(v)
  }
  return out
}

/** RXConnect row at add time — metadata only (face loaded via visit-detail). */
function buildVisitorSuspectSnapshot(v) {
  if (!v || typeof v !== 'object') return null
  const entryId = v.id ?? v.entry_id ?? v.visitor_system_ref
  return {
    entry_id: entryId != null ? String(entryId) : null,
    first_name: v.first_name || null,
    last_name: v.last_name || null,
    display_name:
      v.display_name || [v.first_name, v.last_name].filter(Boolean).join(' ') || null,
    date_of_birth: v.date_of_birth || null,
    entry_datetime: v.entry_datetime || null,
    visitor_system_ref: v.visitor_system_ref || (entryId != null ? String(entryId) : null),
    visitor_source: v.visitor_source || 'rxconnect',
    rx_profile_id: v.rx_profile_id || null,
    member_id: v.member_id || null,
    visitor_entry_type: v.visitor_entry_type || null,
    has_id_face: Boolean(v.id_face_image || v.vms_id_face_b64),
  }
}

function visitorDataFromSuspectSnapshot(snap) {
  if (!snap || typeof snap !== 'object') return null
  const display =
    snap.display_name ||
    [snap.first_name, snap.last_name].filter(Boolean).join(' ') ||
    (snap.entry_id ? `Visitor ${snap.entry_id}` : null)
  return {
    identity: {
      first_name: snap.first_name,
      last_name: snap.last_name,
      display_name: display,
      date_of_birth: snap.date_of_birth,
      visitor_system_ref: snap.visitor_system_ref || snap.entry_id,
      rx_profile_id: snap.rx_profile_id,
      member_id: snap.member_id,
    },
    visit: {
      entry_datetime: snap.entry_datetime,
      visitor_entry_type: snap.visitor_entry_type,
    },
    source: snap.visitor_source || 'rxconnect',
    has_id_face: snap.has_id_face,
  }
}

function buildSuspectPayloadFromVisitor(v) {
  const entryId = v.id ?? v.entry_id
  const displayName = dedupePatronTitleLabel(
    v.display_name ||
      [v.first_name, v.last_name].filter(Boolean).join(' ') ||
      v.visitor_system_ref ||
      `Visitor ${entryId}`
  )
  const memberId =
    v.member_id != null && String(v.member_id).trim() !== '' ? String(v.member_id).trim() : null
  return {
    visitor_id: entryId,
    ...(memberId ? { member_id: memberId, subject_type: 'member' } : { subject_type: 'visitor' }),
    display_name: displayName,
    source: 'visitor_window',
    visit_snapshot: buildVisitorSuspectSnapshot(v),
  }
}

/** One suspect row per member / visitor (duplicate seed or double-add). */
function dedupeSuspectsByIdentity(rows) {
  if (!Array.isArray(rows)) return []
  const seen = new Set()
  const out = []
  for (const s of rows) {
    const mid = s.member_id != null && String(s.member_id).trim() !== ''
    const vid = s.visitor_id != null && String(s.visitor_id).trim() !== ''
    const key = mid ? `m:${String(s.member_id).trim()}` : vid ? `v:${String(s.visitor_id).trim()}` : s.id != null ? `id:${s.id}` : null
    if (key) {
      if (seen.has(key)) continue
      seen.add(key)
    }
    out.push(s)
  }
  return out
}

function evidenceRowStableKey(it) {
  if (!it || typeof it !== 'object') return null
  const payoutId = it.payout_id || it.PayoutID || it.payoutId
  if (payoutId != null && String(payoutId).trim() !== '') return String(payoutId).trim()
  const pk =
    it.TransactionPK ??
    it.transaction_id ??
    it.SessionPK ??
    it.session_id ??
    it._evidence_source_id ??
    it.source_data_id ??
    it.id
  const s = pk != null ? String(pk).trim() : ''
  if (s && s !== '—') return s
  return null
}

function evidenceDedupeKey(it) {
  const stable = evidenceRowStableKey(it)
  if (stable) return `pk:${stable}`
  const srcTable = it._evidence_source_table
  const srcId = it._evidence_source_id
  if (srcTable && srcId) return `src:${srcTable}:${srcId}`
  const amt = Number(it.CashOut ?? it.cash_out ?? it.amount ?? 0)
  if (amt > 0) {
    const ts = it.SnapShotTime || it.timestamp || it.StartTime || it._evidence_event_time || ''
    const day = ts ? String(ts).slice(0, 10) : ''
    const venue = String(it.Venue || it.venue || '').toLowerCase()
    if (day || venue) return `soft:${amt}:${day}:${venue}`
  }
  return null
}

function dedupeEvidenceTransactionRows(items) {
  if (!Array.isArray(items)) return []
  const seen = new Set()
  return items.filter((it) => {
    if (!isLinkableEvidenceItem(it)) return false
    const key = evidenceDedupeKey(it)
    if (key) {
      if (seen.has(key)) return false
      seen.add(key)
      return true
    }
    return true
  })
}

function isLinkableEvidenceItem(it) {
  if (!it || typeof it !== 'object') return false
  const intervalKey = it.sessionpk && it.intervalstart ? `${it.sessionpk}|${it.intervalstart}` : null
  const txPk = it.TransactionPK || it.transaction_id || it.transactionId || intervalKey
  const sessPk = it.SessionPK || it.session_id || it.sessionpk
  const payoutId = it.payout_id || it.PayoutID || it.payoutId
  if (txPk || sessPk || payoutId) return true
  const srcTable = it._evidence_source_table
  const srcId = it._evidence_source_id
  if (srcId && (srcTable === 'gaming_transactions' || srcTable === 'gaming_sessions' || srcTable === 'payout_records' || srcTable === 'manual' || srcTable === 'csv' || srcTable === 'fact_slot_analysis_daily')) {
    return true
  }
  const itemType = it._evidence_item_type
  if ((itemType === 'transaction' || itemType === 'session' || itemType === 'payout' || itemType === 'combined_session' || itemType === 'combined_interval' || itemType === 'slot_jackpot') && srcId) return true
  if (srcTable === 'gaming_session_interval_attribution' && srcId) return true
  return false
}

function deriveLinkReasonFromEvidence(it) {
  return evidenceLinkReasonFromItem(it)
}

function formatLinkedTransactionDate(ts) {
  if (!ts) return '—'
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return '—'
  return formatSydneyDate(d)
}

function formatLinkedTransactionId(rawId) {
  return formatEvidenceRowId(rawId)
}

function formatLinkedTransactionMember(n) {
  const mid = String(n?.MemberID || n?.member_id || '').trim()
  if (!mid || mid === '—') {
    const spk = String(n?.SessionPK || n?.session_id || '')
    if (spk.startsWith('UNC_') || n?.player_type === 'uncarded') return 'Uncarded'
    return '—'
  }
  if (mid === '__UNCARDED__') return 'Uncarded'
  return mid
}

/** Map API evidence item → Linked Transactions table row (PascalCase + snake_case snapshots). */
function mapEvidenceItemToLinkedTransactionRow(it, fallbackDate) {
  if (!isLinkableEvidenceItem(it)) return null
  if (!it || typeof it !== 'object') return null
  const n = { ...it }
  if (!n.TransactionPK && n.transaction_id) n.TransactionPK = n.transaction_id
  if (!n.SessionPK && n.session_id) n.SessionPK = n.session_id
  if (!n.SessionPK && n.sessionpk) n.SessionPK = n.sessionpk
  if (!n.MemberID && n.member_id) n.MemberID = n.member_id
  if (n.CashIn == null && n.cash_in != null) n.CashIn = Number(n.cash_in)
  if (n.CashIn == null && n.cashin_aud != null) n.CashIn = Number(n.cashin_aud)
  if (n.CashOut == null && n.cash_out != null) n.CashOut = Number(n.cash_out)
  if (n.Turnover == null && n.turnover != null) n.Turnover = Number(n.turnover)
  if (n.Turnover == null && n.turnover_aud != null) n.Turnover = Number(n.turnover_aud)
  const ts =
    n.SnapShotTime ||
    n.IntervalStart ||
    n.intervalstart ||
    n.SessionStart ||
    n.sessionstart ||
    n.StartTime ||
    n.timestamp ||
    n.TransactionTime ||
    n.EventTime ||
    n.trading_day ||
    n.created_at ||
    fallbackDate
  const dateStr = formatLinkedTransactionDate(ts)
  const venueRaw = n.Venue || n.venue || n.venue_code || ''
  const payoutId = n.payout_id || n.PayoutID || n.payoutId
  const rowId =
    evidenceRowStableKey(n) ||
    (payoutId != null ? String(payoutId) : null) ||
    (n.source_data_id != null && String(n.source_data_id).trim() !== '' ? String(n.source_data_id) : null) ||
    '—'
  const isPayout = Boolean(payoutId || n._evidence_item_type === 'payout' || n._evidence_source_table === 'payout_records')
  const isCombinedSession = n._evidence_item_type === 'combined_session' || n.evidence_category === 'combined_session'
  const isCombinedInterval = n._evidence_item_type === 'combined_interval' || n.evidence_category === 'combined_interval'
  const isSlotJackpot = n._evidence_item_type === 'slot_jackpot' || n.evidence_category === 'slot_jackpot' || n._evidence_source_table === 'fact_slot_analysis_daily'
  const isSession = Boolean(n.SessionPK || n.session_id) && !isCombinedSession && !isCombinedInterval && !isSlotJackpot
  const amountRaw =
    n.CashOut ??
    n.cash_out ??
    n.amount ??
    n.jackpot_win_aud ??
    n.jackpot_win ??
    n.creditwin_aud ??
    n.credit_win ??
    n.CashIn ??
    n.cash_in ??
    n.Turnover ??
    n.turnover ??
    n.turnover_aud ??
    0
  const amountNum = Number(amountRaw) || 0
  // Session / combined rows often store cents in DB — heuristics for display dollars
  const amount =
    amountNum >= 10000 && (isSession || isCombinedSession || isCombinedInterval)
      ? amountNum / 100
      : amountNum
  const egm = n.EgmHouseNo || n.egm_house_no || n.egm_pk || n.egm_id || n.game_id || '—'
  const typeLabel = evidenceLinkedTypeLabel(n)
  return {
    id: String(rowId),
    idDisplay: formatLinkedTransactionId(rowId),
    idFull: String(rowId),
    date: dateStr,
    type: typeLabel,
    amount,
    memberId: formatLinkedTransactionMember(n),
    linkReason: deriveLinkReasonFromEvidence(n),
    egm,
    zone: normalizeVenueForDisplay(venueRaw),
  }
}

function formatSnapshotDeviceChanges(v) {
  if (v == null || v === '') return null
  if (typeof v === 'number' && !Number.isNaN(v)) return String(v)
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v)
    } catch {
      return String(v)
    }
  }
  return String(v)
}

const VIDEO_VENUE_CODE_MAP = { diggers: 'TwinStars Diggers', leagues: 'TwinStars Leagues', golf: 'TwinStars Golf', sports: 'TwinStars Sports Club' }

function getVideoEvidenceParams(caseRow, snap, sourceDataId) {
  const params = resolveCctvEvidenceTiming(caseRow, snap, { sourceDataId })
  if (!params.venue && snap?.venue_code) {
    const code = String(snap.venue_code).toLowerCase()
    if (VIDEO_VENUE_CODE_MAP[code]) {
      return { ...params, venue: VIDEO_VENUE_CODE_MAP[code] }
    }
  }
  return params
}
function formatDateOnly(dateStr) {
  if (!dateStr || dateStr === '—') return '—'
  // Handle ISO datetime strings by extracting just the date part
  const datePart = String(dateStr).split('T')[0]
  // If it's already in YYYY-MM-DD format, format it nicely
  const d = new Date(datePart + 'T00:00:00')
  if (Number.isNaN(d.getTime())) return datePart || '—'
  return formatSydneyDate(d)
}

function formatVideoEvidenceLocalDisplay(isoOrLocal) {
  if (!isoOrLocal) return '—'
  const d = new Date(isoOrLocal)
  if (Number.isNaN(d.getTime())) return '—'
  return formatSydneyDateTime(d)
}

function CctvRetentionBanner({ caseRow, snapshot }) {
  const retention = getCctvRetentionStatus(pickIncidentDate(caseRow, snapshot))
  if (!retention) return null

  const incidentLabel = formatSydneyDate(retention.incidentDate)
  const bg = retention.overdue ? '#fef2f2' : retention.urgent ? '#fffbeb' : '#fff7ed'
  const border = retention.overdue ? '#fecaca' : retention.urgent ? '#fde68a' : '#fed7aa'
  const color = retention.overdue ? '#dc2626' : retention.urgent ? '#d97706' : '#ea580c'
  const message = retention.overdue
    ? `CCTV footage may already be overwritten — the ${retention.retentionDays}-day retention window from the incident on ${incidentLabel} has passed. Request footage from security urgently if not yet secured.`
    : `CCTV footage may be overwritten in ${retention.daysLeft} day${retention.daysLeft === 1 ? '' : 's'} (${retention.retentionDays}-day retention from incident on ${incidentLabel}). Request and secure footage before it is lost.`

  return (
    <div className="rounded-lg border-2 p-4 mb-4 flex gap-3 items-start" style={{ borderColor: border, background: bg }}>
      <AlertTriangle size={18} style={{ color, flexShrink: 0, marginTop: 2 }} />
      <div>
        <div className="text-sm font-bold" style={{ color }}>CCTV retention warning</div>
        <div className="text-sm mt-1 text-slate-700">{message}</div>
        {!retention.overdue && (
          <div className="text-xs mt-1.5 font-semibold" style={{ color }}>
            {retention.daysLeft} day{retention.daysLeft === 1 ? '' : 's'} remaining
          </div>
        )}
      </div>
    </div>
  )
}

function VideoEvidenceCard({
  venue, egms, from, to, mode, sessionSpanStartIso, sessionSpanEndIso,
  transactionAnchorIso, anchorLabel, windowLabel, usedCaseOpenedFallback,
  egmCameraRows = [], camerasLoading = false,
  isEstimated = false, needsPayoutFloorLink = false,
}) {
  const hasData = venue || egms.length > 0 || from || to
  if (!hasData) return null
  const hasSessionSpan = sessionSpanStartIso && sessionSpanEndIso
  const spanLabel = mode === 'session' || mode === 'session_full_day'
    ? 'Session on EGM (start → end)'
    : 'Time window (start → end)'

  const cameraByHouse = Object.fromEntries(
    (egmCameraRows || []).map((row) => [String(row.house_number || ''), row])
  )

  const renderEgmCameras = () => {
    if (camerasLoading) {
      return (
        <span className="inline-flex items-center gap-1.5 text-slate-500 text-sm">
          <Loader2 size={14} className="animate-spin" />
          Loading cameras…
        </span>
      )
    }
    if (!egms.length) {
      return <span className="text-sm text-slate-500">—</span>
    }
    return (
      <ul className="space-y-1.5">
        {egms.map((egm) => {
          const row = cameraByHouse[egm] || egmCameraRows.find(
            (r) => String(r.house_number) === String(egm) || String(r.egm_id) === String(egm)
          )
          const linked = row?.linked && (row.cameras || []).length > 0
          return (
            <li key={egm} className="text-sm">
              <span className="font-mono text-slate-600 text-[11px] mr-1.5">{egm}</span>
              {linked ? (
                (row.cameras || []).map((cam, i) => (
                  <span
                    key={`${cam.camera_id}-${i}`}
                    className="inline-flex items-center rounded-md bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-emerald-900 font-semibold mr-1 mb-0.5"
                  >
                    {cam.label || cam.camera_id}
                  </span>
                ))
              ) : (
                <span className="text-amber-800 font-medium">Not linked</span>
              )}
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <div className="rounded-lg border-2 p-4 mb-4" style={{ borderColor: P.pri, background: `${P.priM}40` }}>
      <div className="flex items-center gap-2 mb-3">
        <Video size={18} color={P.pri} />
        <span className="text-sm font-bold" style={{ color: P.pri }}>Get Video Evidence</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        {venue && (
          <div>
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Venue</div>
            <div className="font-semibold text-slate-800">{venue}</div>
          </div>
        )}
        {egms.length > 0 && (
          <div>
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">EGM{egms.length > 1 ? 's' : ''}</div>
            <div className="font-semibold text-slate-800 font-mono">{egms.join(', ')}</div>
          </div>
        )}
      </div>
      {hasSessionSpan && (
        <div className="mt-3 text-sm">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">{spanLabel}</div>
          <div className="font-semibold text-slate-800">
            {formatVideoEvidenceLocalDisplay(sessionSpanStartIso)}
            {' → '}
            {formatVideoEvidenceLocalDisplay(sessionSpanEndIso)}
          </div>
        </div>
      )}
      {transactionAnchorIso && !hasSessionSpan && (
        <div className="mt-3 text-sm">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">
            {anchorLabel || 'Event time'}
          </div>
          <div className="font-semibold text-slate-800">{formatVideoEvidenceLocalDisplay(transactionAnchorIso)}</div>
          {usedCaseOpenedFallback && (
            <p className="text-[11px] text-amber-700 mt-1">
              Event time was not on the case — using when the investigation was opened.
            </p>
          )}
          {isEstimated && (
            <p className="text-[11px] text-amber-700 mt-1">
              Estimated about 60 minutes before payout time — link the floor transaction in Payout Monitoring or below to confirm.
            </p>
          )}
        </div>
      )}
      {(from && to) && (
        <div className="mt-3 pt-3 border-t border-slate-200/90">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">
                {windowLabel || 'Suggested CCTV review window'}
              </div>
              <div className="font-semibold text-slate-800">
                {formatVideoEvidenceLocalDisplay(from)}
                {' → '}
                {formatVideoEvidenceLocalDisplay(to)}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">
                CCTV camera
              </div>
              {renderEgmCameras()}
              {!camerasLoading && egms.length > 0 && !(egmCameraRows || []).some((r) => r.linked) && (
                <p className="text-[11px] text-slate-500 mt-1">
                  Link cameras in Group Config → EGM Registry (Edit EGM → Cameras).
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function PayoutFloorLinkBanner({ caseRow, snapshot }) {
  const snap = snapshot && typeof snapshot === 'object' ? snapshot : {}
  const isPayout =
    snap.payout_id ||
    snap.source === 'payout' ||
    caseRow?.rule_id === 'PAYOUT_ESCALATION' ||
    caseRow?.payout_transaction_link?.payoutId
  if (!isPayout) return null
  const linked =
    snap.payout_transaction_linked === true ||
    snap.payout_floor_linked === true ||
    caseRow?.payout_transaction_link?.linked === true
  if (linked) return null
  return (
    <div
      className="rounded-lg border-2 p-4 mb-4 flex gap-3 items-start"
      style={{ borderColor: '#fde68a', background: '#fffbeb' }}
    >
      <LinkIcon size={18} style={{ color: '#d97706', flexShrink: 0, marginTop: 2 }} />
      <div>
        <div className="text-sm font-bold text-amber-900">Map floor transaction</div>
        <p className="text-sm mt-1 text-amber-950/90">
          This payout is not linked to floor activity yet. Accept a transaction link in{' '}
          <strong>Payout Monitoring → Transaction linking</strong>, or use{' '}
          <strong>Linked Transactions → Link Additional Transaction</strong>, before relying on CCTV timing.
        </p>
        <p className="text-xs mt-1.5 text-amber-800">
          Until linked, the review window below uses an estimate (~60 minutes before payout time).
        </p>
      </div>
    </div>
  )
}

function VideoEvidenceSection({ caseRow, snapshot, sourceDataId }) {
  const params = getVideoEvidenceParams(caseRow, snapshot, sourceDataId)
  const [egmCameraRows, setEgmCameraRows] = useState([])
  const [camerasLoading, setCamerasLoading] = useState(false)

  const venueId =
    caseRow?.venue_code ||
    caseRow?.venue_venue_id ||
    snapshot?.venue_code ||
    ''

  const egmKey = (params.egms || []).join(',')

  useEffect(() => {
    const egms = params.egms || []
    if (!egms.length) {
      setEgmCameraRows([])
      return undefined
    }
    let cancelled = false
    setCamerasLoading(true)
    const qs = new URLSearchParams({ house_numbers: egms.join(',') })
    if (venueId) qs.set('venue_id', String(venueId))
    authFetch(getLocalApiUrl(`/api/egms/cameras/suggest?${qs}`))
      .then((r) => r.json())
      .then((j) => {
        if (!cancelled) setEgmCameraRows(Array.isArray(j.data) ? j.data : [])
      })
      .catch(() => {
        if (!cancelled) setEgmCameraRows([])
      })
      .finally(() => {
        if (!cancelled) setCamerasLoading(false)
      })
    return () => { cancelled = true }
  }, [egmKey, venueId])

  return (
    <VideoEvidenceCard
      {...params}
      egmCameraRows={egmCameraRows}
      camerasLoading={camerasLoading}
      isEstimated={params.isEstimated}
      needsPayoutFloorLink={params.needsPayoutFloorLink}
    />
  )
}
const fmt$ = (n) => '$' + (n ?? 0).toLocaleString()
function KycBadge({ s, label }) {
  const m = SC[s] || SC.clear
  return <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold" style={{ background: m.bg, color: m.color, border: `1px solid ${m.bd}` }}>{label || m.label}</span>
}

/** Strip system backfill text from step notes (never show “migration” / internal job labels in UI). */
function sanitizeStepNotesForUi(notes) {
  const n = notes == null ? '' : String(notes)
  if (!n.trim()) return ''
  if (/migration\s*104/i.test(n) || /SMR pipeline backfill/i.test(n)) return ''
  return n
}

function isEvidenceImage(mimeType, fileName) {
  if (mimeType && String(mimeType).toLowerCase().startsWith('image/')) return true
  return /\.(jpe?g|png|gif|webp|bmp)$/i.test(String(fileName || ''))
}

function evidenceDownloadUrl(caseId, stepId, evidenceId, { inline = false } = {}) {
  const qs = inline ? '?inline=1' : ''
  return getLocalApiUrl(
    `/api/cases/${encodeURIComponent(caseId)}/steps/${encodeURIComponent(stepId)}/evidence-files/${encodeURIComponent(evidenceId)}${qs}`
  )
}

function EvidenceFileThumb({ caseId, stepId, evidence, size = 48, className = '' }) {
  const fileName = evidence?.file_name || 'file'
  const isImage = Boolean(evidence?.id && stepId && isEvidenceImage(evidence.mime_type, fileName))
  const [src, setSrc] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!isImage) {
      setSrc(null)
      setFailed(false)
      return undefined
    }
    let revoked = null
    setSrc(null)
    setFailed(false)
    authFetch(evidenceDownloadUrl(caseId, stepId, evidence.id, { inline: true }))
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.blob()
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob)
        revoked = objectUrl
        setSrc(objectUrl)
      })
      .catch(() => setFailed(true))
    return () => {
      if (revoked) URL.revokeObjectURL(revoked)
    }
  }, [caseId, stepId, evidence?.id, isImage])

  const dim = { width: size, height: size }
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className={`object-cover rounded border border-slate-200 flex-shrink-0 bg-slate-100 ${className}`}
        style={dim}
      />
    )
  }
  if (isImage && !failed) {
    return (
      <div
        className={`flex items-center justify-center rounded border border-slate-200 bg-slate-100 flex-shrink-0 ${className}`}
        style={dim}
      >
        <Clock size={14} className="text-slate-400" />
      </div>
    )
  }
  return <FileText size={size >= 40 ? 18 : 13} color={P.pri} className={`flex-shrink-0 ${className}`} />
}

function mapStep(s) {
  const evidence = (s.evidence || []).map(e => {
    if (typeof e === 'string') return { file_name: e, file_path: null, id: null, mime_type: null }
    return {
      file_name: e.file_name || e.name || 'file',
      file_path: e.file_path || e.url || null,
      id: e.id || null,
      mime_type: e.mime_type || null,
    }
  })
  return {
    id: String(s.id),
    category: s.category || '',
    title: s.title || '',
    status: s.status || 'not_started',
    assignedTo: s.assigned_to || null,
    completedDate: s.completed_date ? (typeof s.completed_date === 'string' ? s.completed_date : new Date(s.completed_date).toISOString().slice(0, 16).replace('T', ' ')) : null,
    notes: sanitizeStepNotesForUi(s.notes),
    evidence,
  }
}

function mapActivity(a) {
  const created = a.created_at ? new Date(a.created_at) : null
  const dateStr = created ? created.toISOString().slice(0, 16).replace('T', ' ') : ''
  let type = 'note'
  if (a.activity_type === 'STATUS_CHANGE' && a.title?.toLowerCase().includes('created')) type = 'open'
  if (a.activity_type === 'NOTE') type = 'note'
  return { date: dateStr, who: a.created_by_name || a.created_by || '—', action: a.title || a.description || '—', type }
}

const statusMeta = {
  done: { label: 'Complete', color: '#16a34a', bg: '#f0fdf4', icon: CheckCircle2 },
  in_progress: { label: 'In Progress', color: '#d97706', bg: '#fffbeb', icon: Activity },
  not_started: { label: 'Not Started', color: '#6b7280', bg: '#f9fafb', icon: Clock },
  skipped: { label: 'Skipped', color: '#64748b', bg: '#f1f5f9', icon: X },
}

const categoryColors = {
  'Gaming & Transaction Review': '#0a5169',
  'Staff Observations': '#7c3aed',
  'CCTV Review': '#0891b2',
  'External Checks': '#059669',
  'Source of Funds': '#b45309',
}

const fmtCurrency = (n) => '$' + (Number(n) || 0).toLocaleString()

function Pill({ color, bg, label, size = 'sm', title }) {
  return (
    <span
      title={title}
      className="inline-block whitespace-nowrap font-semibold rounded-full"
      style={{
        background: bg, color, border: `1px solid ${color}22`,
        padding: size === 'sm' ? '2px 10px' : '4px 14px',
        fontSize: size === 'sm' ? 11 : 12, letterSpacing: 0.3,
      }}
    >
      {label}
    </span>
  )
}

function LinkedTransactionsSection({ transactions, loading, onLinkMore }) {
  return (
    <SectionCard title="Linked Transactions" icon="💳">
      <div className="text-xs text-slate-500 mb-3.5">
        The play that opened this investigation, plus any other sessions, intervals, or payouts linked to it.
      </div>
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            {['ID', 'Date', 'Type', 'Amount', 'Member', 'Link reason', 'Venue'].map(h => (
              <th key={h} className="text-left py-2 px-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && transactions.length === 0 ? (
            <tr>
              <td colSpan={7} className="py-4 px-3 text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                  <Loader2 size={12} className="animate-spin" />
                  Loading linked transactions…
                </span>
              </td>
            </tr>
          ) : null}
          {!loading && transactions.length === 0 ? (
            <tr>
              <td colSpan={7} className="py-4 px-3 text-slate-500">No linked transactions yet.</td>
            </tr>
          ) : null}
          {transactions.map((t, i) => (
            <tr key={`${t.id}-${t.linkReason}-${i}`} className="border-b border-slate-100" style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
              <td className="py-2 px-3 font-mono text-slate-700 max-w-[140px] truncate" title={t.idFull || t.id}>{t.idDisplay || t.id}</td>
              <td className="py-2 px-3 text-slate-700 whitespace-nowrap">{t.date}</td>
              <td className="py-2 px-3 text-slate-700">{t.type}</td>
              <td className="py-2 px-3 font-semibold text-red-600 tabular-nums">{fmtCurrency(t.amount)}</td>
              <td className="py-2 px-3 text-slate-700">{t.memberId}</td>
              <td className="py-2 px-3 text-slate-600 max-w-[160px] leading-snug">{t.linkReason}</td>
              <td className="py-2 px-3 text-slate-700">{t.zone}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-slate-200 bg-slate-50">
            <td colSpan={3} className="py-2 px-3 font-semibold text-slate-700">Total ({transactions.length} linked)</td>
            <td className="py-2 px-3 font-semibold text-red-600 tabular-nums">{fmtCurrency(transactions.reduce((a, t) => a + (Number(t.amount) || 0), 0))}</td>
            <td colSpan={3} />
          </tr>
        </tfoot>
      </table>
      <button
        type="button"
        onClick={onLinkMore}
        className="mt-3.5 text-xs py-1.5 px-3.5 rounded-md border font-semibold flex items-center gap-1"
        style={{ borderColor: P.pri, background: P.priM, color: P.pri }}
      >
        <Plus size={12} /> Link Additional Transaction
      </button>
    </SectionCard>
  )
}

function SectionCard({ title, icon, children, action, className = '' }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-lg overflow-hidden mb-4 ${className}`}>
      <div className="flex items-center justify-between py-3 px-4 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-800">
          {icon && <span className="text-base">{icon}</span>}
          {title}
        </div>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  )
}

function StepRow({ caseId, step, onExpand, expanded, onAttachEvidence, onBrowseData, onMarkInProgress, onMarkComplete, onSkip, onAssign, onSaveNotes, savingStepId, onViewEvidence, onDeleteEvidence, deletingEvidenceId }) {
  const browseConfig = getStepBrowseConfig(step)
  const meta = statusMeta[step.status] || statusMeta.not_started
  const StatusIcon = meta.icon
  const catColor = categoryColors[step.category] || P.pri
  const [notesDraft, setNotesDraft] = useState(step.notes || '')
  const isSaving = savingStepId === step.id
  useEffect(() => { setNotesDraft(step.notes || '') }, [step.notes])

  const handleSaveNotes = () => {
    if (notesDraft !== (step.notes || '') && onSaveNotes) onSaveNotes(step, notesDraft)
  }

  return (
    <div className="border border-slate-200 rounded-lg mb-2 overflow-hidden">
      <div
        onClick={onExpand}
        className={`flex items-center gap-3 py-3 px-4 cursor-pointer transition-colors ${expanded ? 'bg-slate-50' : 'bg-white'}`}
      >
        <StatusIcon size={16} color={meta.color} className="flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-800">{step.title}</div>
          <div className="text-[11px] text-slate-400 mt-0.5">
            <span className="px-1.5 py-0.5 rounded-full font-semibold text-[10px]" style={{ background: catColor + '15', color: catColor }}>{step.category}</span>
            {step.assignedTo && <span className="ml-2">→ {step.assignedTo}</span>}
            {step.completedDate && <span className="ml-2">✓ {step.completedDate.split(' ')[0]}</span>}
          </div>
        </div>
        <Pill color={meta.color} bg={meta.bg} label={meta.label} />
        {step.evidence.length > 0 && (
          <span className="flex items-center gap-1 text-[11px] text-slate-500">
            <Paperclip size={12} />{step.evidence.length}
          </span>
        )}
        {expanded ? <ChevronDown size={15} className="text-slate-400" /> : <ChevronRight size={15} className="text-slate-400" />}
      </div>

      {expanded && (
        <div className="border-t border-slate-100 p-4 bg-slate-50">
          <div className="mb-3">
            <div className="text-[11px] font-bold text-slate-500 mb-1.5 uppercase tracking-wider">Findings / Notes</div>
            <textarea
              value={notesDraft}
              onChange={e => setNotesDraft(e.target.value)}
              onBlur={handleSaveNotes}
              onPaste={(e) => {
                handleClipboardImagePaste(e, (f) => onAttachEvidence?.(step, f), {
                  disabled: isSaving,
                  uploading: isSaving,
                })
              }}
              placeholder="Record findings, observations, and outcomes for this step… (paste screenshots here with Cmd+V)"
              className="w-full min-h-[80px] text-sm text-slate-700 border border-slate-200 rounded-md p-2.5 resize-y box-border"
            />
            {isSaving && <span className="text-[11px] text-slate-500">Saving…</span>}
          </div>

          {step.evidence.length > 0 && (
            <div className="mb-3">
              <div className="text-[11px] font-bold text-slate-500 mb-1.5 uppercase tracking-wider">Evidence Attached</div>
              {step.evidence.map((f, i) => (
                <div key={f.id || i} className="flex items-center gap-2 py-1.5 px-2.5 bg-white border border-slate-200 rounded-md mb-1.5">
                  <EvidenceFileThumb caseId={caseId} stepId={step.id} evidence={f} size={36} />
                  <span className="text-xs text-slate-700 flex-1 truncate">{f.file_name || 'file'}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onViewEvidence?.(step, f) }}
                    className="p-1 hover:bg-slate-100 rounded"
                    title="View evidence"
                  >
                    <Eye size={13} className="text-slate-500 hover:text-slate-700" />
                  </button>
                  {f.id ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onDeleteEvidence?.(step, f) }}
                      disabled={deletingEvidenceId === f.id}
                      className="p-1 hover:bg-red-50 rounded disabled:opacity-50"
                      title="Delete evidence"
                    >
                      {deletingEvidenceId === f.id
                        ? <Loader2 size={13} className="animate-spin text-slate-400" />
                        : <Trash2 size={13} className="text-red-500 hover:text-red-700" />}
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            {browseConfig && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onBrowseData?.(step, browseConfig) }}
                className="text-xs py-1.5 px-3.5 rounded-md border font-semibold flex items-center gap-1"
                style={{ borderColor: P.pri, background: '#fff', color: P.pri }}
              >
                {browseConfig.subTab === 'payouts' ? <Banknote size={12} /> : <Table2 size={12} />}
                {browseConfig.label}
              </button>
            )}
            <PasteScreenshotZone
              compact
              uploading={isSaving}
              onImageFile={(f) => onAttachEvidence?.(step, f)}
            />
            {step.status === 'not_started' && (
              <>
                <button type="button" onClick={() => onMarkInProgress?.(step)} className="text-xs py-1.5 px-3.5 rounded-md border font-semibold" style={{ borderColor: '#d97706', background: '#fffbeb', color: '#d97706' }}>
                  Mark In Progress
                </button>
                <button type="button" onClick={() => onSkip?.(step)} className="text-xs py-1.5 px-3.5 rounded-md border font-semibold" style={{ borderColor: '#6b7280', background: '#f1f5f9', color: '#6b7280' }}>
                  Skip
                </button>
              </>
            )}
            {step.status === 'in_progress' && (
              <button type="button" onClick={() => onMarkComplete?.(step)} className="text-xs py-1.5 px-3.5 rounded-md border font-semibold flex items-center gap-1" style={{ borderColor: '#16a34a', background: '#f0fdf4', color: '#16a34a' }}>
                <Check size={12} /> Mark Complete
              </button>
            )}
            <button type="button" onClick={() => onAssign?.(step)} className="text-xs py-1.5 px-3.5 rounded-md border font-semibold flex items-center gap-1 bg-white text-slate-500 border-slate-200">
              <User size={12} /> Assign
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function TimelineEvent({ event }) {
  const typeIcon = {
    open: { icon: Folder, color: P.pri },
    complete: { icon: CheckCircle2, color: '#16a34a' },
    assign: { icon: User, color: '#7c3aed' },
    upload: { icon: Paperclip, color: '#0891b2' },
    note: { icon: MessageSquare, color: '#d97706' },
  }[event.type] || { icon: Activity, color: '#64748b' }
  const Icon = typeIcon.icon

  return (
    <div className="flex gap-3 mb-3.5">
      <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center border mt-0.5" style={{ background: typeIcon.color + '15', borderColor: typeIcon.color + '33' }}>
        <Icon size={13} color={typeIcon.color} />
      </div>
      <div className="flex-1">
        <div className="text-xs text-slate-700"><strong>{event.who}</strong> — {event.action}</div>
        <div className="text-[11px] text-slate-400 mt-0.5">{event.date}</div>
      </div>
    </div>
  )
}

function OutcomePanel({ outcome, setOutcome, rationale, setRationale, onFinalise, saving, alreadyFinalised }) {
  const options = [
    { key: 'close_no_action', label: 'Close — No Further Action', desc: 'Insufficient grounds to escalate. Investigation inconclusive.', color: '#6b7280', icon: X },
    { key: 'escalate_ecdd', label: 'Escalate to ECDD', desc: 'Elevated risk confirmed — open an Enhanced Customer Due Diligence case.', color: '#7c3aed', icon: Shield },
    { key: 'escalate_smr', label: 'Escalate to SMR', desc: 'Suspicion formed on reasonable grounds — submit a Suspicious Matter Report.', color: '#dc2626', icon: AlertTriangle },
    { key: 'escalate_both', label: 'Escalate to ECDD + SMR', desc: 'Suspicion formed and ongoing service requires parallel ECDD.', color: '#d97706', icon: Zap },
  ]

  return (
    <SectionCard title="Investigation Outcome" icon="🏁" action={alreadyFinalised ? <Pill color="#16a34a" bg="#f0fdf4" label="Finalised" /> : outcome ? <Pill color="#d97706" bg="#fffbeb" label="Outcome Selected" /> : null}>
      {alreadyFinalised && (
        <div className="text-xs text-slate-500 mb-4 p-2.5 rounded-lg border" style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
          <strong className="text-green-800">Outcome has been finalised.</strong> The investigation has been resolved as: <strong>{options.find(o => o.key === outcome)?.label || outcome}</strong>
        </div>
      )}
      <div className="text-xs text-slate-500 mb-4 p-2.5 rounded-lg border" style={{ background: '#fffbeb', borderColor: '#fde68a' }}>
        <strong className="text-amber-800">Tipping-off reminder</strong> — Do not disclose to the patron that they are under investigation. Recording your outcome here does not notify the patron.
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-4">
        {options.map(opt => {
          const Icon = opt.icon
          const selected = outcome === opt.key
          return (
            <div
              key={opt.key}
              onClick={() => !alreadyFinalised && setOutcome(opt.key)}
              className={`rounded-lg p-3 transition-all border ${alreadyFinalised ? 'opacity-60' : 'cursor-pointer'}`}
              style={{
                borderWidth: selected ? 2 : 1,
                borderColor: selected ? opt.color : '#e2e8f0',
                background: selected ? opt.color + '08' : '#fff',
              }}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <Icon size={14} color={opt.color} />
                <span className="text-xs font-bold" style={{ color: opt.color }}>{opt.label}</span>
              </div>
              <div className="text-[11px] text-slate-500 leading-snug">{opt.desc}</div>
            </div>
          )
        })}
      </div>

      {outcome && (
        <div className="mb-3">
          <div className="text-[11px] font-bold text-slate-500 mb-1.5 uppercase tracking-wider">Outcome Rationale (required)</div>
          <textarea
            value={rationale}
            onChange={e => setRationale(e.target.value)}
            disabled={alreadyFinalised}
            placeholder="Summarise the grounds for this outcome decision — what did the investigation find, and why does it support (or not support) escalation?"
            className="w-full min-h-[90px] text-sm text-slate-700 border border-slate-200 rounded-md p-2.5 resize-y box-border"
          />
        </div>
      )}

      {outcome && !alreadyFinalised && (
        <button
          onClick={onFinalise}
          disabled={saving || !rationale?.trim()}
          className="w-full py-2.5 rounded-md border-none text-sm font-bold text-white flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
          style={{ background: P.pri }}
        >
          <Check size={14} /> {saving ? 'Saving…' : 'Finalise Outcome'}
          {outcome === 'escalate_ecdd' && ' — Open ECDD'}
          {outcome === 'escalate_smr' && ' — Start SMR'}
          {outcome === 'escalate_both' && ' — Open ECDD + Start SMR'}
          {outcome === 'close_no_action' && ' — Close Investigation'}
          <ArrowRight size={14} />
        </button>
      )}
    </SectionCard>
  )
}

const STEP_CATEGORIES = ['Gaming & Transaction Review', 'Staff Observations', 'CCTV Review', 'External Checks', 'Source of Funds']

/** Snapshot / KYC strip label after Manual Verify (none vs confirmed vs pending). */
function resolveInvestigationAmlLabel(kycAml = {}, amlChecks = [], alerts = []) {
  const decision = kycAml.aml_analyst_decision || amlChecks[0]?.analyst_decision || null
  if (decision === 'no_match') return 'No match'
  if (kycAml.aml_derived_category) return kycAml.aml_derived_category
  if (kycAml.is_pep) return 'PEP'
  if (decision === 'confirmed_match') {
    return kycAml.aml_derived_category || 'Match confirmed'
  }
  if (amlChecks[0]?.result_count > 0 && !decision) {
    return `${amlChecks[0].result_count} for review`
  }
  if (alerts.length > 0) return '1 for review'
  return 'Clear'
}

function investigationAmlNeedsReview(kycAml = {}, amlChecks = [], alerts = []) {
  const decision = kycAml.aml_analyst_decision || amlChecks[0]?.analyst_decision || null
  if (decision === 'no_match') return false
  if (decision === 'confirmed_match') return true
  return !!(kycAml.aml_derived_category || kycAml.is_pep || (amlChecks[0]?.result_count > 0) || alerts.length > 0)
}

function AmlModal({ data, onClose, onReviewSaved, C }) {
  const results = data?.results || []
  const [saving, setSaving] = useState(false)
  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-[1100px] bg-white shadow-xl z-50 flex flex-col">
        <div className="flex items-center justify-between py-3.5 px-4 border-b border-slate-200 shrink-0">
          <div>
            <div className="text-base font-bold text-slate-800">
              {results.length === 0 ? 'Screening clear' : 'AML/PEP match review'}
            </div>
            <div className="text-xs text-slate-500">
              {data?.name}
              {data?.checkId ? ` · Check #${data.checkId}` : ''}
              {data?.country ? ` · ${data.country}` : ''}
              {results.length > 0 ? ` · ${results.length} results` : ''}
            </div>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-md border border-slate-200 bg-white flex items-center justify-center cursor-pointer text-slate-500">
            <X size={16} />
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 min-h-0">
          {results.length === 0 ? (
            <div className="rounded-lg p-6 border text-center" style={{ background: C.greenBg, borderColor: C.greenBd }}>
              <CheckCircle2 size={28} color={C.green} className="mx-auto mb-2" />
              <div className="text-base font-bold" style={{ color: C.green }}>No matches found</div>
              <div className="text-xs text-slate-600 mt-1">No PEP · No Sanctions · No Adverse Media</div>
            </div>
          ) : (
            <ProfileAmlMatchReviewPanel
              results={results}
              searchName={data?.name || ''}
              checkId={data?.checkId || null}
              initialDecision={data?.analystDecision || null}
              initialReason={data?.analystReason || ''}
              saving={saving}
              onSaveReview={async ({ decision, reason, selected_match }) => {
                if (!data?.checkId) throw new Error('Check id missing')
                setSaving(true)
                try {
                  const resp = await authFetch(
                    getLocalApiUrl(`/api/aml/checks/${encodeURIComponent(String(data.checkId))}/review`),
                    {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ decision, reason, selected_match }),
                    }
                  )
                  const json = await resp.json().catch(() => ({}))
                  if (!resp.ok || !json?.success) throw new Error(json?.error || 'Failed to save review')
                  onReviewSaved?.(decision, reason)
                } finally {
                  setSaving(false)
                }
              }}
            />
          )}
        </div>
      </div>
    </>
  )
}

function KycAmlTab({ memberId, isValidMemberId, overview, verificationCapture, amlChecks, amlLoading, patronName, onReScreen, onViewAmlCheck, KycBadge, fmt$, C, SC }) {
  if (!isValidMemberId) {
    return (
      <SectionCard title="KYC & AML Status" icon="🪪">
        <p className="text-sm text-slate-500">No member linked to this case. KYC and AML data require a patron profile.</p>
      </SectionCard>
    )
  }
  if (overview.loading && !overview.data) {
    return (
      <SectionCard title="KYC & AML Status" icon="🪪">
        <p className="text-sm text-slate-500">Loading patron data…</p>
      </SectionCard>
    )
  }
  if (overview.error) {
    return (
      <SectionCard title="KYC & AML Status" icon="🪪">
        <p className="text-sm text-red-600">{overview.error}</p>
      </SectionCard>
    )
  }
  const profile = overview.data?.profile || {}
  const kycAml = overview.data?.kyc_aml || {}
  const capture = verificationCapture?.data
  const captureOcr = capture?.ocrData || null
  const captureDvsStatus = capture?.dvsStatus ? String(capture.dvsStatus).toUpperCase() : null
  const kycCompleted = !!kycAml.kyc_completed
  const dvsStatus = (captureDvsStatus || kycAml.dvs_status || 'PENDING').toUpperCase()
  const dvsVerified = ['VERIFIED', 'MATCHED', 'PASSED'].includes(dvsStatus)
  const alerts = overview.data?.alerts || []
  const amlLabel = resolveInvestigationAmlLabel(kycAml, amlChecks, alerts)
  const amlStatus = investigationAmlNeedsReview(kycAml, amlChecks, alerts) ? 'review' : 'clear'

  return (
    <>
      <SectionCard
        title="KYC — Know Your Customer"
        icon="🪪"
        action={
          <div className="flex items-center gap-2">
            <KycBadge s={kycCompleted ? 'verified' : 'review'} label={kycCompleted ? 'Verified' : 'Pending'} />
          </div>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">Identity Fields</div>
            {[
              {
                label: 'Full Legal Name',
                value: patronName || [captureOcr?.given_name, captureOcr?.family_name].filter(Boolean).join(' ') || '—',
                verified: !!patronName || !!(captureOcr?.given_name || captureOcr?.family_name),
              },
              {
                label: 'Date of Birth',
                value: formatDateOnly(profile.date_of_birth || captureOcr?.date_of_birth),
                verified: !!(profile.date_of_birth || captureOcr?.date_of_birth),
              },
              {
                label: 'Residential Address',
                value: (profile.address || profile.address1 || profile.residential_address || profile.suburb)
                  ? [profile.address || profile.address1 || profile.residential_address, profile.address2, profile.suburb || profile.city, profile.state, profile.postcode].filter(Boolean).join(', ')
                  : (captureOcr?.address_line
                    ? [captureOcr.address_line, captureOcr.address_suburb, captureOcr.address_state, captureOcr.address_postcode].filter(Boolean).join(', ')
                    : '—'),
                verified: false,
                note: captureOcr?.address_line && !(profile.address || profile.address1) ? 'From ID OCR' : 'Self-declared',
              },
              { label: 'Occupation', value: profile.occupation || '—', verified: false, note: 'Self-declared' },
              { label: 'Phone', value: profile.phone || '—', verified: !!profile.phone },
              { label: 'Email', value: profile.email || '—', verified: !!profile.email },
            ].map((f, i) => (
              <div key={i} className="flex gap-2 py-2 border-b border-slate-100 last:border-0">
                {f.verified ? <CheckCircle2 size={14} color={C.green} className="flex-shrink-0 mt-0.5" /> : <AlertTriangle size={14} color={C.amber} className="flex-shrink-0 mt-0.5" />}
                <div>
                  <div className="text-xs text-slate-500">{f.label}</div>
                  <div className="text-sm font-semibold text-slate-800">{f.value}</div>
                  {f.note && <div className="text-xs text-amber-600 mt-0.5">{f.note}</div>}
                </div>
              </div>
            ))}
          </div>
          <div>
            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">ID capture &amp; DVS</div>
            {captureOcr ? (
              <OcrExtractPanel
                ocrData={captureOcr}
                title="ID document (OCR capture)"
              />
            ) : verificationCapture?.loading ? (
              <div className="text-xs text-slate-500 mb-3">Loading ID capture…</div>
            ) : (
              <div className="rounded-lg p-3 mb-3 border border-slate-200 bg-slate-50 text-xs text-slate-500">
                No ID OCR on file for this patron yet.
              </div>
            )}
            <div className={`rounded-lg p-3 mb-2 border ${dvsVerified ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-bold text-slate-800">DVS status</span>
                <KycBadge s={dvsVerified ? 'verified' : 'review'} label={dvsVerified ? 'Verified' : dvsStatus} />
              </div>
              <div className="text-xs text-slate-600">
                {dvsVerified
                  ? `Verified via DVS${capture?.dvsProvider ? ` (${capture.dvsProvider})` : ''}`
                  : `DVS: ${dvsStatus}${captureOcr ? ' — OCR captured, verification pending' : ''}`}
              </div>
              {capture?.queueId ? (
                <Link
                  to="/profile-safe/verification-queue"
                  className="inline-block mt-2 text-xs font-semibold"
                  style={{ color: P.pri }}
                >
                  View in verification queue →
                </Link>
              ) : null}
            </div>
            <Link to={`/profile-safe/search?q=${encodeURIComponent(memberId)}`} className="text-xs font-semibold" style={{ color: P.pri }}>View full profile →</Link>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="AML / PEP Screening"
        icon="🛡️"
        action={
          <div className="flex items-center gap-2">
            <KycBadge s={amlStatus} label={amlLabel} />
            <button type="button" onClick={onReScreen} disabled={amlLoading} className="text-xs py-1.5 px-3 rounded-md border font-semibold flex items-center gap-1 bg-slate-50 text-slate-600 border-slate-200 disabled:opacity-70 disabled:cursor-wait">
              {amlLoading ? 'Screening…' : 'Re-screen'}
            </button>
          </div>
        }
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {[
            { key: 'pep', icon: '👤', label: 'PEP Screening', status: kycAml.aml_pep_match ? 'review' : 'clear', detail: kycAml.aml_pep_match ? 'PEP match — domestic or foreign.' : 'No PEP match.' },
            { key: 'sanctions', icon: '🚫', label: 'Sanctions Lists', status: kycAml.aml_sanctions_match ? 'review' : 'clear', detail: kycAml.aml_sanctions_match ? 'Listed on sanctions lists.' : 'Not listed on sanctions lists.' },
            { key: 'adverse', icon: '📰', label: 'Adverse Media', status: alerts.length > 0 ? 'review' : 'clear', detail: alerts.length > 0 ? 'Review required.' : 'No adverse media matches.' },
            { key: 'watchlists', icon: '🔎', label: 'Intelligence Watchlists', status: 'clear', detail: 'Not listed on AUSTRAC or AFP watchlists.' },
          ].map(cat => {
            const m = SC[cat.status] || SC.clear
            return (
              <div key={cat.key} className="rounded-lg p-3 border" style={{ borderColor: m.bd, borderTopWidth: 3, borderTopColor: m.color, background: m.bg }}>
                <div className="text-lg mb-1">{cat.icon}</div>
                <div className="flex items-center gap-1 mb-1">
                  {cat.status === 'clear' ? <CheckCircle2 size={12} color={m.color} /> : <AlertTriangle size={12} color={m.color} />}
                  <span className="text-xs font-bold" style={{ color: m.color }}>{m.label}</span>
                </div>
                <div className="text-xs font-bold text-slate-800">{cat.label}</div>
                <div className="text-xs text-slate-600 leading-snug">{cat.detail}</div>
              </div>
            )
          })}
        </div>
        <div className="rounded-lg p-3 bg-slate-50 border border-slate-200">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">Screening History</div>
          {amlChecks.length > 0 ? amlChecks.map((c, i) => (
            <div key={c.id} className="flex gap-2 items-start py-2 border-b border-slate-100 last:border-0">
              {c.result_count > 0 ? <AlertTriangle size={13} color={C.amber} className="flex-shrink-0 mt-0.5" /> : <CheckCircle2 size={13} color={C.green} className="flex-shrink-0 mt-0.5" />}
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-700">SCR-{c.id} · {c.result_count} match{c.result_count !== 1 ? 'es' : ''} · {c.reason || 'Screening'}</div>
                <div className="text-[11px] text-slate-400">{c.performed_by || 'System'} · {c.created_at ? formatSydneyDateTime(new Date(c.created_at)) : ''}</div>
              </div>
              <button type="button" onClick={() => onViewAmlCheck(c.id)} className="text-xs font-semibold flex items-center gap-1" style={{ color: C.priL }}><Eye size={12} /> View</button>
            </div>
          )) : (
            <div className="flex gap-2 py-2">
              <CheckCircle2 size={13} color={C.green} className="flex-shrink-0 mt-0.5" />
              <div className="text-xs text-slate-600">No screening on record — click Re-screen to run AML/PEP check</div>
            </div>
          )}
        </div>
      </SectionCard>
    </>
  )
}

function mergeCaseSnapshotPayoutIntoTrend(weeks, caseRow) {
  if (!caseRow || !Array.isArray(weeks)) return weeks
  const snap = (() => {
    try {
      return typeof caseRow.source_data_snapshot === 'string'
        ? JSON.parse(caseRow.source_data_snapshot || '{}')
        : (caseRow.source_data_snapshot || {})
    } catch {
      return {}
    }
  })()
  const amount = Number(snap.amount ?? snap.CashOut ?? snap.cash_out ?? 0)
  const tsRaw = snap.timestamp ?? snap.SnapShotTime ?? caseRow.created_at
  if (!amount || !tsRaw) return weeks
  const ts = new Date(tsRaw).getTime()
  if (!Number.isFinite(ts)) return weeks
  const weekMs = 7 * 86400000
  const now = Date.now()
  const out = [...weeks]
  for (let i = 0; i < 12; i++) {
    const weekEnd = now - (11 - i) * weekMs
    const weekStart = weekEnd - weekMs
    if (ts >= weekStart && ts < weekEnd) {
      out[i] += amount
      break
    }
  }
  return out
}

function formatVisitDayLabel(raw) {
  if (!raw) return null
  const s = String(raw).trim()
  const key = /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : null
  const d = key ? new Date(`${key}T12:00:00`) : new Date(s)
  if (Number.isNaN(d.getTime())) return null
  return formatSydneyDate(d)
}

function PatternsTab({ overview, caseData, isValidMemberId, fmt$, C }) {
  const memberId = overview.data?.requested_member_id || overview.data?.member_id || ''
  const [visitPattern, setVisitPattern] = useState(null)
  const [visitPatternLoading, setVisitPatternLoading] = useState(false)
  const [gamingBehaviour, setGamingBehaviour] = useState(null)

  useEffect(() => {
    if (!memberId) return
    let cancelled = false
    setVisitPatternLoading(true)
    authFetch(getLocalApiUrl(`/api/members/${encodeURIComponent(memberId)}/visit-pattern?months=12`))
      .then(r => r.json())
      .then(j => { if (!cancelled && j.success) setVisitPattern(j.data) })
      .catch(() => {})
      .finally(() => { if (!cancelled) setVisitPatternLoading(false) })
    authFetch(getLocalApiUrl(`/api/members/${encodeURIComponent(memberId)}/gaming-behaviour?months=12`))
      .then(r => r.json())
      .then(j => { if (!cancelled && j.success) setGamingBehaviour(j.data) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [memberId])

  if (!isValidMemberId) {
    return (
      <SectionCard title="Behaviour & Patterns" icon="📊">
        <p className="text-sm text-slate-500">No member linked to this case. Pattern data requires a patron profile.</p>
      </SectionCard>
    )
  }
  if (overview.loading && !overview.data) {
    return (
      <SectionCard title="Behaviour & Patterns" icon="📊">
        <p className="text-sm text-slate-500">Loading patron data…</p>
      </SectionCard>
    )
  }
  const transactions = overview.data?.transactions || []
  const sessions = overview.data?.sessions || []
  const sessionRows = sessions.map(s => {
    const start = s.start_time ? new Date(s.start_time) : null
    const end = s.end_time ? new Date(s.end_time) : null
    const durMs = start && end ? end - start : 0
    const durM = Math.floor(durMs / 60000)
    const dur = durM >= 60 ? `${Math.floor(durM / 60)}h ${durM % 60}m` : `${durM}m`
    const cashIn = s.CashIn ?? 0
    const payout = s.credit_win ?? s.CreditWin ?? 0
    return {
      id: s.SessionPK,
      date: start ? formatSydneyDate(start) : '-',
      egm: s.egmhouseno || s.egm_house_no || s.EgmHouseNo || '-',
      zone: s.venue || '-',
      cashIn,
      payout,
      total: payout,
    }
  })
  const payouts = transactions.map(t => ({ amount: t.CashOut ?? t.CashIn ?? 0 }))
  const _totalPayoutFromTx = payouts.reduce((a, p) => a + p.amount, 0)
  const apiPayoutTrend = overview.data?.payout_trend_12w
  let payoutWeeks = Array.isArray(apiPayoutTrend) && apiPayoutTrend.length === 12
    ? [...apiPayoutTrend]
    : (() => {
        const weekMs = 7 * 86400000
        const now = Date.now()
        return Array(12).fill(0).map((_, i) => {
          const weekEnd = now - (11 - i) * weekMs
          const weekStart = weekEnd - weekMs
          let sum = 0
          transactions.forEach(t => {
            const ts = t.snapshot_time ? new Date(t.snapshot_time).getTime() : 0
            if (ts >= weekStart && ts < weekEnd) sum += (t.CashOut ?? t.CashIn ?? 0)
          })
          sessions.forEach(s => {
            const ts = s.start_time ? new Date(s.start_time).getTime() : 0
            if (ts >= weekStart && ts < weekEnd) sum += (s.credit_win ?? s.CreditWin ?? 0)
          })
          return sum
        })
      })()
  if (!payoutWeeks.some((v) => v > 0)) {
    payoutWeeks = mergeCaseSnapshotPayoutIntoTrend(payoutWeeks, caseData)
  }
  const hasPayoutTrend = payoutWeeks.some((v) => v > 0)
  const dowNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  const visitDow = visitPattern?.has_data
    ? dowNames.map((dayName, i) => {
        const fromApi = (visitPattern.dow_counts || []).find((v) => v?.day === dayName)
          || visitPattern.dow_counts?.[i]
        const n = Number(fromApi?.count ?? fromApi?.n ?? 0)
        return { d: dayName, n: Number.isFinite(n) ? n : 0 }
      })
    : sessions.length > 0
      ? dowNames.map((d, i) => {
          const jsDay = i === 6 ? 0 : i + 1
          const count = sessions.filter(s => {
            const t = s.start_time ? new Date(s.start_time) : null
            return t && t.getDay() === jsDay
          }).length
          return { d, n: count }
        })
      : dowNames.map(d => ({ d, n: 0 }))
  const maxDow = Math.max(...visitDow.map(v => v.n), 1)
  const hasVisitBars = visitDow.some((v) => v.n > 0)
  const maxWeek = Math.max(...payoutWeeks, 1)

  // EGM preferences — prefer API history (12 months), fall back to current session window
  const topEgms = gamingBehaviour?.has_data && (gamingBehaviour.top_egms || []).length > 0
    ? gamingBehaviour.top_egms.slice(0, 5).map(e => ({
        egm: e.egm_pk || '—',
        sessions: e.session_count,
        total: e.total_credit_win ?? 0,
        pct: e.pct,
      }))
    : (() => {
        const egmCounts = {}
        sessionRows.forEach(s => {
          const egm = s.egm || '-'
          if (!egmCounts[egm]) egmCounts[egm] = { sessions: 0, total: 0 }
          egmCounts[egm].sessions += 1
          egmCounts[egm].total += s.payout || 0
        })
        const prefs = Object.entries(egmCounts).map(([egm, d]) => ({ egm, sessions: d.sessions, total: d.total, pct: 0 }))
        const maxS = Math.max(...prefs.map(e => e.sessions), 1)
        prefs.forEach(e => { e.pct = Math.round((e.sessions / maxS) * 100) })
        return prefs.sort((a, b) => b.sessions - a.sessions).slice(0, 5)
      })()

  const totalCashIn = gamingBehaviour?.total_cash_in ?? sessionRows.reduce((a, s) => a + (s.cashIn || 0), 0)
  const totalPayout2 = gamingBehaviour?.total_credit_win ?? payouts.reduce((a, p) => a + p.amount, 0)
  const avgDurationMin = gamingBehaviour?.avg_duration_min
  const avgSessionDurLabel = avgDurationMin != null
    ? avgDurationMin >= 60 ? `~${Math.floor(avgDurationMin / 60)}h ${avgDurationMin % 60}m` : `~${avgDurationMin}m`
    : sessionRows.length ? '—' : '—'

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <SectionCard title="12-Week Payout Trend" icon="📦">
          {hasPayoutTrend ? (
            <div className="flex items-end gap-1 h-32">
              {payoutWeeks.map((v, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className="w-full rounded-t min-h-[2px]"
                    style={{ height: `${Math.max(2, (v / maxWeek) * 100)}px`, background: i >= 10 ? C.red : i >= 8 ? C.amber : C.priL }}
                    title={v > 0 ? fmt$(v) : undefined}
                  />
                  <div className="text-[10px] text-slate-400">W{i + 1}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-500 py-6 text-center">
              No payouts in the last 12 weeks from gaming activity or PaySafeGo payout records.
            </p>
          )}
          <div className="flex gap-3 mt-2 text-xs text-slate-500">
            <span style={{ color: C.priL }}>■ Earlier weeks</span>
            <span style={{ color: C.red }}>■ Recent weeks</span>
            <span className="text-slate-400">· gaming + payout listing</span>
          </div>
        </SectionCard>
        <SectionCard title="Visit Pattern — Day of Week" icon="📅">
          {visitPatternLoading && !visitPattern && (
            <p className="text-xs text-slate-400 py-2">Loading visit history…</p>
          )}
          {hasVisitBars ? (
            <div className="flex items-end gap-1 h-32">
              {visitDow.map((v, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className="text-[10px] font-semibold text-slate-600 tabular-nums h-3">
                    {v.n > 0 ? v.n : ''}
                  </div>
                  <div
                    className="w-full rounded-t"
                    style={{
                      height: `${Math.max(v.n > 0 ? 8 : 2, (v.n / maxDow) * 96)}px`,
                      background: v.n === maxDow && v.n > 0 ? C.amber : C.priL,
                    }}
                    title={`${v.d}: ${v.n} visit day${v.n !== 1 ? 's' : ''}`}
                  />
                  <div className="text-[10px] text-slate-400">{v.d}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-500 py-6 text-center">
              {visitPatternLoading ? 'Loading visit history…' : 'No visit-day pattern available yet.'}
            </p>
          )}
          <div className="mt-2 space-y-1">
            {visitPattern?.has_data ? (
              <>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                  <span>Peak day: <strong className="text-slate-700">{visitPattern.peak_dow || '—'}</strong></span>
                  <span>Avg/week: <strong className="text-slate-700">{visitPattern.avg_visits_per_week ?? '—'}</strong></span>
                  <span>Avg/month: <strong className="text-slate-700">{visitPattern.avg_visits_per_month ?? '—'}</strong></span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                  <span>{visitPattern.total_visit_days ?? 0} visit days · {visitPattern.total_sessions ?? 0} sessions over 12 months</span>
                  {formatVisitDayLabel(visitPattern.last_visit_date) && (
                    <span>
                      Last visit:{' '}
                      <strong className="text-slate-600">{formatVisitDayLabel(visitPattern.last_visit_date)}</strong>
                    </span>
                  )}
                </div>
              </>
            ) : !visitPatternLoading ? (
              <p className="text-xs text-slate-400 text-center">No gaming session history — chart uses current window sessions only</p>
            ) : null}
          </div>
        </SectionCard>
      </div>

      <SectionCard title="EGM Preferences" icon="🎰">
        {topEgms.length === 0 ? (
          <p className="text-xs text-slate-500">No session data yet</p>
        ) : (
          topEgms.map((e, i) => (
            <div key={e.egm} className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0">
              <span className="font-mono text-sm font-bold w-16 flex-shrink-0" style={{ color: i === 0 ? C.red : '#1e293b' }}>{e.egm}</span>
              <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden">
                <div className="h-full rounded transition-all" style={{ width: `${e.pct}%`, background: i === 0 ? C.red : i === 1 ? C.amber : C.priL }} />
              </div>
              <span className="text-xs font-bold text-slate-800 w-16 text-right">{e.sessions} sessions</span>
              <span className="text-xs text-slate-500 w-16">{fmt$(e.total)}</span>
            </div>
          ))
        )}
      </SectionCard>

      <SectionCard title="Gaming Behaviour Profile" icon="📊">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {[
            ['Avg session length', avgSessionDurLabel, false],
            ['Peak arrival time', gamingBehaviour?.peak_hour_label ?? '—', false],
            ['Avg cash-in per session',
              gamingBehaviour?.avg_cash_in != null
                ? fmt$(Math.round(gamingBehaviour.avg_cash_in))
                : (sessionRows.length ? fmt$(Math.round(totalCashIn / sessionRows.length)) : '—'),
              false],
            ['Payout-to-cash-in ratio',
              gamingBehaviour?.payout_ratio != null
                ? `${gamingBehaviour.payout_ratio.toFixed(2)}×`
                : (sessionRows.length && totalPayout2 > 0 ? `${(totalPayout2 / Math.max(totalCashIn, 1)).toFixed(2)}×` : '—'),
              false],
            ['Multi-session days (12 mo)',
              gamingBehaviour?.multi_session_days != null ? String(gamingBehaviour.multi_session_days) : '—',
              false],
            ['Total sessions (12 mo)',
              gamingBehaviour?.total_sessions != null ? String(gamingBehaviour.total_sessions) : (sessionRows.length ? String(sessionRows.length) : '—'),
              false],
            ['Favourite EGM',
              topEgms.length ? topEgms[0].egm : '—',
              false],
            ['Zone preference', sessionRows[0]?.zone || '—', false],
            ['First recorded session',
              gamingBehaviour?.first_session
                ? formatSydneyDate(new Date(gamingBehaviour.first_session))
                : '—',
              false],
          ].map(([l, v, flag], i) => (
            <div key={i} className="p-3 rounded-lg border border-slate-100">
              <div className="text-[11px] text-slate-500 mb-1">{l}</div>
              <div className="text-sm font-bold flex items-center gap-1" style={{ color: flag ? C.red : '#1e293b' }}>{v} {flag && <AlertTriangle size={12} color={C.amber} />}</div>
            </div>
          ))}
        </div>
        {gamingBehaviour?.has_data && (
          <p className="text-xs text-slate-400 mt-2">Based on {gamingBehaviour.total_sessions} sessions over 12 months.</p>
        )}
      </SectionCard>
    </>
  )
}

function SuspectsTab({ suspects, onRemove, onConfirm, onViewDetails, expandedId, onToggleExpand, suspectDetails, suspectDetailsLoading, onAddMember, onIdentifyFromPhoto, onSearchPatronsFromFace }) {
  const labels = usePatronLabels()
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [results, setResults] = useState([])
  const [searchError, setSearchError] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const [selected, setSelected] = useState(null)
  const [adding, setAdding] = useState(false)
  const [addError, setAddError] = useState('')
  const searchSeqRef = React.useRef(0)

  const runSearch = async () => {
    const q = query.trim()
    setSelected(null)
    setAddError('')
    setHasSearched(true)
    if (q.length < PATRON_SEARCH_MIN_CHARS) {
      setResults([])
      setSearchError(`Enter at least ${PATRON_SEARCH_MIN_CHARS} characters to search.`)
      return
    }
    const seq = ++searchSeqRef.current
    setSearching(true)
    setSearchError('')
    try {
      const rows = await fetchPatronMatches(q)
      if (searchSeqRef.current !== seq) return
      setResults(rows)
      if (rows.length === 0) setSearchError('No matching members found.')
    } catch (e) {
      if (searchSeqRef.current !== seq) return
      setResults([])
      setSearchError(e?.message || 'Search failed')
    } finally {
      if (searchSeqRef.current === seq) setSearching(false)
    }
  }

  const handleAddSelected = async () => {
    const membershipNumber = patronMembershipNumber(selected)
    if (!membershipNumber && !selected?.MasterProfileID) return
    setAdding(true)
    setAddError('')
    try {
      const ok = await onAddMember({
        member_id: membershipNumber || String(selected.MasterProfileID),
        display_name: patronSearchLabel(selected),
      })
      if (ok !== false) {
        setQuery('')
        setResults([])
        setSelected(null)
        setHasSearched(false)
        setSearchError('')
      }
    } catch (e) {
      setAddError(e?.message || 'Could not add member')
    } finally {
      setAdding(false)
    }
  }

  return (
    <SectionCard title="Suspects" icon="👤">
      <p className="text-xs text-slate-500 mb-3">
        Candidates identified during the investigation. Expand to view AML/KYC details. Confirm one to set as the case subject.
      </p>
      <div className="flex flex-col sm:flex-row gap-2 mb-2">
        <button
          type="button"
          onClick={onIdentifyFromPhoto}
          className="inline-flex items-center justify-center gap-1.5 text-xs py-2 px-3 rounded border font-semibold shrink-0"
          style={{ borderColor: P.pri, background: '#fff', color: P.pri }}
        >
          <ScanFace size={14} /> Identify from photo
        </button>
        <input
          type="text"
          placeholder="Member name or membership #"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setSelected(null)
            setAddError('')
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              if (!searching) runSearch()
            }
          }}
          className="flex-1 text-sm border border-slate-200 rounded p-2 min-w-0"
          autoComplete="off"
        />
        <button
          type="button"
          onClick={runSearch}
          disabled={searching || !query.trim()}
          className="inline-flex items-center justify-center gap-1.5 text-xs py-1.5 px-3 rounded border font-semibold shrink-0 disabled:opacity-50"
          style={{ borderColor: P.pri, background: '#fff', color: P.pri }}
        >
          {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          {searching ? 'Searching…' : 'Search'}
        </button>
        <button
          type="button"
          onClick={handleAddSelected}
          disabled={adding || !selected || (!patronMembershipNumber(selected) && !selected.MasterProfileID)}
          className="text-xs py-1.5 px-3 rounded border font-semibold shrink-0 disabled:opacity-50"
          style={{ borderColor: P.pri, background: P.priM, color: P.pri }}
          title={selected ? `Add ${patronSearchLabel(selected)}` : 'Select a search result first'}
        >
          {adding ? 'Adding…' : 'Add selected'}
        </button>
      </div>

      {hasSearched && (
        <div className="mb-3 rounded-md border border-slate-200 bg-white max-h-48 overflow-y-auto">
          {searchError && results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-500">{searchError}</div>
          ) : (
            results.map((p) => {
              const key = patronSearchKey(p)
              const active = selected && patronSearchKey(selected) === key
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setSelected(p); setAddError('') }}
                  className={`w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-0 hover:bg-slate-50 ${active ? 'bg-teal-50 ring-1 ring-inset ring-teal-200' : ''}`}
                >
                  <div className="font-semibold text-slate-800">{patronSearchLabel(p)}</div>
                  <div className="text-[11px] text-slate-500 font-mono">
                    {patronMembershipNumber(p) ? `Membership ${patronMembershipNumber(p)}` : 'No membership number'}
                    {p.MasterProfileID ? ` · ${p.MasterProfileID}` : ''}
                  </div>
                </button>
              )
            })
          )}
        </div>
      )}

      {selected && (patronMembershipNumber(selected) || selected.MasterProfileID) && (
        <div className="mb-3 text-xs text-slate-600">
          Selected: <span className="font-semibold">{patronSearchLabel(selected)}</span>
          {patronMembershipNumber(selected) ? (
            <span className="font-mono text-slate-500"> Membership {patronMembershipNumber(selected)}</span>
          ) : null}
          <span className="text-slate-400"> — click Add selected</span>
        </div>
      )}
      {addError && (
        <div className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">{addError}</div>
      )}
      {suspects.length === 0 ? (
        <div className="py-8 text-center text-slate-400 text-sm">No suspects added yet. Use the Narrow tab to add {labels.visitors.toLowerCase()} or search for members.</div>
      ) : (
        <div className="space-y-2">
          {suspects.map(s => {
            const name = dedupePatronTitleLabel(
              s.display_name || (s.member_id ? `Member ${s.member_id}` : `${labels.visitor} ${s.visitor_id}`)
            )
            const expanded = expandedId === s.id
            const details = suspectDetails[s.id]
            const loading = suspectDetailsLoading[s.id]
            return (
              <div key={s.id} className="border border-slate-200 rounded-lg overflow-hidden">
                <div
                  className="flex items-center justify-between py-3 px-4 cursor-pointer hover:bg-slate-50"
                  onClick={() => onToggleExpand(s.id)}
                >
                  <div className="flex items-center gap-2">
                    <ChevronRight size={16} className={`transition-transform ${expanded ? 'rotate-90' : ''}`} />
                    <User size={16} color={P.pri} />
                    <span className="text-sm font-semibold text-slate-800">{name}</span>
                    {s.subject_type === 'member' || s.member_id ? (
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">Member</span>
                    ) : (
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-violet-100 text-violet-800">{labels.visitor}</span>
                    )}
                    <span className="text-[11px] text-slate-400">({s.source})</span>
                    {s.identification_confidence != null && Number(s.identification_confidence) > 0 && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-teal-50 text-teal-800 border border-teal-200">
                        Face {Math.round(Number(s.identification_confidence) * 100)}%
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                    <button type="button" onClick={(e) => { e.stopPropagation(); onViewDetails(s); }} className="text-xs py-1 px-2 rounded border font-semibold flex items-center gap-1" style={{ borderColor: P.pri, background: P.priM, color: P.pri }}>
                      <Eye size={11} /> Details
                    </button>
                    {s.member_id && (
                      <button type="button" onClick={() => onConfirm(s)} className="text-xs py-1 px-2 rounded border font-semibold flex items-center gap-1 bg-green-50 border-green-200 text-green-700">
                        <Check size={11} /> Confirm
                      </button>
                    )}
                    <button type="button" onClick={() => onRemove(s.id)} className="text-xs py-1 px-2 rounded border border-red-200 text-red-600 hover:bg-red-50">
                      <X size={11} />
                    </button>
                  </div>
                </div>
                {expanded && (
                  <div className="border-t border-slate-200 bg-slate-50 p-4">
                    {loading ? (
                      <div className="text-sm text-slate-500">Loading details…</div>
                    ) : details ? (
                      <div className="text-sm space-y-2">
                        {details.visitorData && (
                          <>
                            <div className="flex gap-3 items-start">
                              <div className="flex-1 min-w-0">
                                <div className="text-[11px] font-bold text-slate-500 uppercase mb-1">Identity</div>
                                <div className="text-slate-700">{dedupePatronTitleLabel(details.visitorData.identity?.display_name || '—')} · {details.visitorData.identity?.visitor_system_ref || '—'}</div>
                                {(details.visitorData.identity?.email || details.visitorData.identity?.phone) && (
                                  <div className="text-slate-600 text-xs mt-0.5">{[details.visitorData.identity.email, details.visitorData.identity.phone].filter(Boolean).join(' · ')}</div>
                                )}
                                {details.visitorData.visit?.entry_datetime && (
                                  <div className="text-slate-600 text-xs mt-0.5">Visit: {formatSydneyDateTime(new Date(details.visitorData.visit.entry_datetime))}</div>
                                )}
                                {details.visitorData.identity?.rx_profile_id && (
                                  <div className="text-slate-600 text-xs mt-0.5">RX profile: {details.visitorData.identity.rx_profile_id}</div>
                                )}
                                {details.visitorData.visit?.visitor_entry_type && (
                                  <div className="text-slate-600 text-xs mt-0.5">Type: {details.visitorData.visit.visitor_entry_type}</div>
                                )}
                              </div>
                              <VmsFaceThumbnail
                                b64={details.visitorData.vms_id_face_b64 || details.visitorData.id_face_image}
                                onSearchPatrons={
                                  onSearchPatronsFromFace
                                    ? () => onSearchPatronsFromFace(
                                        details.visitorData.vms_id_face_b64 || details.visitorData.id_face_image
                                      )
                                    : undefined
                                }
                              />
                            </div>
                            <div>
                              <div className="text-[11px] font-bold text-slate-500 uppercase mb-1">KYC Status</div>
                              <div className="text-slate-700">DVS: {details.visitorData.kyc_status?.dvs_status ?? '—'} · Completed: {details.visitorData.kyc_status?.kyc_completed ? 'Yes' : 'No'}</div>
                            </div>
                            {details.visitorData.visitor_history?.length > 0 && (
                              <div>
                                <div className="text-[11px] font-bold text-slate-500 uppercase mb-1">Visit History</div>
                                <div className="text-slate-700">{details.visitorData.visitor_history.length} visit(s)</div>
                              </div>
                            )}
                            {!details.visitorData.linked_member_id && (
                              <div className="text-slate-500 text-xs">Link to a patron profile to run AML checks.</div>
                            )}
                          </>
                        )}
                        {details.overview && (
                          <div>
                            <div className="text-[11px] font-bold text-slate-500 uppercase mb-1">Risk / KYC</div>
                            <div className="text-slate-700">Risk: {details.overview?.risk_score ?? '—'} | KYC: {details.overview?.kyc_aml?.kyc_completed ? 'Verified' : 'Pending'}</div>
                          </div>
                        )}
                        {details.amlChecks?.length > 0 && (
                          <div>
                            <div className="text-[11px] font-bold text-slate-500 uppercase mb-1">AML Checks</div>
                            <div className="text-slate-700">{details.amlChecks.length} check(s) — latest: {details.amlChecks[0]?.result_count ?? 0} matches</div>
                          </div>
                        )}
                        {!details.overview && !details.amlChecks?.length && !details.visitorData && s.member_id && (
                          <div className="text-slate-500">No overview/AML data for this member yet.</div>
                        )}
                        {!details.overview && !details.amlChecks?.length && !details.visitorData && !s.member_id && (
                          <div className="text-slate-500">No details available.</div>
                        )}
                      </div>
                    ) : (
                      <div className="text-slate-500 text-sm">No details loaded. Click Details to fetch.</div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </SectionCard>
  )
}

function NarrowingPanel({ caseId, caseRow, caseSnapshot, caseSourceDataId, visitors, loading, onLoad, onAddSuspect, suspects, visitorsHint, addHint, embeddedInToolsPanel = false }) {
  const [addingEntryId, setAddingEntryId] = useState(null)
  const params = getVideoEvidenceParams(caseRow, caseSnapshot || {}, caseSourceDataId)
  const [venues, setVenues] = useState([])
  const [venue, setVenue] = useState(params.venue || '')
  const [from, setFrom] = useState(params.from || '')
  const [to, setTo] = useState(params.to || '')
  const addedIds = new Set(suspects.map(s => (s.visitor_id ? `v_${s.visitor_id}` : s.member_id)))
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/venues'))
        const j = await r.json()
        if (j.success && Array.isArray(j.data) && !cancelled) setVenues(j.data)
      } catch {}
    })()
    return () => { cancelled = true }
  }, [])
  useEffect(() => {
    if (params.venue && !venue) setVenue(params.venue)
    if (params.from && !from) setFrom(params.from)
    if (params.to && !to) setTo(params.to)
  }, [params.venue, params.from, params.to])
  return (
    <SectionCard
      title="Narrow Down"
      icon="🔍"
      className={embeddedInToolsPanel ? 'flex flex-col flex-1 min-h-0 mb-0' : ''}
    >
      <p className="text-xs text-slate-500 mb-3">
        Load visitors from RXConnect for the incident window (max 31 days). Uncarded visitors go to <strong>Suspects</strong>; carded entries with a member id are added as member suspects (confirm on Suspects tab).
      </p>
      {addHint ? (
        <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-100 rounded px-3 py-2 mb-3">{addHint}</p>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="text-[11px] font-bold text-slate-500 block mb-1">Venue</label>
          <select value={venue} onChange={e => setVenue(e.target.value)} className="w-full text-sm border border-slate-200 rounded p-2 bg-white">
            <option value="">— Select venue —</option>
            {venues.map(v => {
              const label = v.name || v.venue_name || String(v.id || v.venue_id)
              const val = v.name || v.venue_name || v.id || v.venue_id || label
              return <option key={val} value={val}>{label}</option>
            })}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-bold text-slate-500 block mb-1">From</label>
          <input type="datetime-local" value={from} onChange={e => setFrom(e.target.value)} className="w-full text-sm border border-slate-200 rounded p-2" />
        </div>
        <div>
          <label className="text-[11px] font-bold text-slate-500 block mb-1">To</label>
          <input type="datetime-local" value={to} onChange={e => setTo(e.target.value)} className="w-full text-sm border border-slate-200 rounded p-2" />
        </div>
      </div>
      <button type="button" onClick={() => onLoad(from, to, venue)} disabled={loading || !venue} className="mb-4 text-xs py-1.5 px-3 rounded border font-semibold flex items-center gap-1 disabled:opacity-50" style={{ borderColor: P.pri, background: P.priM, color: P.pri }} title={!venue ? 'Select a venue first' : undefined}>
        {loading ? 'Loading…' : 'Load Visitors from VMS'}
      </button>
      {!loading && (visitors.length > 0 || visitorsHint) ? (
        <div className="mb-2 flex items-center justify-between gap-2 rounded-md border border-slate-100 bg-slate-50 px-3 py-2">
          <span className="text-xs font-semibold text-slate-800">
            {visitors.length} visitor{visitors.length === 1 ? '' : 's'} returned
          </span>
          {visitorsHint && visitors.length === 0 ? (
            <span className="text-[11px] text-slate-500 truncate" title={visitorsHint}>
              {visitorsHint}
            </span>
          ) : null}
        </div>
      ) : null}
      {visitors.length > 0 ? (
        <div
          className={`space-y-1.5 overflow-y-auto border border-slate-100 rounded-md p-1 ${
            embeddedInToolsPanel
              ? 'min-h-[420px] max-h-[calc(100vh-14rem)]'
              : 'min-h-[320px] max-h-[min(520px,calc(100vh-18rem))]'
          }`}
        >
          {visitors.map((v, idx) => {
            const key = v.member_id ? v.member_id : `v_${v.id}`
            const listKey = v.id != null ? `v_${v.id}` : `visitor_${idx}`
            const alreadyAdded = addedIds.has(key) || addedIds.has(`v_${v.id}`)
            const displayName = dedupePatronTitleLabel(
              v.display_name || [v.first_name, v.last_name].filter(Boolean).join(' ') || v.visitor_system_ref || `Visitor ${v.id}`
            )
            return (
              <div key={listKey} className="flex items-center justify-between py-2 px-3 bg-slate-50 border border-slate-100 rounded gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {(v.id_face_image || v.vms_id_face_b64) && (
                    <SmartImage
                      base64Data={v.id_face_image || v.vms_id_face_b64}
                      alt="VMS entry face"
                      className="w-10 h-10 object-cover rounded border border-slate-200 bg-white shrink-0"
                      clickable
                      modalTitle="VMS entry face"
                    />
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">{displayName}</div>
                    <div className="text-[11px] text-slate-500">{v.visitor_system_ref || v.entry_id || v.id || '—'} · {v.entry_datetime ? formatSydneyDateTime(new Date(v.entry_datetime)) : '—'}</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    if (alreadyAdded || addingEntryId) return
                    const entryId = v.id ?? v.entry_id
                    setAddingEntryId(entryId)
                    try {
                      await onAddSuspect(buildSuspectPayloadFromVisitor(v))
                    } finally {
                      setAddingEntryId(null)
                    }
                  }}
                  disabled={alreadyAdded || addingEntryId === (v.id ?? v.entry_id)}
                  className="text-xs py-1 px-2 rounded border font-semibold flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ borderColor: alreadyAdded ? '#94a3b8' : P.pri, background: alreadyAdded ? '#f1f5f9' : P.priM, color: alreadyAdded ? '#64748b' : P.pri }}
                >
                  <UserPlus size={11} />{' '}
                  {alreadyAdded ? 'Added' : addingEntryId === (v.id ?? v.entry_id) ? 'Adding…' : v.member_id ? 'Add member' : 'Add'}
                </button>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="text-sm text-slate-400 py-4 text-center space-y-2">
          <p>Set time window and click Load to see visitors.</p>
          {visitorsHint ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-3 py-2 text-left">{visitorsHint}</p>
          ) : null}
        </div>
      )}
    </SectionCard>
  )
}

export default function InvestigationCase() {
  const { id } = useParams()
  const { can } = useAuth()
  const [searchParams] = useSearchParams()
  const tabQuery = searchParams.get('tab')
  const labels = usePatronLabels()
  const { isClub } = labels
  const { assigneeNames: ASSIGNEES, members: teamMembers } = useStaffMembers({ includeUsers: true })
  const [expandedStep, setExpandedStep] = useState(null)
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('steps')
  const [outcome, setOutcome] = useState(null)
  const [outcomeRationale, setOutcomeRationale] = useState('')
  const [outcomeSaving, setOutcomeSaving] = useState(false)
  const [creatingEcdd, setCreatingEcdd] = useState(false)
  const [noteText, setNoteText] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [caseData, setCaseData] = useState(null)
  const [steps, setSteps] = useState([])
  const [timeline, setTimeline] = useState([])
  const [transactions, setTransactions] = useState([])
  const [rawEvidenceItems, setRawEvidenceItems] = useState([])
  const [patronActivities, setPatronActivities] = useState([])
  const [transactionsLoading, setTransactionsLoading] = useState(false)
  const [addStepOpen, setAddStepOpen] = useState(false)
  const [addStepTitle, setAddStepTitle] = useState('')
  const [addStepCategory, setAddStepCategory] = useState(STEP_CATEGORIES[0])
  const [assignModal, setAssignModal] = useState(null)
  const [savingStepId, setSavingStepId] = useState(null)
  const [evidenceStepForUpload, setEvidenceStepForUpload] = useState(null)
  const [evidenceUploadStepId, setEvidenceUploadStepId] = useState(null)
  const [faceIdentifyModal, setFaceIdentifyModal] = useState(null)
  const [overview, setOverview] = useState({ loading: false, data: null, error: null })
  const [verificationCapture, setVerificationCapture] = useState({ loading: false, data: null, error: null })
  const [amlChecks, setAmlChecks] = useState([])
  const [amlChecksLoading, setAmlChecksLoading] = useState(false)
  const [amlLoading, setAmlLoading] = useState(false)
  const [amlModalOpen, setAmlModalOpen] = useState(false)
  const [amlModalData, setAmlModalData] = useState(null)
  const [selectedAmlIndex, setSelectedAmlIndex] = useState(0)
  const [linkTransactionOpen, setLinkTransactionOpen] = useState(false)
  const [activityAddPreset, setActivityAddPreset] = useState(null)
  const [observationModalOpen, setObservationModalOpen] = useState(false)
  const [floorPickerOpen, setFloorPickerOpen] = useState(false)
  const floorPickerHandlerRef = React.useRef(null)
  const [changeProfileOpen, setChangeProfileOpen] = useState(false)
  const [suspects, setSuspects] = useState([])
  const [suspectsLoading, setSuspectsLoading] = useState(false)
  const [visitorsInWindow, setVisitorsInWindow] = useState([])
  const [visitorsWindowHint, setVisitorsWindowHint] = useState('')
  const [suspectAddHint, setSuspectAddHint] = useState('')
  const [visitorsLoading, setVisitorsLoading] = useState(false)
  const [expandedSuspectId, setExpandedSuspectId] = useState(null)
  const [detailsModalSuspect, setDetailsModalSuspect] = useState(null)
  const [detailsModalTab, setDetailsModalTab] = useState('identity')
  const [suspectDetails, setSuspectDetails] = useState({})
  const [suspectDetailsLoading, setSuspectDetailsLoading] = useState({})
  const [toolsPanelOpen, setToolsPanelOpen] = useState(false)
  const [helpPanelOpen, setHelpPanelOpen] = useState(false)
  const [toolsPanelSubTab, setToolsPanelSubTab] = useState('narrow')
  const [browsePreset, setBrowsePreset] = useState({
    seq: 0,
    subTab: 'sessions',
    days: 90,
    stepId: null,
    stepTitle: '',
  })
  const [typology, setTypology] = useState('structuring')
  const [previousHistory, setPreviousHistory] = useState({ smrs: [], ecdds: [] })
  const [previousHistoryLoading, setPreviousHistoryLoading] = useState(false)
  const [stepsSeeding, setStepsSeeding] = useState(false)

  useEffect(() => {
    if (!steps.length) return
    setEvidenceUploadStepId((prev) => {
      if (evidenceStepForUpload?.id && steps.some((s) => s.id === evidenceStepForUpload.id)) {
        return evidenceStepForUpload.id
      }
      if (prev && steps.some((s) => s.id === prev)) return prev
      return steps[0].id
    })
  }, [steps, evidenceStepForUpload?.id])

  // Deep-link: /investigations/:id?tab=smr | smr_form | ecdd
  useEffect(() => {
    if (!caseData?.id) return
    const tab = (tabQuery || '').toLowerCase()
    if (tab === 'smr' || tab === 'smr_form') {
      if (showSmrFormNav(caseData)) setActiveTab('smr_form')
      else {
        if (!caseData?.outcome) setOutcome('escalate_smr')
        setActiveTab('outcome')
      }
    } else if (tab === 'ecdd' || tab === 'ecdd_tasks') {
      if (showEcddTasksNav(caseData)) setActiveTab('ecdd_tasks')
      else {
        if (!caseData?.outcome) setOutcome('escalate_ecdd')
        setActiveTab('outcome')
      }
    }
  }, [caseData?.id, caseData?.outcome, caseData?.case_type, caseData?.status, caseData?.case_status, caseData?.suspicion_formed_at, tabQuery])

  // Sync outcome from DB on load
  useEffect(() => {
    if (caseData?.outcome) {
      setOutcome(caseData.outcome)
      setOutcomeRationale(caseData.outcome_rationale || '')
    }
  }, [caseData?.id, caseData?.outcome])

  useEffect(() => {
    if (!id || !caseData?.member_id) return
    let cancelled = false
    ;(async () => {
      setPreviousHistoryLoading(true)
      try {
        const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}/previous-history`))
        const json = await res.json()
        if (cancelled) return
        if (json.success && json.data) setPreviousHistory({ smrs: json.data.smrs || [], ecdds: json.data.ecdds || [] })
        else setPreviousHistory({ smrs: [], ecdds: [] })
      } catch (_) {
        if (!cancelled) setPreviousHistory({ smrs: [], ecdds: [] })
      } finally {
        if (!cancelled) setPreviousHistoryLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [id, caseData?.member_id])

  const loadCase = useCallback(async () => {
    if (!id) return
    try {
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}`))
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Case not found')
      const c = json.data || {}
      const mappedSteps = (c.investigation_steps || []).map(mapStep)
      setCaseData(c)
      setSteps(mappedSteps)
      setTimeline((c.activities || []).map(mapActivity))
    } catch (e) {
      setError(e.message || 'Failed to load case')
    }
  }, [id])

  useEffect(() => {
    let cancelled = false
    if (!id) return
    ;(async () => {
      try {
        setLoading(true)
        setError(null)
        setOverview({ loading: false, data: null, error: null })
        setVerificationCapture({ loading: false, data: null, error: null })
        setAmlChecks([])
        setTransactions([])
        setPreviousHistory({ smrs: [], ecdds: [] })
        setSuspects([])
        const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}`))
        const json = await res.json()
        if (cancelled) return
        if (!json.success) throw new Error(json.error || 'Case not found')
        const c = json.data || {}
        let mappedSteps = (c.investigation_steps || []).map(mapStep)
        setCaseData(c)
        setTimeline((c.activities || []).map(mapActivity))
        setSteps(mappedSteps)
        if (mappedSteps.length > 0) setExpandedStep(String(mappedSteps[0].id))
        // Show case shell immediately; seed + secondary fetches continue below.
        setLoading(false)

        // Auto-seed default investigation steps for cases that have none (non-blocking)
        if (mappedSteps.length === 0 && !cancelled) {
          setStepsSeeding(true)
          try {
            const seedRes = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}/steps/seed`), { method: 'POST' })
            const seedJson = await seedRes.json()
            if (seedJson.success && seedJson.data?.seeded > 0) {
              const res2 = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}`))
              const json2 = await res2.json()
              if (!cancelled && json2.success) {
                mappedSteps = (json2.data?.investigation_steps || []).map(mapStep)
                setCaseData(json2.data || c)
                setTimeline((json2.data?.activities || []).map(mapActivity))
                setSteps(mappedSteps)
                if (mappedSteps.length > 0) setExpandedStep(String(mappedSteps[0].id))
              }
            }
          } catch (_) { /* seed failed — show empty steps, user can add manually */ }
          finally {
            if (!cancelled) setStepsSeeding(false)
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load case')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [id])

  const loadSuspects = useCallback(async () => {
    if (!id) return
    setSuspectsLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}/suspects`))
      const json = await res.json()
      if (json.success && Array.isArray(json.data)) setSuspects(dedupeSuspectsByIdentity(json.data))
      else setSuspects([])
    } catch (_) { setSuspects([]) }
    finally { setSuspectsLoading(false) }
  }, [id])

  useEffect(() => { if (id) loadSuspects() }, [id, loadSuspects])

  const loadVisitorsInWindow = useCallback(async (from, to, venue) => {
    if (!id) return
    if (!venue) {
      setVisitorsWindowHint('Select a venue to query RXConnect Visitor Management.')
      return
    }
    setVisitorsLoading(true)
    try {
      const qs = new URLSearchParams()
      if (from) qs.set('from', from)
      if (to) qs.set('to', to)
      if (venue) qs.set('venue', venue)
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}/visitors-in-window?${qs}`))
      const json = await res.json()
      if (json.success && Array.isArray(json.data)) {
        setVisitorsInWindow(dedupeVisitorsByIdentity(json.data))
        const sourceLabel = json.source === 'rxconnect' ? 'RXConnect VMS' : 'local database'
        const hintParts = [json.hint].filter(Boolean)
        if (json.data.length > 0) {
          const daysNote = json.days_queried > 1 ? ` (${json.days_queried} days queried)` : ''
          hintParts.unshift(`Loaded ${json.data.length} visitor(s) from ${sourceLabel}${daysNote}.`)
        } else if (json.source === 'rxconnect' && json.days_queried) {
          hintParts.unshift(`Queried RXConnect across ${json.days_queried} day(s).`)
        } else if (json.rxconnect_error?.code === 'RXCONNECT_VISITORS_WINDOW_TOO_WIDE') {
          const span = json.days_requested ? `${json.days_requested} days` : 'this range'
          const max = json.max_days || 31
          hintParts.unshift(
            `RXConnect limit: ${span} selected (max ${max} per query). Narrow the incident window and reload.`
          )
        }
        if (json.rxconnect_error?.message) {
          hintParts.push(`RXConnect: ${json.rxconnect_error.message}`)
        }
        setVisitorsWindowHint(hintParts.join(' '))
      } else {
        setVisitorsInWindow([])
        setVisitorsWindowHint(json.hint || json.error || '')
      }
    } catch (_) { setVisitorsInWindow([]); setVisitorsWindowHint('') }
    finally { setVisitorsLoading(false) }
  }, [id])

  const addSuspect = useCallback(async (payload) => {
    if (!id) return false
    setSuspectAddHint('')
    try {
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}/suspects`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
      const json = await res.json()
      if (json.success) {
        await loadSuspects()
        if (json.data?.id != null) setExpandedSuspectId(json.data.id)
        setActiveTab('suspects')
        setToolsPanelOpen(false)
        setSuspectAddHint(`Added ${payload.display_name || 'candidate'} to Suspects. Open Suspects for full VMS details.`)
        return true
      }
      setSuspectAddHint(json.error || 'Could not add to Suspects. Try again or check permissions.')
      return false
    } catch (e) {
      setSuspectAddHint(e?.message || 'Could not add to Suspects.')
      return false
    }
  }, [id, loadSuspects])

  const removeSuspect = useCallback(async (suspectId) => {
    if (!id) return
    try {
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}/suspects/${suspectId}`), { method: 'DELETE' })
      const json = await res.json()
      if (json.success) await loadSuspects()
    } catch (_) {}
  }, [id, loadSuspects])

  const confirmSuspect = useCallback(async (suspect) => {
    if (!id || !suspect.member_id) return
    if (!confirm(`Confirm ${suspect.display_name || suspect.member_id} as the case subject?`)) return
    try {
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}/confirm-suspect`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ member_id: suspect.member_id, member_name: suspect.display_name })
      })
      const json = await res.json()
      if (json.success) { await loadCase(); setActiveTab('kyc') }
    } catch (e) { alert(e?.message || 'Failed to confirm') }
  }, [id, loadCase])

  const fetchSuspectDetails = useCallback(async (suspect) => {
    setSuspectDetailsLoading(prev => ({ ...prev, [suspect.id]: true }))
    try {
      let overview = null
      let amlChecks = []
      let visitorData = null

      if (suspect.member_id) {
        const [ovRes, amlRes] = await Promise.all([
          authFetch(getLocalApiUrl(`/api/watchlist/member/${encodeURIComponent(suspect.member_id)}/overview`)),
          authFetch(getLocalApiUrl(`/api/aml/checks?member_id=${encodeURIComponent(suspect.member_id)}&limit=5`))
        ])
        const ovJson = await ovRes.json()
        const amlJson = await amlRes.json()
        overview = ovJson.success ? ovJson.data : null
        amlChecks = amlJson?.success && Array.isArray(amlJson.data) ? amlJson.data : []
      }

      if (suspect.visitor_id) {
        const snapFromRow = visitorDataFromSuspectSnapshot(suspect.visit_snapshot)
        visitorData = snapFromRow
        const snap = parseCaseSnapshot(caseData)
        const venue = snap.Venue || snap.venue || ''
        const qs = new URLSearchParams()
        if (venue) qs.set('venue', venue)
        qs.set('entry_id', String(suspect.visitor_id))
        const res = await authFetch(
          getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}/suspects/${suspect.id}/visit-detail?${qs}`)
        )
        const json = await res.json()
        if (json.success && json.data) {
          visitorData = {
            ...(snapFromRow || {}),
            identity: json.data.identity,
            visit: json.data.visit,
            kyc_status: json.data.kyc_status,
            visitor_history: json.data.visitor_history,
            prior_cases: json.data.prior_cases,
            linked_member_id: json.data.linked_member_id,
            id_face_image: json.data.id_face_image,
            vms_id_face_b64: json.data.vms_id_face_b64,
            source: json.data.source,
          }
        } else if (!snapFromRow) {
          const fallback = await authFetch(getLocalApiUrl(`/api/profiles/visitors/${encodeURIComponent(suspect.visitor_id)}`))
          const fbJson = await fallback.json()
          visitorData = fbJson.success ? fbJson.data : null
        }
      }

      setSuspectDetails(prev => ({
        ...prev,
        [suspect.id]: { overview, amlChecks, visitorData },
      }))
    } catch (_) {}
    finally { setSuspectDetailsLoading(prev => ({ ...prev, [suspect.id]: false })) }
  }, [id, caseData])

  const handleAddStep = async () => {
    if (!addStepTitle.trim()) return
    try {
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}/steps`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: addStepTitle.trim(), category: addStepCategory })
      })
      const json = await res.json()
      if (json.success) { setAddStepOpen(false); setAddStepTitle(''); setAddStepCategory(STEP_CATEGORIES[0]); await loadCase() }
    } catch (_) {}
  }

  const handleUpdateStep = async (step, updates) => {
    try {
      setSavingStepId(step.id)
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}/steps/${step.id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      })
      const json = await res.json()
      if (json.success) await loadCase()
    } finally {
      setSavingStepId(null)
    }
  }

  const handleBrowseFromStep = useCallback((step, config) => {
    setBrowsePreset({
      seq: Date.now(),
      subTab: config.subTab || 'sessions',
      days: config.days ?? 90,
      stepId: step?.id ?? null,
      stepTitle: step?.title || '',
    })
    setExpandedStep(step?.id ?? null)
    setToolsPanelSubTab('browse')
    setToolsPanelOpen(true)
  }, [])

  const handleAttachEvidence = async (step, file) => {
    if (!step?.id) {
      window.alert('No investigation step is available. Open the Investigation Steps tab and ensure at least one step exists (or wait for the page to finish loading).')
      return
    }
    try {
      setSavingStepId(step.id)
      const fd = new FormData()
      const uploadFile = file?.type?.startsWith('image/') ? normalizePastedScreenshotFile(file) : file
      fd.append('file', uploadFile)
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}/steps/${step.id}/evidence`), {
        method: 'POST',
        body: fd
      })
      const json = await res.json().catch(() => ({}))
      if (json.success) await loadCase()
      else window.alert(json.error || `Upload failed (${res.status})`)
    } catch (e) {
      window.alert(e?.message || 'Upload failed')
    } finally {
      setSavingStepId(null)
    }
  }

  const [evidenceViewer, setEvidenceViewer] = useState(null)
  const closeEvidenceViewer = useCallback(() => {
    setEvidenceViewer((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url)
      return null
    })
  }, [])

  const handleViewEvidence = useCallback(async (step, evidence) => {
    if (!evidence?.id || !step?.id) {
      window.alert('Unable to open evidence file. File path not available.')
      return
    }
    setEvidenceViewer({
      url: null,
      fileName: evidence.file_name || 'evidence',
      mimeType: evidence.mime_type || null,
      loading: true,
      error: null,
    })
    try {
      const res = await authFetch(evidenceDownloadUrl(id, step.id, evidence.id, { inline: true }))
      if (!res.ok) throw new Error('Failed to load file')
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      const mime = evidence.mime_type || blob.type || null
      setEvidenceViewer({
        url: objectUrl,
        fileName: evidence.file_name || 'evidence',
        mimeType: mime,
        loading: false,
        error: null,
      })
    } catch (e) {
      setEvidenceViewer({
        url: null,
        fileName: evidence.file_name || 'evidence',
        mimeType: evidence.mime_type || null,
        loading: false,
        error: e?.message || 'Unable to open evidence file.',
      })
    }
  }, [id])

  const [deletingEvidenceId, setDeletingEvidenceId] = useState(null)
  const handleDeleteEvidence = useCallback(async (step, evidence) => {
    if (!evidence?.id || !step?.id) return
    const label = evidence.file_name || 'this file'
    if (!window.confirm(`Delete evidence “${label}”? This cannot be undone.`)) return
    try {
      setDeletingEvidenceId(evidence.id)
      const res = await authFetch(
        getLocalApiUrl(
          `/api/cases/${encodeURIComponent(id)}/steps/${encodeURIComponent(step.id)}/evidence-files/${encodeURIComponent(evidence.id)}`
        ),
        { method: 'DELETE' }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) {
        throw new Error(json.error || `Delete failed (${res.status})`)
      }
      await loadCase()
    } catch (e) {
      window.alert(e?.message || 'Unable to delete evidence.')
    } finally {
      setDeletingEvidenceId(null)
    }
  }, [id, loadCase])

  const handleAssign = async (step, assignee) => {
    try {
      setSavingStepId(step.id)
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}/steps/${step.id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigned_to: assignee || null })
      })
      const json = await res.json()
      if (json.success) { setAssignModal(null); await loadCase() }
    } finally {
      setSavingStepId(null)
    }
  }

  const handleAddNote = async () => {
    if (!noteText.trim()) return
    try {
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}/activities`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activity_type: 'NOTE', title: 'Note added', description: noteText.trim(), created_by: 'web_user', created_by_name: 'Web User' })
      })
      const json = await res.json()
      if (json.success) { setNoteText(''); await loadCase() }
    } catch (_) {}
  }

  const handleCreateEcdd = async () => {
    if (creatingEcdd) return
    try {
      setCreatingEcdd(true)
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}/create-ecdd`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) {
        throw new Error(json.error || `HTTP ${res.status}`)
      }
      const ecddId = json.data?.ecdd_id
      if (!ecddId) throw new Error('Server did not return an ECDD id')
      const pid = resolvePatronIdentity(caseData || {})
      navigate(
        `/compliance-safe/ecdd/${encodeURIComponent(ecddId)}?patronId=${encodeURIComponent(pid.master_profile_id || pid.patron_id || '')}&memberId=${encodeURIComponent(pid.member_id || caseData?.member_id || '')}&memberName=${encodeURIComponent(caseData?.member_name || '')}`
      )
    } catch (e) {
      alert(e?.message || 'Failed to create ECDD')
    } finally {
      setCreatingEcdd(false)
    }
  }

  const handleFinaliseOutcome = async () => {
    if (!outcome || !outcomeRationale.trim() || outcomeSaving) return
    try {
      setOutcomeSaving(true)
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}/outcome`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outcome, outcome_rationale: outcomeRationale.trim() })
      })
      const json = await res.json()
      if (json.success) {
        await loadCase()
        // Navigate to the newly-created section
        if (outcome === 'escalate_ecdd') setActiveTab('ecdd_tasks')
        else if (outcome === 'escalate_smr') setActiveTab('smr_form')
        else if (outcome === 'escalate_both') setActiveTab('ecdd_tasks')
      }
    } catch (_) {} finally { setOutcomeSaving(false) }
  }

  const openEscalationQuickAction = (type) => {
    if (type === 'ecdd') {
      if (showEcddTasksNav(caseData) || caseData?.linked_ecdd) {
        setActiveTab('ecdd_tasks')
      } else {
        if (!caseData?.outcome) setOutcome('escalate_ecdd')
        setActiveTab('outcome')
      }
    } else if (type === 'smr') {
      if (showSmrFormNav(caseData)) {
        setActiveTab('smr_form')
      } else {
        if (!caseData?.outcome) setOutcome('escalate_smr')
        setActiveTab('outcome')
      }
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const memberId = caseData?.member_id
  const isValidMemberId = memberId && memberId !== '—'
  const loadVerificationCapture = useCallback(async () => {
    if (!id || !memberId || memberId === '—') {
      setVerificationCapture({ loading: false, data: null, error: null })
      return
    }
    setVerificationCapture((prev) => ({ ...prev, loading: true, error: null }))
    try {
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}/verification-capture`))
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to load ID capture')
      setVerificationCapture({ loading: false, data: json.data || null, error: null })
    } catch (e) {
      setVerificationCapture({ loading: false, data: null, error: e.message || 'Failed to load ID capture' })
    }
  }, [id, memberId])

  const loadOverview = useCallback(async () => {
    if (!memberId || memberId === '—') return
    try {
      setOverview({ loading: true, data: null, error: null })
      const res = await authFetch(getLocalApiUrl(`/api/watchlist/member/${encodeURIComponent(memberId)}/overview`))
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to load')
      setOverview({ loading: false, data: json.data, error: null })
    } catch (err) {
      setOverview({ loading: false, data: null, error: err.message })
    }
  }, [memberId])

  const loadAmlChecks = useCallback(async () => {
    if (!memberId || memberId === '—') return
    setAmlChecksLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/aml/checks?member_id=${encodeURIComponent(memberId)}&limit=10`))
      const json = await res.json()
      if (json?.success && Array.isArray(json.data)) setAmlChecks(json.data)
      else setAmlChecks([])
    } catch (_) { setAmlChecks([]) }
    finally { setAmlChecksLoading(false) }
  }, [memberId])

  const patronName = resolveCasePatronDisplayName(overview.data?.profile, caseData)
  const handleReScreen = useCallback(async () => {
    if (!memberId || memberId === '—' || !patronName.trim() || patronName === 'Unknown') return
    try {
      setAmlLoading(true)
      const res = await authFetch(getLocalApiUrl('/api/aml/checks/run'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ member_id: memberId, name: patronName.trim(), country: 'AUST', reason: 'Investigation re-screen', performed_by: 'web_user' })
      })
      const json = await res.json()
      if (!json?.success) throw new Error(json?.error || 'AML check failed')
      await loadAmlChecks()
      await loadOverview()
      const d = json.data
      if (d?.id) {
        setAmlModalData({
          checkId: d.id,
          name: d.name || patronName,
          country: d.country,
          results: Array.isArray(d.sample) ? d.sample : [],
          analystDecision: d.analyst_decision || null,
          analystReason: d.analyst_reason || '',
        })
        setSelectedAmlIndex(0)
        setAmlModalOpen(true)
      }
    } catch (e) { alert(e?.message || 'Re-screen failed') }
    finally { setAmlLoading(false) }
  }, [memberId, patronName, loadAmlChecks, loadOverview])

  const viewAmlCheckDetails = useCallback(async (checkId) => {
    if (!checkId) return
    try {
      const res = await authFetch(getLocalApiUrl(`/api/aml/checks/${checkId}`))
      const json = await res.json()
      if (!json?.success || !json.data) return
      const d = json.data
      const sample = Array.isArray(d.result_sample) ? d.result_sample : []
      setAmlModalData({
        checkId: d.id,
        name: d.full_name || patronName,
        country: d.country_code,
        results: sample,
        analystDecision: d.analyst_decision || null,
        analystReason: d.analyst_reason || '',
      })
      setSelectedAmlIndex(0)
      setAmlModalOpen(true)
    } catch (_) {}
  }, [patronName])

  const handleAmlReviewSaved = useCallback(async (decision, reason) => {
    setAmlModalData((prev) =>
      prev
        ? { ...prev, analystDecision: decision, analystReason: reason || '' }
        : prev
    )
    await loadAmlChecks()
    await loadOverview()
  }, [loadAmlChecks, loadOverview])

  // S8-F1b — transitioning SoF to `verified` routes through a modal that
  // captures a structured change_reason_code + optional narrative. Other
  // transitions still fire the PATCH immediately.
  const [sofVerifyOpen, setSofVerifyOpen] = useState(false)
  const [sofVerifySubmitting, setSofVerifySubmitting] = useState(false)
  const sendSofUpdate = useCallback(async (body) => {
    const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}/source-of-funds`), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const json = await res.json()
    if (!res.ok || !json?.success) throw new Error(json?.error || 'Failed to update SOF status')
    return json
  }, [id])
  const handleUpdateSof = useCallback(async (status) => {
    if (!id) return
    if (status === 'verified') {
      setSofVerifyOpen(true)
      return
    }
    try {
      await sendSofUpdate({ status })
      await loadCase()
    } catch (e) { alert(e?.message || 'Failed to update SOF status') }
  }, [id, loadCase, sendSofUpdate])
  const handleSofVerifyConfirm = useCallback(async ({ change_reason_code, change_reason_notes }) => {
    setSofVerifySubmitting(true)
    try {
      await sendSofUpdate({ status: 'verified', change_reason_code, change_reason_notes })
      setSofVerifyOpen(false)
      await loadCase()
    } catch (e) {
      alert(e?.message || 'Failed to verify source of funds')
    } finally {
      setSofVerifySubmitting(false)
    }
  }, [sendSofUpdate, loadCase])

  const loadPatronActivity = useCallback(async () => {
    if (!id) return
    try {
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}/patron-activity`))
      const json = await res.json()
      if (json.success) {
        const rows = json.data?.activities ?? json.data
        setPatronActivities(Array.isArray(rows) ? rows : [])
      }
    } catch (_) {}
  }, [id])

  const loadLinkedTransactions = useCallback(async () => {
    if (!id) return
    setTransactionsLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(id)}/evidence?limit=50`))
      const json = await res.json()
      if (json.success) {
        const floorItems = json.data?.items
        const raw = Array.isArray(floorItems) ? floorItems : []
        if (raw.length === 0) {
          setRawEvidenceItems([])
          setTransactions([])
        } else {
          const fallbackDate = caseData?.created_at ? new Date(caseData.created_at).toISOString() : null
          const uniqueItems = dedupeEvidenceTransactionRows(raw)
          setRawEvidenceItems(uniqueItems)
          setTransactions(
            uniqueItems
              .map((it) => mapEvidenceItemToLinkedTransactionRow(it, fallbackDate))
              .filter(Boolean)
          )
        }
      }
    } catch (_) {}
    finally { setTransactionsLoading(false) }
  }, [id, caseData?.created_at])

  useEffect(() => {
    if (!id || !caseData) return
    loadLinkedTransactions()
    loadPatronActivity()
  }, [id, caseData, loadLinkedTransactions, loadPatronActivity])

  useEffect(() => {
    if (!memberId || memberId === '—') return
    loadOverview()
    loadVerificationCapture()
  }, [memberId, loadOverview, loadVerificationCapture])

  useEffect(() => {
    if (!memberId || memberId === '—') return
    loadAmlChecks()
  }, [memberId, loadAmlChecks])

  if (loading && !caseData) {
    return (
      <div className="min-h-full p-6">
        <Link to="/compliance-safe/investigations" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-600">
          <ChevronLeft size={16} /> Back to Investigation
        </Link>
        <div className="mt-6 text-slate-500">Loading case…</div>
      </div>
    )
  }

  if (error || !caseData) {
    return (
      <div className="min-h-full p-6">
        <Link to="/compliance-safe/investigations" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-600">
          <ChevronLeft size={16} /> Back to Investigation
        </Link>
        <div className="mt-6 text-red-600">{error || 'Case not found'}</div>
      </div>
    )
  }

  const profileFromOverview = overview.data?.profile || {}
  const patronDirectoryName = patronDirectoryFullName(profileFromOverview)
  const resolvedPatronDisplayName = resolveCasePatronDisplayName(profileFromOverview, caseData)
  const caseSnapshotName = caseData.member_name ? dedupePatronTitleLabel(caseData.member_name) : ''
  const patronNameDiffersFromCaseSnapshot =
    Boolean(caseData.member_id && caseSnapshotName && patronDirectoryName) &&
    caseSnapshotName.toLowerCase() !== dedupePatronTitleLabel(patronDirectoryName).toLowerCase()

  const displaySnapshot = normalizeSourceSnapshotForDisplay(caseData.source_data_snapshot || {}, caseData)
  const linkedMembership = String(profileFromOverview.membership_number || '').trim()
  const casePatronIdentity = resolvePatronIdentity({
    ...caseData,
    ...(linkedMembership ? { membership_number: linkedMembership } : {}),
    member_id: profileFromOverview.member_id || caseData.member_id,
    master_profile_id: caseData.master_profile_id || profileFromOverview.master_profile_id,
    visitor_id: caseData.visitor_id || profileFromOverview.visitor_id,
    profile_type: caseData.profile_type || profileFromOverview.profile_type,
  })
  const profileSearchId =
    casePatronIdentity.master_profile_id ||
    casePatronIdentity.member_id ||
    casePatronIdentity.visitor_id ||
    (caseData.member_id && caseData.member_id !== '—' ? caseData.member_id : null)
  const patronSecondary = clubOrVisitorIdLine(casePatronIdentity, isClub)
  const pipelineStageId = resolvePipelineStage(caseData)
  const pipelineStageLabel = resolveStageLabel(pipelineStageId)
  const stageEnteredAt = pickStageEnteredAt(caseData, pipelineStageId, caseData.status)
  const stageStale = getStageStaleInfo(stageEnteredAt, pipelineStageId)
  const CASE = {
    id: caseData.case_number || caseData.id,
    status: (caseData.status || 'OPEN').toLowerCase(),
    priority: (caseData.priority || 'NORMAL').toLowerCase(),
    openedDate: caseData.created_at ? formatSydneyDateTime(new Date(caseData.created_at)) : '—',
    openedBy: caseData.created_by || '—',
    daysOpen: Math.floor((Date.now() - new Date(caseData.created_at).getTime()) / 86400000),
    assignedTo: caseData.assigned_to || '—',
    patron: {
      id: casePatronIdentity.patron_id || '—',
      masterProfileId: casePatronIdentity.master_profile_id,
      memberId: casePatronIdentity.member_id,
      visitorId: casePatronIdentity.visitor_id,
      subjectType: casePatronIdentity.subject_type,
      name: resolvedPatronDisplayName,
      risk: caseData.member_id ? (caseData.risk_score ?? overview.data?.risk_score ?? 0) : null,
      kycStatus: caseData.member_id
        ? (overview.data?.kyc_aml ? (overview.data.kyc_aml.kyc_completed || /PASSED|VERIFIED|MATCHED/i.test(overview.data.kyc_aml.dvs_status || '') ? 'Verified' : 'Pending') : '—')
        : 'N/A',
      dob: formatDateOnly(profileFromOverview.date_of_birth),
      address: (profileFromOverview.address || profileFromOverview.address1 || profileFromOverview.residential_address) ? [profileFromOverview.address || profileFromOverview.address1 || profileFromOverview.residential_address, profileFromOverview.address2, profileFromOverview.suburb || profileFromOverview.city, profileFromOverview.state, profileFromOverview.postcode].filter(Boolean).join(', ') : '—',
      occupation: profileFromOverview.occupation || '—',
    },
    source: {
      type: caseData.source_data_type || 'event',
      ruleId: caseData.rule_id || '—',
      ruleName: caseData.rule_name || '—',
      sourceDataId: caseData.source_data_id || '—',
      alertRef: caseData.source_alert_id ? `ALT-${caseData.source_alert_id}` : null,
      snapshot: displaySnapshot,
      desc: caseData.summary || 'Opened from alert or rule trigger',
    },
    linkedAlerts: caseData.source_alert_id ? [`ALT-${caseData.source_alert_id}`] : [],
    linkedTransactions: transactions.map(t => t.id),
    pastEcddCases: previousHistory?.ecdds || [],
    pastSmrCases: previousHistory?.smrs || [],
    previousHistory,
    summary: caseData.summary || 'No summary yet.',
  }

  const STEPS = steps
  const TIMELINE = timeline.length > 0 ? timeline : [{ date: CASE.openedDate, who: CASE.openedBy, action: 'Investigation case opened', type: 'open' }]
  const TRANSACTIONS = transactions
  // No identifiable subject: blank member id or an uncarded placeholder (nothing to profile, KYC or screen).
  const caseMemberKey = String(caseData?.member_id ?? '').trim().toUpperCase()
  const isUnknown = !caseMemberKey || caseMemberKey === '__UNCARDED__' || caseMemberKey === 'UNCARDED' || caseMemberKey.startsWith('UNC_')

  const caseId = CASE.id
  const hidePatronSidebar = FULL_WIDTH_CASE_TABS.has(activeTab)

  // Progressive secondary loads — case shell renders first; these fill in asynchronously.
  const secondaryLoadingItems = [
    stepsSeeding && 'Investigation steps',
    overview.loading && 'Patron profile',
    verificationCapture.loading && 'ID capture',
    amlChecksLoading && 'AML checks',
    previousHistoryLoading && 'Related cases',
    transactionsLoading && 'Linked transactions',
    suspectsLoading && 'Suspects',
  ].filter(Boolean)

  // ECDD requirement banner. Backend-derived on GET /api/cases/:id.
  const ecddReq = caseData?.ecdd_requirement || { mandated: false, reasons: [], existing_ecdd_id: null, hint: null }
  const existingEcddId = ecddReq.existing_ecdd_id || caseData?.linked_ecdd || null
  const ecddProfileLink = existingEcddId
    ? `/compliance-safe/ecdd/${encodeURIComponent(existingEcddId)}?patronId=${encodeURIComponent(casePatronIdentity.master_profile_id || casePatronIdentity.patron_id || '')}&memberId=${encodeURIComponent(casePatronIdentity.member_id || caseData?.member_id || '')}&memberName=${encodeURIComponent(caseData?.member_name || '')}`
    : null

  return (
    <div className="flex flex-col min-h-0" style={{ height: 'calc(100dvh - 7rem)' }}>
      <SofVerifyModal
        open={sofVerifyOpen}
        subjectLabel={caseData?.case_number ? `Case ${caseData.case_number}` : 'this case'}
        submitting={sofVerifySubmitting}
        onCancel={() => setSofVerifyOpen(false)}
        onConfirm={handleSofVerifyConfirm}
      />
      <div className="shrink-0 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <Link to="/compliance-safe/investigations" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-600 transition-colors">
          <ChevronLeft size={16} /> Back to Investigation
        </Link>
        <HelpScreenButton
          screenKey={CASE_HELP_SCREEN_KEY}
          onClick={() => {
            setToolsPanelOpen(false)
            setHelpPanelOpen(true)
          }}
        />
      </div>

      {!existingEcddId && ecddReq.mandated ? (
        <div className="rounded-lg border-2 border-red-300 bg-red-50 px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2 min-w-0">
              <AlertTriangle size={18} className="text-red-600 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-sm font-bold text-red-800">ECDD is required for this case</div>
                <ul className="mt-1 list-disc list-inside text-sm text-red-700 space-y-0.5">
                  {ecddReq.reasons.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            </div>
            <button
              type="button"
              onClick={handleCreateEcdd}
              disabled={creatingEcdd}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Shield size={14} />
              {creatingEcdd ? 'Creating…' : 'Create ECDD'}
            </button>
          </div>
        </div>
      ) : !existingEcddId && ecddReq.reasons.length > 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <span className="font-semibold">ECDD recommended.</span> {ecddReq.reasons.join(' ')}
            </div>
          </div>
          <button
            type="button"
            onClick={handleCreateEcdd}
            disabled={creatingEcdd}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-amber-400 text-amber-900 text-sm font-semibold hover:bg-amber-100 disabled:opacity-60"
          >
            <Shield size={14} />
            {creatingEcdd ? 'Creating…' : 'Create ECDD'}
          </button>
        </div>
      ) : null}

      {/* CASE HEADER — pinned above scrollable case body */}
      <div
        className="rounded-lg shadow-md"
        style={{ background: P.pri, padding: '16px 24px 20px' }}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2.5 mb-2 flex-wrap">
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#7ec8e3' }}>Investigation Case</span>
              <span className="text-xs" style={{ color: '#4d8fa3' }}>•</span>
              <span className="text-sm font-mono" style={{ color: '#a0c4d4' }}>{caseId}</span>
              {stageStale ? (
                <Pill
                  color="#d97706"
                  bg="#fffbeb"
                  label={formatDaysInStageLabel(stageStale.daysInStage, pipelineStageLabel, { context: 'detail' })}
                  title={stageAgingTooltip(pipelineStageLabel, stageEnteredAt)}
                />
              ) : null}
            </div>
            <div className="text-2xl font-extrabold text-white mb-1.5">
              {profileSearchId ? (
                <Link to={`/profile-safe/search?memberId=${encodeURIComponent(profileSearchId)}`} className="hover:underline hover:opacity-90">
                  {CASE.patron.name}
                </Link>
              ) : (
                <span>{CASE.patron.name}</span>
              )}
            </div>
            <div className="text-sm flex items-center gap-2 flex-wrap" style={{ color: '#a0c4d4' }}>
              {CASE.patron.id !== '—' ? (
                <span className="font-mono text-xs" style={{ background: 'rgba(255,255,255,0.15)', padding: '2px 8px', borderRadius: 4 }} title="Patron / master profile ID">
                  {CASE.patron.id}
                </span>
              ) : null}
              {CASE.patron.subjectType && CASE.patron.subjectType !== 'patron' && (
                <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.12)' }}>
                  {subjectTypeLabel(CASE.patron.subjectType, isClub)}
                </span>
              )}
              {patronSecondary && (
                <span className="font-mono text-xs" style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 8px', borderRadius: 4 }}>
                  {patronSecondary.label}: {patronSecondary.value}
                </span>
              )}
              {CASE.patron.id === '—' && !patronSecondary && (
                <span className="text-xs">Unidentified subject</span>
              )}
              <button
                type="button"
                onClick={() => setChangeProfileOpen(true)}
                className="inline-flex items-center gap-1 rounded border border-white/25 bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-white/20"
                title={caseData?.member_id ? 'Replace the linked patron profile' : 'Link a patron profile to this case'}
              >
                <UserPlus size={12} />
                {caseData?.member_id ? 'Change linked profile' : 'Link profile'}
              </button>
            </div>
              {patronNameDiffersFromCaseSnapshot && (
                <div
                  className="mt-2 max-w-xl text-xs rounded-md px-2.5 py-1.5 leading-snug border border-amber-300/40"
                  style={{ background: 'rgba(251, 191, 36, 0.18)', color: '#fef3c7' }}
                  role="status"
                >
                  <strong className="text-white">Name mismatch — review recommended.</strong> Case / payout name{' '}
                  <strong className="text-white">{caseSnapshotName}</strong> differs from patron profile{' '}
                  <strong className="text-white">{dedupePatronTitleLabel(patronDirectoryName)}</strong>. The
                  investigation keeps the name as recorded on the payout.
                </div>
              )}
              {CASE.patron.memberId && CASE.patron.memberId !== '—' && (
                <div className="mt-3">
                  {(caseData?.patron_compliance || overview.data) ? (
                    <PatronComplianceStrip
                      size="compact"
                      dark
                      complianceStage={
                        caseData?.patron_compliance?.compliance_stage
                        ?? overview.data?.compliance_stage
                      }
                      riskScore={
                        caseData?.patron_compliance?.risk_score
                        ?? overview.data?.risk_score
                        ?? CASE.patron.risk
                      }
                      riskLevel={
                        caseData?.patron_compliance?.risk_level
                        ?? overview.data?.risk_level
                      }
                      cddTier={
                        caseData?.patron_compliance?.cdd_tier
                        ?? overview.data?.cdd_tier
                      }
                    />
                  ) : overview.loading ? (
                    <div className="inline-flex items-center gap-1.5 text-xs" style={{ color: '#a0c4d4' }}>
                      <Loader2 size={12} className="animate-spin" />
                      Loading compliance…
                    </div>
                  ) : null}
                  {overview.loading && !patronDirectoryName ? (
                    <div className="mt-1.5 text-[11px]" style={{ color: '#7ec8e3' }}>
                      Checking patron profile name…
                    </div>
                  ) : null}
                </div>
              )}
          </div>
          <div className="text-right">
            <div className="text-[11px] mb-1" style={{ color: '#7ec8e3' }}>Assigned to</div>
            <div className="text-sm font-bold text-white">{CASE.assignedTo}</div>
            <div className="text-[11px] mt-2" style={{ color: '#7ec8e3' }}>Opened {CASE.daysOpen} days ago</div>
            <div className="text-[11px]" style={{ color: '#a0c4d4' }}>{CASE.openedDate}</div>
          </div>
        </div>

      </div>

      {secondaryLoadingItems.length > 0 && (
        <div
          className="flex items-center gap-2 flex-wrap rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"
          role="status"
          aria-live="polite"
        >
          <Loader2 size={13} className="animate-spin shrink-0 text-slate-400" />
          <span className="font-medium text-slate-700">Loading</span>
          {secondaryLoadingItems.map((label) => (
            <span
              key={label}
              className="inline-flex items-center rounded-full bg-white border border-slate-200 px-2 py-0.5 text-[11px] text-slate-600"
            >
              {label}
            </span>
          ))}
        </div>
      )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto space-y-6 pt-4 pb-2">

      {/* LIFECYCLE STATUS BANNER */}
      {(() => {
        const outcomeVal = caseData?.outcome || outcome
        const caseStatus = caseStatusUpper(caseData) || 'OPEN'
        const bannerProgress = computeCaseBannerProgress({
          caseData,
          outcomeVal,
          caseStatus,
          checklistSteps: STEPS,
        })
        const visiblePhases = bannerProgress.phases || buildCaseWorkflowPhases(caseData, outcomeVal, caseStatus)
        const bannerPct = bannerProgress.progress
        const progressColor = bannerPct >= 100 ? '#16a34a' : bannerPct >= 50 ? P.pri : '#d97706'
        return (
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-slate-200 bg-white px-4 py-3 mx-auto" style={{ maxWidth: 1400 }}>
            <div className="flex items-center gap-0 flex-wrap">
              {visiblePhases.map((phase, i) => {
                const isLast = i === visiblePhases.length - 1
                const color = phase.done ? '#16a34a' : phase.active ? (phase.color || P.pri) : '#cbd5e1'
                const bg = phase.done ? '#f0fdf4' : phase.active ? (phase.color ? phase.color + '10' : P.priM) : '#f8fafc'
                return (
                  <React.Fragment key={phase.key}>
                    <button
                      type="button"
                      onClick={() => {
                        if (phase.key === 'ecdd') setActiveTab('ecdd_tasks')
                        else if (phase.key === 'smr') setActiveTab('smr_form')
                        else if (phase.key === 'investigation') setActiveTab('steps')
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-bold transition-colors cursor-pointer border-none"
                      style={{ background: bg, color }}
                    >
                      {phase.done
                        ? <CheckCircle2 size={14} />
                        : phase.active
                          ? <Activity size={14} />
                          : <Clock size={14} />
                      }
                      {phase.label}
                    </button>
                    {!isLast && (
                      <ArrowRight size={14} className="mx-1.5 flex-shrink-0" style={{ color: '#cbd5e1' }} />
                    )}
                  </React.Fragment>
                )
              })}
            </div>
            <div className="flex items-center gap-3 ml-auto min-w-[220px] max-w-full">
              <div className="text-right shrink-0">
                <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{bannerProgress.label}</div>
                <div className="text-sm font-extrabold text-slate-800 tabular-nums">
                  {bannerProgress.done}<span className="text-slate-400 font-semibold">/{bannerProgress.total}</span>
                </div>
              </div>
              <div className="flex-1 min-w-[140px]">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Progress</span>
                  <span className="text-xs font-extrabold tabular-nums" style={{ color: progressColor }}>{bannerPct}%</span>
                </div>
                <div className="h-3 rounded-full bg-slate-100 overflow-hidden border border-slate-200">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${bannerPct}%`, background: progressColor }}
                  />
                </div>
              </div>
            </div>
          </div>
        )
      })()}

      <CaseWorkflowTransitions
        caseData={caseData}
        teamMembers={teamMembers}
        onTransitionComplete={loadCase}
      />

      {/* BODY — left section nav + main content + right sidebar */}
      <div className="flex gap-0 mx-auto" style={{ maxWidth: hidePatronSidebar ? 'none' : 1400 }}>

        {/* LEFT SECTION NAV */}
        <div className="hidden lg:block flex-shrink-0" style={{ width: 210 }}>
          <div className="sticky top-0 pr-3 max-h-[calc(100dvh-8rem)] overflow-y-auto">
            {(() => {
              const investigationItems = isUnknown
                ? [
                    { key: 'steps', label: 'Steps', icon: BookOpen },
                    { key: 'evidence', label: 'Evidence', icon: Folder },
                    { key: 'suspects', label: 'Suspects', icon: Users, count: suspects.length },
                  ]
                : [
                    { key: 'steps', label: 'Steps', icon: BookOpen },
                    { key: 'evidence', label: 'Evidence', icon: Folder },
                    { key: 'suspects', label: 'Suspects', icon: Users, count: suspects.length },
                    { key: 'transactions', label: 'Linked Txns', icon: Activity },
                    { key: 'kyc', label: 'KYC & AML', icon: Fingerprint },
                  ]

              const analysisItems = isUnknown ? [] : [
                { key: 'patterns', label: 'Patterns', icon: BarChart2 },
                { key: 'canvas', label: 'Canvas', icon: LayoutGrid },
                { key: 'scored', label: 'Scored', icon: Scale },
                { key: 'tree', label: 'Tree', icon: GitBranch },
              ]

              const ecddItems = showEcddTasksNav(caseData) ? [
                { key: 'ecdd_tasks', label: 'ECDD Tasks', icon: Shield },
              ] : []

              const smrItems = showSmrFormNav(caseData) ? [
                { key: 'smr_form', label: 'SMR Form', icon: AlertTriangle },
              ] : []

              const groups = [
                { label: 'Investigation', items: investigationItems },
                ...(analysisItems.length > 0 ? [{ label: 'Analysis', items: analysisItems }] : []),
                ...(ecddItems.length > 0 ? [{ label: 'ECDD', items: ecddItems, color: '#7c3aed' }] : []),
                ...(smrItems.length > 0 ? [{ label: 'SMR', items: smrItems, color: '#dc2626' }] : []),
                { label: null, items: [
                  { key: 'timeline', label: 'Activity Log', icon: Clock },
                  { key: 'outcome', label: 'Outcome', icon: Flag },
                ]},
              ]

              return groups.map((group, gi) => (
                <div key={gi} className={gi > 0 ? 'mt-3' : ''}>
                  {group.label && (
                    <div className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 mb-0.5"
                      style={{ color: group.color || '#94a3b8' }}>
                      {group.label}
                    </div>
                  )}
                  {!group.label && gi > 0 && <div className="border-t border-slate-200 my-2 mx-2" />}
                  {group.items.map(tab => {
                    const Icon = tab.icon
                    const active = activeTab === tab.key
                    const stepStatus = tab.key === 'steps'
                      ? (STEPS.every(s => s.status === 'done' || s.status === 'skipped') && STEPS.length > 0 ? 'done' : STEPS.some(s => s.status === 'in_progress' || s.status === 'done') ? 'in_progress' : 'not_started')
                      : null
                    return (
                      <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className="w-full flex items-center gap-2 px-2.5 py-2 border-none cursor-pointer text-xs font-semibold rounded-md transition-colors mb-0.5"
                        style={{
                          background: active ? P.priM : 'transparent',
                          color: active ? P.pri : '#64748b',
                          borderLeft: active ? `3px solid ${P.pri}` : '3px solid transparent',
                        }}
                      >
                        <Icon size={14} className="flex-shrink-0" />
                        <span className="flex-1 text-left">
                          {tab.label}
                          {tab.count > 0 ? ` (${tab.count})` : ''}
                        </span>
                        {tab.key === 'outcome' && outcome && (
                          <span className="w-2 h-2 rounded-full bg-green-600 flex-shrink-0" />
                        )}
                        {tab.key === 'suspects' && tab.count > 0 && (
                          <span className="min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-violet-100 text-violet-800 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                            {tab.count}
                          </span>
                        )}
                        {stepStatus === 'done' && <span className="w-2 h-2 rounded-full bg-green-600 flex-shrink-0" />}
                        {stepStatus === 'in_progress' && <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />}
                      </button>
                    )
                  })}
                </div>
              ))
            })()}
            {/* Tools toggle at bottom of nav */}
            <div className="border-t border-slate-200 mt-3 pt-3 mx-2">
              <button
                onClick={() => {
                  setHelpPanelOpen(false)
                  setToolsPanelOpen(prev => !prev)
                }}
                className="w-full flex items-center gap-2 px-2.5 py-2 border-none cursor-pointer text-xs font-semibold rounded-md transition-colors"
                style={{
                  background: toolsPanelOpen ? P.priM : 'transparent',
                  color: toolsPanelOpen ? P.pri : '#64748b',
                }}
                title="Tools (Narrow Down, Browse Data)"
              >
                <PanelRightOpen size={14} className="flex-shrink-0" />
                <span className="flex-1 text-left">Tools</span>
              </button>
            </div>
          </div>
        </div>

        {/* MOBILE TAB BAR — visible only on small screens */}
        <div className="lg:hidden overflow-x-auto mb-4 -mx-4 px-4">
          <div className="flex gap-0 flex-nowrap min-w-max">
            {(isUnknown
              ? [
                  { key: 'steps', label: 'Steps', icon: BookOpen },
                  { key: 'evidence', label: 'Evidence', icon: Folder },
                  { key: 'suspects', label: 'Suspects', icon: Users },
                ]
              : (() => {
                  const base = [
                    { key: 'steps', label: 'Steps', icon: BookOpen },
                    { key: 'evidence', label: 'Evidence', icon: Folder },
                    { key: 'suspects', label: suspects.length ? `Suspects (${suspects.length})` : 'Suspects', icon: Users },
                    { key: 'transactions', label: 'Txns', icon: Activity },
                    { key: 'kyc', label: 'KYC', icon: Fingerprint },
                    { key: 'patterns', label: 'Patterns', icon: BarChart2 },
                    { key: 'canvas', label: 'Canvas', icon: LayoutGrid },
                  ]
                  if (showEcddTasksNav(caseData)) base.push({ key: 'ecdd_tasks', label: 'ECDD', icon: Shield })
                  if (showSmrFormNav(caseData)) base.push({ key: 'smr_form', label: 'SMR', icon: AlertTriangle })
                  base.push(
                    { key: 'timeline', label: 'Log', icon: Clock },
                    { key: 'outcome', label: 'Outcome', icon: Flag },
                  )
                  return base
                })()
            ).map(tab => {
              const Icon = tab.icon
              const active = activeTab === tab.key
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 py-2 px-3 border-none cursor-pointer text-xs font-bold rounded-t-lg transition-colors ${active ? 'bg-gray-100' : 'bg-transparent'}`}
                  style={{ color: active ? P.pri : '#94a3b8' }}
                >
                  <Icon size={13} />
                  {tab.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div className={`flex-1 min-w-0 ${hidePatronSidebar ? '' : 'lg:mr-5'}`}>
          {activeTab === 'steps' && (
            <>
              <SectionCard title="Source / Trigger" icon="⚡">
                <div className="space-y-4">
                  {(() => {
                    const snap = CASE.source.snapshot || {}
                    const rawType = String(CASE.source.type || 'event').toLowerCase()
                    let sourceType = rawType
                    if (snap.TransactionPK || snap.transaction_id) sourceType = 'transaction'
                    else if (snap.SessionPK || snap.session_id) sourceType = 'session'
                    else if (snap.source === 'payout' || snap.payout_id) sourceType = 'payout'
                    const triggerRef = resolveSourceTriggerRef(CASE.source, snap)
                    const typeLabel = humanizeSourceType(sourceType)
                    const showTypePrefix = !triggerRef || triggerRef.label !== typeLabel
                    return (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                        <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">What opened this case</div>
                        <div className="text-sm font-semibold text-slate-800">{CASE.source.ruleName}</div>
                        <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          {showTypePrefix && <span>{typeLabel}</span>}
                          {triggerRef && (
                            <>
                              {showTypePrefix && <span className="text-slate-300">·</span>}
                              <span className="text-slate-500">{triggerRef.label}:</span>
                              <span className="font-mono text-slate-700">{triggerRef.value}</span>
                            </>
                          )}
                          {CASE.source.ruleId && CASE.source.ruleId !== '—' && (
                            <>
                              <span className="text-slate-300">·</span>
                              <span className="font-mono text-slate-400">{CASE.source.ruleId}</span>
                            </>
                          )}
                        </div>
                        {CASE.source.alertRef && (
                          <div className="text-xs mt-1.5">
                            <span className="text-slate-500">Linked alert </span>
                            <span className="font-mono font-semibold" style={{ color: P.pri }}>{CASE.source.alertRef}</span>
                          </div>
                        )}
                      </div>
                    )
                  })()}
                  {(() => {
                    const snap = CASE.source.snapshot || {}
                    const rawType = String(CASE.source.type || 'event').toLowerCase()
                    let sourceType = rawType
                    if (snap.TransactionPK || snap.transaction_id) sourceType = 'transaction'
                    else if (snap.SessionPK || snap.session_id) sourceType = 'session'

                    if (snap.source === 'payout' || snap.payout_id) {
                      const payoutName =
                        snap.payout_capture_name || snap.MemberName || snap.member_name || caseData?.member_name
                      const profileName = snap.patron_profile_name || null
                      const nameMismatch = Boolean(snap.payout_name_mismatch || snap.name_mismatch_note)
                      const payoutRows = []
                      if (snap.amount != null) payoutRows.push({ label: 'Amount', val: fmt$(snap.amount), bold: true })
                      if (snap.Venue) payoutRows.push({ label: 'Venue', val: String(snap.Venue) })
                      const payoutTime = snap.TimeStart || snap.time_start || snap.SnapShotTime || snap.snapshot_time
                      if (payoutTime) {
                        payoutRows.push({
                          label: 'Time',
                          val: formatSydneyDateTime(new Date(payoutTime)),
                        })
                      }
                      if (payoutRows.length === 0) return null
                      return (
                        <div>
                          <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Payout context</div>
                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                            {payoutRows.map((row) => (
                              <React.Fragment key={row.label}>
                                <span className="text-slate-500 font-medium">{row.label}:</span>
                                <span className={`text-slate-800 ${row.bold ? 'font-semibold' : ''}`}>{row.val}</span>
                              </React.Fragment>
                            ))}
                          </div>
                          {nameMismatch && (
                            <div className="mt-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2.5 py-2 leading-snug">
                              {snap.name_mismatch_note ||
                                `Payout name "${payoutName}" differs from patron profile "${profileName}". See header for current subject.`}
                            </div>
                          )}
                        </div>
                      )
                    }

                    if (sourceType === 'transaction') {
                      const txData = {
                        date: snap.SnapShotTime || snap.snapshot_time || snap.TransactionTime || snap.transaction_time,
                        amount: snap.CashOut ?? snap.CashIn ?? snap.amount ?? snap.Amount,
                        type: snap.TransactionType || snap.transaction_type || 'Unknown',
                        egm: snap.EgmHouseNo || snap.egm_house_no || snap.egmhouseno,
                        venue: snap.Venue || snap.venue,
                        memberId: snap.MemberID || snap.member_id,
                        method: snap.PaymentMethod || snap.payment_method,
                      }
                      return (
                        <div>
                          <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Transaction details</div>
                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                            {txData.date && (
                              <><span className="text-slate-500 font-medium">Date:</span><span className="text-slate-800">{formatSydneyDateTime(new Date(txData.date))}</span></>
                            )}
                            {txData.amount != null && (
                              <><span className="text-slate-500 font-medium">Amount:</span><span className="text-slate-800 font-semibold">{fmt$(txData.amount)}</span></>
                            )}
                            {txData.type && (
                              <><span className="text-slate-500 font-medium">Type:</span><span className="text-slate-800">{txData.type}</span></>
                            )}
                            {txData.egm && (
                              <><span className="text-slate-500 font-medium">EGM:</span><span className="text-slate-800 font-mono">{txData.egm}</span></>
                            )}
                            {txData.venue && (
                              <><span className="text-slate-500 font-medium">Venue:</span><span className="text-slate-800">{txData.venue}</span></>
                            )}
                            {txData.method && (
                              <><span className="text-slate-500 font-medium">Method:</span><span className="text-slate-800">{txData.method}</span></>
                            )}
                          </div>
                        </div>
                      )
                    }
                    
                    if (sourceType === 'session') {
                      const sessData = {
                        sessionId: snap.SessionPK || snap.session_id,
                        start: snap.start_time || snap.StartTime,
                        end: snap.end_time || snap.EndTime,
                        cashIn: snap.CashIn ?? snap.cash_in,
                        creditWin: snap.credit_win ?? snap.CreditWin,
                        turnover: snap.Turnover ?? snap.turnover,
                        strokes: snap.Stroke ?? snap.stroke ?? snap.strokes,
                        egm: snap.EgmHouseNo || snap.egm_house_no || snap.egmhouseno,
                        venue: snap.Venue || snap.venue,
                        memberId: snap.MemberID || snap.member_id,
                        memberName: snap.MemberName || snap.member_name,
                        deviceChanges: formatSnapshotDeviceChanges(snap.device_changes ?? snap.deviceChanges),
                        timeWindowHours:
                          snap.time_window_hours != null
                            ? snap.time_window_hours
                            : snap.timeWindowHours != null
                              ? snap.timeWindowHours
                              : null,
                      }
                      const duration =
                        sessData.start && sessData.end
                          ? Math.floor((new Date(sessData.end) - new Date(sessData.start)) / 60000)
                          : null
                      const sessionRows = []
                      if (sessData.sessionId) {
                        sessionRows.push({ label: 'Session ID', val: String(sessData.sessionId), money: false, mono: true })
                      }
                      if (sessData.memberId && !memberIdMatchesHeader(sessData.memberId, casePatronIdentity)) {
                        sessionRows.push({ label: 'Member', val: String(sessData.memberId), money: false, mono: true })
                      }
                      if (
                        sessData.memberName &&
                        !isUnknown &&
                        dedupePatronTitleLabel(String(sessData.memberName)).toLowerCase() !==
                          dedupePatronTitleLabel(CASE.patron.name).toLowerCase()
                      ) {
                        sessionRows.push({
                          label: 'Member name',
                          val: dedupePatronTitleLabel(String(sessData.memberName)),
                          money: false,
                          mono: false,
                        })
                      }
                      if (sessData.deviceChanges) {
                        sessionRows.push({ label: 'Device changes', val: sessData.deviceChanges, money: false, mono: false })
                      }
                      if (sessData.timeWindowHours != null && sessData.timeWindowHours !== '') {
                        sessionRows.push({
                          label: 'Time window (hours)',
                          val: String(sessData.timeWindowHours),
                          money: false,
                          mono: false,
                        })
                      }
                      if (sessData.start) {
                        sessionRows.push({
                          label: 'Start',
                          val: formatSydneyDateTime(new Date(sessData.start)),
                          money: false,
                          mono: false,
                        })
                      }
                      if (sessData.end) {
                        sessionRows.push({
                          label: 'End',
                          val: formatSydneyDateTime(new Date(sessData.end)),
                          money: false,
                          mono: false,
                        })
                      }
                      if (duration != null) {
                        sessionRows.push({
                          label: 'Duration',
                          val: duration >= 60 ? `${Math.floor(duration / 60)}h ${duration % 60}m` : `${duration}m`,
                          money: false,
                          mono: false,
                        })
                      }
                      if (sessData.cashIn != null) sessionRows.push({ label: 'Cash in', val: fmt$(sessData.cashIn), money: true, mono: false })
                      if (sessData.creditWin != null) sessionRows.push({ label: 'Credit win', val: fmt$(sessData.creditWin), money: true, mono: false })
                      if (sessData.turnover != null) sessionRows.push({ label: 'Turnover', val: fmt$(sessData.turnover), money: true, mono: false })
                      if (sessData.strokes != null) {
                        sessionRows.push({
                          label: 'Strokes',
                          val: Number(sessData.strokes).toLocaleString(),
                          money: false,
                          mono: false,
                        })
                      }
                      if (sessData.egm) sessionRows.push({ label: 'EGM', val: String(sessData.egm), money: false, mono: true })
                      if (sessData.venue) sessionRows.push({ label: 'Venue', val: String(sessData.venue), money: false, mono: false })
                      if (sessionRows.length > 0) {
                        return (
                          <div>
                            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Session details</div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                              {sessionRows.map((row, ri) => (
                                <React.Fragment key={`${row.label}-${ri}`}>
                                  <span className="text-slate-500 font-medium">{row.label}:</span>
                                  <span className={`text-slate-800 ${row.money ? 'font-semibold' : ''} ${row.mono ? 'font-mono' : ''}`}>
                                    {row.val}
                                  </span>
                                </React.Fragment>
                              ))}
                            </div>
                          </div>
                        )
                      }
                    }
                    
                    const SOURCE_SNAPSHOT_SKIP_KEYS = new Set([
                      'member_id', 'MemberID', 'member_name', 'MemberName',
                      'payout_capture_name', 'patron_profile_name', 'payout_id',
                      'source', 'amount', 'Venue', 'venue',
                    ])
                    if (Object.keys(snap).length > 0) {
                      const extraEntries = Object.entries(snap).filter(([k, v]) => {
                        if (/^_/.test(k)) return false
                        if (SOURCE_SNAPSHOT_SKIP_KEYS.has(k)) return false
                        if (k === 'description' && /demo|seed|test/i.test(String(snap.description || ''))) return false
                        if (
                          (k === 'member_id' || k === 'MemberID') &&
                          memberIdMatchesHeader(v, casePatronIdentity)
                        ) return false
                        return true
                      })
                      if (extraEntries.length === 0) return null
                      return (
                        <div>
                          <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Additional source fields</div>
                          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs space-y-2">
                            {extraEntries.map(([k, v]) => {
                              const val = typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v ?? '—')
                              const isLong = val.length > 60 || k === 'description'
                              return (
                                <div key={k} className={isLong ? '' : 'flex gap-2 flex-wrap'}>
                                  <span className="text-slate-500 font-medium flex-shrink-0">{k}:</span>
                                  <span className={`text-slate-800 ${isLong ? 'block mt-0.5' : ''}`}>{val}</span>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )
                    }
                    return null
                  })()}
                  {CASE.source.desc && CASE.source.desc !== 'Opened from alert or rule trigger' && (
                    <div className="text-sm text-slate-600 border-t border-slate-100 pt-3">{CASE.source.desc}</div>
                  )}
                </div>
              </SectionCard>

              <LinkedTransactionsSection
                transactions={TRANSACTIONS}
                loading={transactionsLoading}
                onLinkMore={() => {
                  setActivityAddPreset(null)
                  setLinkTransactionOpen(true)
                }}
              />

              <SectionCard title="Investigation Steps" icon="🔬" action={
                <button type="button" onClick={() => setAddStepOpen(true)} className="text-xs py-1 px-3 rounded-md border font-semibold flex items-center gap-1" style={{ borderColor: P.pri, background: P.priM, color: P.pri }}>
                  <Plus size={12} /> Add Step
                </button>
              }>
                <div className="text-[11px] text-slate-400 mb-3">
                  Each step documents what was investigated, findings, and any evidence collected. These records form the audit trail for this matter.
                </div>
                {STEPS.map(step => (
                  <StepRow
                    key={step.id}
                    caseId={id}
                    step={step}
                    expanded={expandedStep === step.id}
                    onExpand={() => setExpandedStep(expandedStep === step.id ? null : step.id)}
                    onAttachEvidence={handleAttachEvidence}
                    onBrowseData={handleBrowseFromStep}
                    onViewEvidence={handleViewEvidence}
                    onDeleteEvidence={handleDeleteEvidence}
                    deletingEvidenceId={deletingEvidenceId}
                    onMarkInProgress={s => handleUpdateStep(s, { status: 'in_progress' })}
                    onMarkComplete={s => handleUpdateStep(s, { status: 'done' })}
                    onSkip={s => handleUpdateStep(s, { status: 'skipped' })}
                    onAssign={s => setAssignModal(s)}
                    onSaveNotes={(s, notes) => handleUpdateStep(s, { notes })}
                    savingStepId={savingStepId}
                  />
                ))}
              </SectionCard>

            </>
          )}

          {activeTab === 'suspects' && (
            <SuspectsTab
              suspects={suspects}
              onRemove={removeSuspect}
              onConfirm={confirmSuspect}
              onViewDetails={(s) => { setDetailsModalSuspect(s); setDetailsModalTab('identity'); fetchSuspectDetails(s) }}
              expandedId={expandedSuspectId}
              onToggleExpand={(sid) => {
                setExpandedSuspectId((prev) => {
                  const next = prev === sid ? null : sid
                  if (next != null) {
                    const suspect = suspects.find((s) => s.id === sid)
                    if (suspect && !suspectDetails[sid] && !suspectDetailsLoading[sid]) {
                      fetchSuspectDetails(suspect)
                    }
                  }
                  return next
                })
              }}
              suspectDetails={suspectDetails}
              suspectDetailsLoading={suspectDetailsLoading}
              onAddMember={(patron) =>
                addSuspect({
                  member_id: patron.member_id,
                  display_name: patron.display_name,
                  source: 'manual',
                })
              }
              onIdentifyFromPhoto={() => setFaceIdentifyModal({ mode: 'suspects' })}
              onSearchPatronsFromFace={(b64) => setFaceIdentifyModal({ mode: 'suspect_face', preloadBase64: b64 })}
            />
          )}

          {activeTab === 'evidence' && (
            <>
              <CctvRetentionBanner caseRow={caseData} snapshot={displaySnapshot} />
              <PayoutFloorLinkBanner caseRow={caseData} snapshot={displaySnapshot} />
              <VideoEvidenceSection caseRow={caseData} snapshot={displaySnapshot} sourceDataId={caseData?.source_data_id} />
              <PatronActivityTimeline
                caseId={caseData?.id ?? id}
                patronActivities={patronActivities}
                steps={STEPS}
                incidentDate={pickIncidentDate(caseData, displaySnapshot)}
                memberNumber={
                  caseData?.membership_number ||
                  displaySnapshot?.membership_number ||
                  displaySnapshot?.MembershipNumber ||
                  (memberId !== '—' ? memberId : '')
                }
                onRefresh={async () => {
                  await loadPatronActivity()
                  await loadLinkedTransactions()
                  await loadCase()
                }}
                onAddObservation={() => setObservationModalOpen(true)}
                onLinkFloorData={() => {
                  const incidentYmd = toLocalDateYmd(pickIncidentDate(caseData, displaySnapshot))
                  const gamingStep = STEPS.find((s) => /session|gaming/i.test(String(s.title || '')))
                  setActivityAddPreset({
                    defaultLane: 'combined',
                    defaultCombinedView: 'session',
                    defaultTradingDay: incidentYmd || '',
                    stepId: gamingStep?.id ?? null,
                    title: 'Link floor data',
                  })
                  setLinkTransactionOpen(true)
                }}
              />
              <SectionCard title="Evidence Register" icon="📎">
              <div className="text-xs text-slate-500 mb-3.5">
                All evidence attached at any investigation step is consolidated here. Evidence is retained as part of the 7-year compliance record.
                {' '}Venue CCTV is typically retained for {CCTV_RETENTION_DAYS} days from the incident — see the warning above for days remaining.
              </div>
              {STEPS.length > 0 && (
                <div className="mb-3 flex flex-col sm:flex-row sm:items-center gap-2">
                  <label htmlFor="evidence-upload-step" className="text-[11px] font-bold text-slate-500 uppercase tracking-wider shrink-0">
                    Attach to step
                  </label>
                  <select
                    id="evidence-upload-step"
                    value={evidenceUploadStepId ?? ''}
                    onChange={(e) => setEvidenceUploadStepId(Number(e.target.value) || null)}
                    className="flex-1 max-w-md text-sm border border-slate-200 rounded-md px-2.5 py-1.5 bg-white text-slate-800"
                  >
                    {STEPS.map((s) => (
                      <option key={s.id} value={s.id}>{s.title}</option>
                    ))}
                  </select>
                </div>
              )}
              {STEPS.flatMap(s => (s.evidence || []).map((e, i) => ({
                name: e.file_name || 'file',
                stepTitle: s.title,
                stepId: s.id,
                evidence: e,
                step: s
              }))).map((f, i) => (
                <div key={`${f.stepId}-${f.evidence?.id || i}`} className="flex items-center gap-3 py-3 px-3.5 bg-slate-50 border border-slate-200 rounded-lg mb-2">
                  <EvidenceFileThumb caseId={id} stepId={f.stepId} evidence={f.evidence} size={52} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">{f.name}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">Step: {f.stepTitle}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleViewEvidence(f.step, f.evidence)}
                    className="py-1 px-3 text-[11px] font-semibold rounded-md border flex items-center gap-1"
                    style={{ borderColor: P.pri, background: P.priM, color: P.pri }}
                  >
                    <Eye size={11} /> View
                  </button>
                  {isEvidenceImage(f.evidence?.mime_type, f.name) && (
                    <button
                      type="button"
                      onClick={() =>
                        setFaceIdentifyModal({
                          mode: 'evidence',
                          stepId: f.stepId,
                          evidenceId: f.evidence?.id,
                          fileName: f.name,
                        })
                      }
                      className="py-1 px-3 text-[11px] font-semibold rounded-md border flex items-center gap-1 bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                    >
                      <ScanFace size={11} /> Search patrons
                    </button>
                  )}
                  {f.evidence?.id ? (
                    <button
                      type="button"
                      onClick={() => handleDeleteEvidence(f.step, f.evidence)}
                      disabled={deletingEvidenceId === f.evidence.id}
                      className="py-1 px-3 text-[11px] font-semibold rounded-md border flex items-center gap-1 bg-white text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-50"
                      title="Delete evidence"
                    >
                      {deletingEvidenceId === f.evidence.id
                        ? <Loader2 size={11} className="animate-spin" />
                        : <Trash2 size={11} />}
                      Delete
                    </button>
                  ) : null}
                </div>
              ))}
              {STEPS.length === 0 ? (
                <div className="py-5 text-center text-slate-400 text-xs border-2 border-dashed border-slate-200 rounded-lg">
                  Add an investigation step before uploading evidence.
                </div>
              ) : (
                <PasteScreenshotZone
                  disabled={!evidenceUploadStepId}
                  uploading={Boolean(savingStepId && STEPS.some((s) => s.id === savingStepId))}
                  onImageFile={(file) => {
                    const step = STEPS.find((s) => s.id === evidenceUploadStepId) || STEPS[0]
                    if (step) handleAttachEvidence(step, file)
                  }}
                />
              )}
            </SectionCard>
            </>
          )}

          {activeTab === 'transactions' && (
            <LinkedTransactionsSection
              transactions={TRANSACTIONS}
              loading={transactionsLoading}
              onLinkMore={() => {
                setActivityAddPreset(null)
                setLinkTransactionOpen(true)
              }}
            />
          )}

          {activeTab === 'kyc' && !isUnknown && (
            <KycAmlTab
              memberId={memberId}
              isValidMemberId={isValidMemberId}
              overview={overview}
              verificationCapture={verificationCapture}
              amlChecks={amlChecks}
              amlLoading={amlLoading}
              patronName={patronName}
              onReScreen={handleReScreen}
              onViewAmlCheck={viewAmlCheckDetails}
              KycBadge={KycBadge}
              fmt$={fmt$}
              C={C}
              SC={SC}
            />
          )}

          {activeTab === 'patterns' && !isUnknown && (
            <PatternsTab
              overview={overview}
              caseData={caseData}
              isValidMemberId={isValidMemberId}
              fmt$={fmt$}
              C={C}
            />
          )}

          {(['canvas', 'scored', 'tree']).map(key => (
            <div key={key} style={{ display: activeTab === key ? 'block' : 'none' }}>
              {(activeTab === 'canvas' || activeTab === 'scored' || activeTab === 'tree') && (
                <div className="mb-4">
                  <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">Typology</div>
                  <TypologySelector value={typology} onChange={setTypology} />
                </div>
              )}
              {key === 'canvas' && (
                <InvestigationCanvasStrategy
                  caseData={{ ...CASE, source_of_funds_status: caseData?.source_of_funds_status }}
                  transactions={transactions}
                  summary={CASE.summary}
                  overview={overview.data}
                  amlChecks={amlChecks}
                  fmt$={fmt$}
                  caseId={id}
                  onUpdateSof={handleUpdateSof}
                  typology={typology}
                  onTypologyChange={setTypology}
                />
              )}
              {key === 'scored' && (
                <InvestigationScoredStrategy
                  caseData={CASE}
                  patron={CASE.patron}
                  transactions={transactions}
                  overview={overview.data}
                  fmt$={fmt$}
                  typology={typology}
                />
              )}
              {key === 'tree' && (
                <InvestigationTreeStrategy
                  caseData={CASE}
                  patron={CASE.patron}
                  transactions={transactions}
                  overview={overview.data}
                  fmt$={fmt$}
                  typology={typology}
                />
              )}
            </div>
          ))}

          {activeTab === 'timeline' && (
            <SectionCard title="Activity Log" icon="🕐">
              <div className="mb-4">
                <div className="text-[11px] font-bold text-slate-500 mb-2 uppercase tracking-wider">Add a Note</div>
                <textarea
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  placeholder="Record an observation, decision, or update…"
                  className="w-full min-h-[80px] text-sm border border-slate-200 rounded-md p-2.5 box-border resize-y"
                />
                <button type="button" onClick={handleAddNote} className="mt-2 text-xs py-1.5 px-4 border-none rounded-md font-bold cursor-pointer text-white" style={{ background: P.pri }}>
                  Add Note
                </button>
              </div>
              <div className="border-t border-slate-100 pt-4">
                {TIMELINE.map((ev, i) => <TimelineEvent key={i} event={ev} />)}
              </div>
            </SectionCard>
          )}

          {/* ECDD inline section — appears when escalated */}
          {activeTab === 'ecdd_tasks' && showEcddTasksNav(caseData) && (
            <EcddInlinePanel
              caseId={String(caseData.id)}
              memberId={caseData.member_id || ''}
              memberName={CASE.patron.name}
            />
          )}

          {/* SMR inline section — appears when escalated (spec: docs/SMR_FORM_UI_SPEC.md) */}
          {activeTab === 'smr_form' && showSmrFormNav(caseData) && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Pill color="#dc2626" bg="#fef2f2" label="SMR Phase" />
              </div>
              <SmrFormPanelV2
                caseId={String(caseData.id)}
                caseNumber={caseData.case_number || CASE.id}
                patronName={CASE.patron.name}
                memberId={caseData.member_id || ''}
                onFiled={loadCase}
              />
            </div>
          )}

          {activeTab === 'outcome' && (
            <OutcomePanel
              outcome={outcome}
              setOutcome={setOutcome}
              rationale={outcomeRationale}
              setRationale={setOutcomeRationale}
              onFinalise={handleFinaliseOutcome}
              saving={outcomeSaving}
              alreadyFinalised={!!caseData?.outcome}
            />
          )}
        </div>

        {/* SIDEBAR — hidden on full-width tabs (analysis, ECDD tasks, etc.) */}
        {!hidePatronSidebar ? (
        <div className="w-full lg:w-72 flex-shrink-0 mt-6 lg:mt-0">
          <div className="bg-white border border-slate-200 rounded-lg p-4 mb-3.5">
            <div className="text-[11px] font-bold text-slate-500 mb-2.5 uppercase tracking-wider">Compliance snapshot</div>
            {overview.loading && !overview.data ? (
              <div className="flex items-center gap-1.5 text-[11px] text-slate-400 py-2">
                <Loader2 size={12} className="animate-spin" />
                Loading profile details…
              </div>
            ) : null}
            {(() => {
              const kycAml = overview.data?.kyc_aml || {}
              const alerts = overview.data?.alerts || []
              const isUnidentified = !caseData.member_id
              const profilePending = Boolean(caseData.member_id && overview.loading && !overview.data)
              const amlPending = Boolean(caseData.member_id && amlChecksLoading && amlChecks.length === 0 && !overview.data)
              const hasAmlReview = investigationAmlNeedsReview(kycAml, amlChecks, alerts)
              const amlLabel = isUnidentified
                ? 'N/A'
                : amlPending
                  ? 'Loading…'
                  : resolveInvestigationAmlLabel(kycAml, amlChecks, alerts)
              const kycLabel = isUnidentified
                ? 'N/A'
                : profilePending
                  ? 'Loading…'
                  : (overview.data?.kyc_aml ? (kycAml.kyc_completed || /PASSED|VERIFIED|MATCHED/i.test(kycAml.dvs_status || '') ? 'Verified' : 'Pending') : CASE.patron.kycStatus)
              const amlColor = isUnidentified || amlPending ? '#64748b' : (hasAmlReview ? '#d97706' : '#16a34a')
              const amlBg = isUnidentified || amlPending ? '#f8fafc' : (hasAmlReview ? '#fffbeb' : '#f0fdf4')
              const kycColor = isUnidentified || profilePending ? '#64748b' : (kycLabel === 'Verified' ? '#16a34a' : '#d97706')
              const kycBg = isUnidentified || profilePending ? '#f8fafc' : (kycLabel === 'Verified' ? '#f0fdf4' : '#fffbeb')
              const riskFromCase = caseData?.patron_compliance?.risk_score ?? CASE.patron.risk
              return [
                ['Risk Score', <span key="r" className={`font-bold ${riskFromCase >= 90 ? 'text-red-600' : riskFromCase >= 70 ? 'text-orange-600' : riskFromCase >= 40 ? 'text-amber-600' : 'text-green-700'}`}>{riskFromCase != null ? `${riskFromCase} / 100` : (profilePending ? '…' : '—')}</span>],
                ['KYC Status', <Pill key="k" color={kycColor} bg={kycBg} label={kycLabel} />],
                ['AML / PEP', <Pill key="a" color={amlColor} bg={amlBg} label={amlLabel} />],
                ['DOB', profilePending ? '…' : CASE.patron.dob],
                ['Occupation', profilePending ? '…' : (overview.data?.profile?.occupation ?? CASE.patron.occupation)],
                ['Address', profilePending ? '…' : ((overview.data?.profile?.address || overview.data?.profile?.address1 || overview.data?.profile?.residential_address) ? [overview.data.profile.address || overview.data.profile.address1 || overview.data.profile.residential_address, overview.data.profile.address2, overview.data.profile.suburb || overview.data.profile.city, overview.data.profile.state, overview.data.profile.postcode].filter(Boolean).join(', ') : CASE.patron.address)],
              ]
            })().map(([label, val]) => (
              <div key={label} className="flex justify-between items-start py-1.5 border-b border-slate-100">
                <span className="text-[11px] text-slate-400 flex-shrink-0 mr-2">{label}</span>
                <span className="text-[11px] text-slate-700 text-right">{val}</span>
              </div>
            ))}
            {caseData.member_id && (
              <CddTierCard memberId={caseData.member_id} compact />
            )}
            <div className="pt-2 mt-1 border-t border-slate-100">
              <div className="text-[11px] font-bold text-slate-500 mb-1.5 uppercase tracking-wider">Related cases</div>
              {(() => {
                const sidebarAlerts = CASE.linkedAlerts.filter((a) => a !== CASE.source.alertRef)
                if (sidebarAlerts.length === 0) return null
                return sidebarAlerts.map(a => {
                const alertNum = a.replace('ALT-', '')
                return (
                  <div key={a} className="flex items-center gap-2 py-1.5 border-b border-slate-100">
                    <AlertTriangle size={13} color="#d97706" />
                    <Link to={`/monitor-safe/alerts/${alertNum}`} className="text-xs font-mono underline" style={{ color: P.pri }}>{a}</Link>
                  </div>
                )
              })})()}
              <div className="flex justify-between items-center py-1.5 border-b border-slate-100 text-xs gap-2">
                <span className="text-[11px] text-slate-400 shrink-0">ECDD (this case)</span>
                {existingEcddId && ecddProfileLink ? (
                  <Link to={ecddProfileLink} className="text-[11px] font-mono font-semibold hover:underline text-right truncate" style={{ color: P.pri }}>
                    {existingEcddId}
                  </Link>
                ) : (
                  <span className="text-[11px] text-slate-600">None</span>
                )}
              </div>
              <div className="flex justify-between items-start py-1.5 border-b border-slate-100 text-xs gap-2">
                <span className="text-[11px] text-slate-400 shrink-0">Past ECDD</span>
                {previousHistoryLoading ? (
                  <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                    <Loader2 size={11} className="animate-spin" /> Loading…
                  </span>
                ) : CASE.pastEcddCases.length > 0 ? (
                  <div className="text-right space-y-0.5 min-w-0">
                    {CASE.pastEcddCases.slice(0, 3).map(c => (
                      <Link key={c.id} to={`/compliance-safe/investigations/${encodeURIComponent(c.case_number)}`} className="block text-[11px] font-mono hover:underline truncate" style={{ color: P.pri }}>
                        {c.case_number}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <span className="text-[11px] text-slate-600">None</span>
                )}
              </div>
              <div className="flex justify-between items-start py-1.5 text-xs gap-2">
                <span className="text-[11px] text-slate-400 shrink-0">Past SMR</span>
                {previousHistoryLoading ? (
                  <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                    <Loader2 size={11} className="animate-spin" /> Loading…
                  </span>
                ) : CASE.pastSmrCases.length > 0 ? (
                  <div className="text-right space-y-0.5 min-w-0">
                    {CASE.pastSmrCases.slice(0, 3).map(c => (
                      <Link key={c.id} to={`/compliance-safe/investigations/${encodeURIComponent(c.case_number)}`} className="block text-[11px] font-mono hover:underline truncate" style={{ color: P.pri }}>
                        {c.case_number}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <span className="text-[11px] text-slate-600">None</span>
                )}
              </div>
            </div>
            {profileSearchId && (
              <Link to={`/profile-safe/search?memberId=${encodeURIComponent(profileSearchId)}`} className="mt-3 block w-full text-center text-xs py-1.5 border rounded-md font-semibold flex items-center justify-center gap-1" style={{ borderColor: P.pri, background: P.priM, color: P.pri }}>
                <Eye size={12} /> View Full Profile
              </Link>
            )}
          </div>

          <FormsModuleGate>
            <div className="mb-3.5">
              <RecordFormsPanel
                compact
                subjectType="case"
                subjectId={String(caseData?.id || caseData?.case_number || '')}
                subjectLabel={CASE.patron.name || caseData?.case_number || 'This investigation'}
                patronId={String(
                  casePatronIdentity?.master_profile_id
                  || casePatronIdentity?.patron_id
                  || caseData?.member_id
                  || ''
                )}
                emailTo={String(overview.data?.profile?.email || '').trim()}
                smsTo={String(overview.data?.profile?.mobile || overview.data?.profile?.phone || '').trim()}
                readOnly={
                  ['CLOSED', 'RESOLVED', 'FILED'].includes(caseStatusUpper(caseData))
                  || !can('compliance-safe/forms', 'assign')
                }
                title="Forms"
              />
            </div>
          </FormsModuleGate>

          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="text-[11px] font-bold text-slate-500 mb-2.5 uppercase tracking-wider">Quick Actions</div>
            {[
              { label: 'Add Investigation Step', icon: Plus, color: P.pri, onClick: () => setAddStepOpen(true) },
              { label: 'Upload Evidence', icon: Upload, color: P.pri, onClick: () => { setActiveTab('evidence'); if (STEPS[0]) { setEvidenceStepForUpload(STEPS[0]); setEvidenceUploadStepId(STEPS[0].id) } } },
              { label: 'Add Co-Investigator', icon: Users, color: '#7c3aed', onClick: () => STEPS[0] && setAssignModal(STEPS[0]) },
              {
                label: 'Escalate to ECDD',
                icon: Shield,
                color: '#7c3aed',
                onClick: () => openEscalationQuickAction('ecdd'),
              },
              {
                label: 'Escalate to SMR',
                icon: AlertTriangle,
                color: '#dc2626',
                onClick: () => openEscalationQuickAction('smr'),
              },
            ].map(({ label, icon: Icon, color, onClick }) => (
              <button key={label} type="button" onClick={onClick} className="w-full text-left py-2 px-2.5 mb-1.5 border border-slate-200 rounded-md bg-slate-50 cursor-pointer text-xs font-semibold text-slate-700 flex items-center gap-2 hover:bg-slate-100">
                <Icon size={13} color={color} />
                {label}
              </button>
            ))}
          </div>
        </div>
        ) : null}
      </div>

      </div>

      {/* Add Step Modal */}
      {changeProfileOpen ? (
        <ChangeLinkedProfileModal
          open
          onClose={() => setChangeProfileOpen(false)}
          caseId={id}
          currentMemberId={caseData?.member_id || ''}
          currentMembershipNumber={
            caseData?.membership_number ||
            caseData?.source_data_snapshot?.membership_number ||
            casePatronIdentity?.membership_number ||
            ''
          }
          currentMemberName={CASE.patron?.name || caseData?.member_name || ''}
          onLinked={async () => {
            await loadCase()
            setActiveTab('kyc')
          }}
        />
      ) : null}

      <LinkTransactionModal
        caseId={caseData?.id ?? id}
        isOpen={linkTransactionOpen}
        onClose={() => {
          setLinkTransactionOpen(false)
          setActivityAddPreset(null)
        }}
        onAdded={async () => {
          await loadLinkedTransactions()
          await loadCase()
          setLinkTransactionOpen(false)
          setActivityAddPreset(null)
        }}
        defaultMemberId={(() => {
          const mn =
            caseData?.membership_number ||
            displaySnapshot?.membership_number ||
            displaySnapshot?.MembershipNumber
          if (mn) return String(mn)
          return memberId !== '—' ? memberId : ''
        })()}
        defaultMemberName={patronName || CASE.patron?.name || ''}
        defaultEgmHouseNo={(() => {
          const s = displaySnapshot && typeof displaySnapshot === 'object' ? displaySnapshot : {}
          return s.EgmHouseNo || s.egm_house_no || s.EgmPK || s.egmpk || ''
        })()}
        defaultLane={activityAddPreset?.defaultLane ?? 'payouts'}
        defaultCombinedView={activityAddPreset?.defaultCombinedView ?? 'session'}
        defaultTradingDay={activityAddPreset?.defaultTradingDay ?? ''}
        stepId={activityAddPreset?.stepId ?? null}
        title={activityAddPreset?.title ?? 'Link evidence'}
      />

      <AddActivityObservationModal
        isOpen={observationModalOpen}
        onClose={() => setObservationModalOpen(false)}
        caseId={caseData?.id ?? id}
        defaultObservedAt={pickIncidentDate(caseData, displaySnapshot)}
        defaultEgm={displaySnapshot?.EgmHouseNo || displaySnapshot?.egm_house_no || ''}
        onBrowseFloorLink={(pickFn) => {
          floorPickerHandlerRef.current = pickFn
          setFloorPickerOpen(true)
        }}
        onSaved={async () => {
          await loadPatronActivity()
          await loadLinkedTransactions()
          await loadCase()
        }}
      />

      <FloorActivityPickerModal
        isOpen={floorPickerOpen}
        onClose={() => {
          setFloorPickerOpen(false)
          floorPickerHandlerRef.current = null
        }}
        caseId={caseData?.id ?? id}
        defaultMemberNumber={
          caseData?.membership_number ||
          displaySnapshot?.membership_number ||
          (memberId !== '—' ? memberId : '')
        }
        defaultEgmHouseNo={displaySnapshot?.EgmHouseNo || displaySnapshot?.egm_house_no || ''}
        defaultTradingDay={toLocalDateYmd(pickIncidentDate(caseData, displaySnapshot)) || ''}
        onPick={(row) => {
          floorPickerHandlerRef.current?.(row)
          setFloorPickerOpen(false)
          floorPickerHandlerRef.current = null
        }}
      />

      {faceIdentifyModal && (
        <FaceIdentifyModal
          open
          onClose={() => setFaceIdentifyModal(null)}
          caseId={id}
          context={faceIdentifyModal.mode === 'evidence' ? 'evidence' : 'investigation'}
          evidenceStepId={faceIdentifyModal.stepId ?? null}
          evidenceId={faceIdentifyModal.evidenceId ?? null}
          title={
            faceIdentifyModal.mode === 'evidence'
              ? 'Search patrons from evidence'
              : faceIdentifyModal.mode === 'suspect_face'
                ? 'Search patrons from VMS photo'
                : 'Identify from photo'
          }
          subtitle={
            faceIdentifyModal.mode === 'evidence'
              ? 'Identify enrolled patrons from this evidence still. Adding a suspect does not confirm the case subject.'
              : faceIdentifyModal.mode === 'suspect_face'
                ? 'Search ProfileSafe using the visitor system face capture. Adding a match does not confirm the case subject.'
                : 'Search ProfileSafe face-enrolled patrons and add as a suspect candidate.'
          }
          preloadFromEvidence={
            faceIdentifyModal.mode === 'evidence' && faceIdentifyModal.evidenceId
              ? {
                  caseId: id,
                  stepId: faceIdentifyModal.stepId,
                  evidenceId: faceIdentifyModal.evidenceId,
                  fileName: faceIdentifyModal.fileName,
                }
              : null
          }
          preloadFromBase64={
            faceIdentifyModal.mode === 'suspect_face' && faceIdentifyModal.preloadBase64
              ? { base64: faceIdentifyModal.preloadBase64, fileName: 'vms-face.jpg' }
              : null
          }
          onAddSuspect={addSuspect}
        />
      )}

      {addStepOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setAddStepOpen(false)}>
          <div className="bg-white rounded-lg p-5 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="text-sm font-bold text-slate-800 mb-3">Add Investigation Step</div>
            <input
              value={addStepTitle}
              onChange={e => setAddStepTitle(e.target.value)}
              placeholder="Step title"
              className="w-full text-sm border border-slate-200 rounded-md p-2.5 mb-3"
            />
            <select value={addStepCategory} onChange={e => setAddStepCategory(e.target.value)} className="w-full text-sm border border-slate-200 rounded-md p-2.5 mb-4">
              {STEP_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setAddStepOpen(false)} className="px-4 py-2 text-sm border border-slate-200 rounded-md">Cancel</button>
              <button type="button" onClick={handleAddStep} className="px-4 py-2 text-sm rounded-md text-white font-semibold" style={{ background: P.pri }}>Add Step</button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {assignModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setAssignModal(null)}>
          <div className="bg-white rounded-lg p-5 w-full max-w-sm shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="text-sm font-bold text-slate-800 mb-3">Assign step: {assignModal.title}</div>
            <select
              className="w-full text-sm border border-slate-200 rounded-md p-2.5 mb-4"
              defaultValue={assignModal.assignedTo || ''}
              onChange={e => handleAssign(assignModal, e.target.value || null)}
            >
              <option value="">— Unassigned —</option>
              {ASSIGNEES.length === 0 ? (
                <option value="" disabled>No staff in directory</option>
              ) : (
                ASSIGNEES.map((a) => <option key={a} value={a}>{a}</option>)
              )}
            </select>
            <button type="button" onClick={() => setAssignModal(null)} className="w-full py-2 text-sm border border-slate-200 rounded-md">Close</button>
          </div>
        </div>
      )}

      {/* Suspect Details Modal */}
      {detailsModalSuspect && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDetailsModalSuspect(null)}>
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 shrink-0">
              <h3 className="text-base font-bold text-slate-800">
                {detailsModalSuspect.display_name || (detailsModalSuspect.member_id ? `Member ${detailsModalSuspect.member_id}` : `${labels.visitor} ${detailsModalSuspect.visitor_id}`)}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => fetchSuspectDetails(detailsModalSuspect)}
                  disabled={suspectDetailsLoading[detailsModalSuspect.id]}
                  className="text-xs py-1.5 px-3 rounded-md border font-semibold flex items-center gap-1"
                  style={{ borderColor: P.pri, background: P.priM, color: P.pri }}
                >
                  {suspectDetailsLoading[detailsModalSuspect.id] ? 'Loading…' : detailsModalSuspect.member_id ? 'Retrieve from member system' : 'Retrieve from visitor system'}
                </button>
                <button
                  type="button"
                  onClick={() => setDetailsModalSuspect(null)}
                  className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="flex border-b border-slate-100 shrink-0 overflow-x-auto">
              {['identity', 'visitor_history', 'kyc_status', 'aml_pep', 'prior_cases', 'actions'].map(tabKey => (
                <button
                  key={tabKey}
                  type="button"
                  onClick={() => setDetailsModalTab(tabKey)}
                  className={`flex-shrink-0 py-2.5 px-4 text-xs font-bold whitespace-nowrap ${detailsModalTab === tabKey ? 'border-b-2 text-slate-800' : 'text-slate-500'}`}
                  style={detailsModalTab === tabKey ? { borderBottomColor: P.pri } : {}}
                >
                  {tabKey === 'identity' && 'Identity'}
                  {tabKey === 'visitor_history' && `${labels.visitor} History`}
                  {tabKey === 'kyc_status' && 'KYC Status'}
                  {tabKey === 'aml_pep' && 'AML/PEP'}
                  {tabKey === 'prior_cases' && 'Prior Cases'}
                  {tabKey === 'actions' && 'Actions'}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {suspectDetailsLoading[detailsModalSuspect.id] && detailsModalTab !== 'actions' ? (
                <div className="text-sm text-slate-500 py-8 text-center">Loading details…</div>
              ) : (() => {
                const d = suspectDetails[detailsModalSuspect.id]
                const hasData = d && (d.visitorData || d.overview || (d.amlChecks && d.amlChecks.length > 0))
                const emptyMsg = (msg) => <div className="text-sm text-slate-500 py-4">{msg}</div>
                if (detailsModalTab === 'identity') {
                  const idRow = d?.visitorData?.identity || d?.overview?.profile
                  const visit = d?.visitorData?.visit
                  const vmsFace = d?.visitorData?.vms_id_face_b64 || d?.visitorData?.id_face_image
                  return idRow ? (
                    <div className="space-y-3 text-sm">
                      <div className="flex gap-4 items-start">
                        <div className="flex-1 space-y-2">
                          <div><span className="text-slate-500 font-medium">Name:</span> {idRow.display_name || [idRow.first_name, idRow.last_name].filter(Boolean).join(' ') || '—'}</div>
                          {(idRow.visitor_system_ref || idRow.member_id) && <div><span className="text-slate-500 font-medium">Ref:</span> {idRow.visitor_system_ref || idRow.member_id}</div>}
                          {idRow.date_of_birth && <div><span className="text-slate-500 font-medium">DOB:</span> {formatDateOnly(idRow.date_of_birth)}</div>}
                          {(idRow.email || idRow.phone) && <div><span className="text-slate-500 font-medium">Contact:</span> {[idRow.email, idRow.phone].filter(Boolean).join(' · ')}</div>}
                          {visit?.entry_datetime && <div><span className="text-slate-500 font-medium">Visit:</span> {formatSydneyDateTime(new Date(visit.entry_datetime))}</div>}
                          {visit?.document_type && <div><span className="text-slate-500 font-medium">Document:</span> {visit.document_type}{visit.document_number ? ` · ${visit.document_number}` : ''}</div>}
                          {d?.visitorData?.source && <div><span className="text-slate-500 font-medium">Source:</span> {d.visitorData.source === 'rxconnect' ? 'RXConnect VMS' : d.visitorData.source}</div>}
                        </div>
                        <VmsFaceThumbnail
                          b64={vmsFace}
                          onSearchPatrons={
                            vmsFace
                              ? () => setFaceIdentifyModal({ mode: 'suspect_face', preloadBase64: vmsFace })
                              : undefined
                          }
                        />
                      </div>
                    </div>
                  ) : emptyMsg(hasData ? 'No identity data.' : 'Click "Retrieve from visitor system" or "Retrieve from member system" to load data.')
                }
                if (detailsModalTab === 'visitor_history') {
                  const hist = d?.visitorData?.visitor_history || []
                  return hist.length > 0 ? (
                    <div className="space-y-2 text-sm">
                      {hist.slice(0, 20).map((h, i) => (
                        <div key={i} className="py-2 border-b border-slate-100 last:border-0">
                          {h.entry_datetime ? formatSydneyDateTime(new Date(h.entry_datetime)) : '—'} · {h.visitor_source || '—'}
                        </div>
                      ))}
                      {hist.length > 20 && <div className="text-slate-500 text-xs mt-2">+{hist.length - 20} more</div>}
                    </div>
                  ) : emptyMsg(detailsModalSuspect.visitor_id ? (hasData ? 'No visit history.' : 'Retrieve data to see visit history.') : `${labels.visitor} history applies to ${labels.visitor.toLowerCase()} records only.`)
                }
                if (detailsModalTab === 'kyc_status') {
                  const kyc = d?.visitorData?.kyc_status || d?.overview?.kyc_aml
                  return kyc ? (
                    <div className="space-y-2 text-sm">
                      <div><span className="text-slate-500 font-medium">DVS:</span> {kyc.dvs_status ?? '—'}</div>
                      <div><span className="text-slate-500 font-medium">Completed:</span> {kyc.kyc_completed ? 'Yes' : 'No'}</div>
                    </div>
                  ) : emptyMsg(hasData ? 'No KYC data.' : 'Retrieve data to see KYC status.')
                }
                if (detailsModalTab === 'aml_pep') {
                  const aml = d?.amlChecks || []
                  return aml.length > 0 ? (
                    <div className="space-y-2 text-sm">
                      <div><span className="text-slate-500 font-medium">Checks:</span> {aml.length}</div>
                      <div><span className="text-slate-500 font-medium">Latest:</span> {aml[0]?.result_count ?? 0} matches</div>
                    </div>
                  ) : emptyMsg(hasData ? 'No AML/PEP checks.' : 'Retrieve data to see AML/PEP screening history.')
                }
                if (detailsModalTab === 'prior_cases') {
                  const cases = d?.overview?.cases || d?.visitorData?.prior_cases || []
                  return cases.length > 0 ? (
                    <div className="space-y-2 text-sm">
                      {cases.slice(0, 10).map((c, i) => (
                        <div key={i} className="py-2 border-b border-slate-100 last:border-0">
                          {c.case_number || c.id} · {c.case_type || '—'} · {c.status || '—'}
                        </div>
                      ))}
                    </div>
                  ) : emptyMsg(hasData ? 'No prior cases.' : 'Retrieve data to see prior SMR/ECDD cases.')
                }
                if (detailsModalTab === 'actions') {
                  return (
                    <div className="space-y-3 text-sm">
                      <button
                        type="button"
                        onClick={() => fetchSuspectDetails(detailsModalSuspect)}
                        disabled={suspectDetailsLoading[detailsModalSuspect.id]}
                        className="text-xs py-2 px-4 rounded-md border font-semibold"
                        style={{ borderColor: P.pri, background: P.priM, color: P.pri }}
                      >
                        {suspectDetailsLoading[detailsModalSuspect.id] ? 'Loading…' : detailsModalSuspect.member_id ? 'Retrieve from patron profile' : 'Retrieve from visitor system'}
                      </button>
                      <p className="text-slate-500 text-xs">Fetch identity, KYC, AML screening and prior cases from the {detailsModalSuspect.member_id ? 'patron profile' : 'visitor system'}.</p>
                    </div>
                  )
                }
                return null
              })()}
            </div>
          </div>
        </div>
      )}

      {/* AML Check Details Modal */}
      {amlModalOpen && amlModalData && (
        <AmlModal
          data={amlModalData}
          onClose={() => setAmlModalOpen(false)}
          onReviewSaved={handleAmlReviewSaved}
          C={C}
        />
      )}

      <ScreenHelpPanel
        screenKey={CASE_HELP_SCREEN_KEY}
        open={helpPanelOpen}
        onClose={() => setHelpPanelOpen(false)}
        focusSectionId={primaryHelpSectionForTab(activeTab, isUnknown)}
        sectionIdPrefix="investigation-help"
      />

      {/* Tools slide-over panel */}
      {toolsPanelOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40 transition-opacity"
            onClick={() => setToolsPanelOpen(false)}
            aria-hidden="true"
          />
          <div
            className="fixed top-0 right-0 bottom-0 w-full max-w-[1100px] bg-white shadow-xl z-50 flex flex-col border-l border-slate-200 transition-transform duration-200 ease-out"
            style={{ boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 shrink-0">
              <h3 className="text-sm font-bold text-slate-800">Tools</h3>
              <button
                type="button"
                onClick={() => setToolsPanelOpen(false)}
                className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
                aria-label="Close tools panel"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex border-b border-slate-100 shrink-0">
              <button
                type="button"
                onClick={() => setToolsPanelSubTab('narrow')}
                className={`flex-1 py-2.5 px-4 text-xs font-bold ${toolsPanelSubTab === 'narrow' ? 'border-b-2 text-slate-800' : 'text-slate-500'}`}
                style={toolsPanelSubTab === 'narrow' ? { borderBottomColor: P.pri } : {}}
              >
                Narrow Down
              </button>
              <button
                type="button"
                onClick={() => setToolsPanelSubTab('browse')}
                className={`flex-1 py-2.5 px-4 text-xs font-bold ${toolsPanelSubTab === 'browse' ? 'border-b-2 text-slate-800' : 'text-slate-500'}`}
                style={toolsPanelSubTab === 'browse' ? { borderBottomColor: P.pri } : {}}
              >
                Browse Data
              </button>
            </div>
            <div className={`flex-1 overflow-y-auto p-4 min-h-0 ${toolsPanelSubTab === 'narrow' ? 'flex flex-col' : ''}`}>
              {toolsPanelSubTab === 'narrow' && (
                <NarrowingPanel
                  embeddedInToolsPanel
                  caseId={id}
                  caseRow={caseData}
                  caseSnapshot={displaySnapshot}
                  caseSourceDataId={caseData?.source_data_id}
                  visitors={visitorsInWindow}
                  visitorsHint={visitorsWindowHint}
                  loading={visitorsLoading}
                  onLoad={loadVisitorsInWindow}
                  onAddSuspect={addSuspect}
                  suspects={suspects}
                  addHint={suspectAddHint}
                />
              )}
              {toolsPanelSubTab === 'browse' && (
                <CaseBrowseData
                  key={`browse-${caseData?.id ?? id}-${browsePreset.seq}`}
                  caseId={caseData?.id ?? id}
                  onAdded={loadCase}
                  defaultVenue={getDefaultVenue(caseData)}
                  defaultMemberId={(() => {
                  const mn = caseData?.membership_number || caseData?.source_data_snapshot?.membership_number
                  if (mn) return String(mn)
                  return caseData?.member_id && caseData.member_id !== '—' ? caseData.member_id : ''
                })()}
                  defaultEgmHouseNo={(() => {
                    const snap = caseData?.source_data_snapshot
                    const s = snap && typeof snap === 'object' ? snap : {}
                    return s.EgmHouseNo || s.egm_house_no || s.EgmPK || s.egmpk || ''
                  })()}
                  defaultDays={browsePreset.days}
                  initialSubTab={browsePreset.subTab}
                  stepId={browsePreset.stepId}
                  stepTitle={browsePreset.stepTitle}
                  presetKey={browsePreset.seq}
                />
              )}
            </div>
          </div>
        </>
      )}
      {evidenceViewer && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-8"
          style={{ background: 'rgba(15,23,42,0.65)' }}
          onClick={closeEvidenceViewer}
          role="dialog"
          aria-modal="true"
          aria-label={evidenceViewer.fileName || 'Evidence preview'}
        >
          <div
            className="relative w-full max-w-5xl max-h-[90vh] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50 shrink-0">
              <div className="min-w-0">
                <div className="text-sm font-bold text-slate-800 truncate">{evidenceViewer.fileName}</div>
                <div className="text-[11px] text-slate-500">Evidence preview</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {evidenceViewer.url && !isEvidenceImage(evidenceViewer.mimeType, evidenceViewer.fileName) ? (
                  <a
                    href={evidenceViewer.url}
                    download={evidenceViewer.fileName}
                    className="py-1.5 px-3 text-xs font-semibold rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                  >
                    Download
                  </a>
                ) : null}
                <button
                  type="button"
                  onClick={closeEvidenceViewer}
                  className="p-1.5 rounded-md hover:bg-slate-200 text-slate-600"
                  aria-label="Close preview"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-slate-100 flex items-center justify-center min-h-[240px] p-4">
              {evidenceViewer.loading ? (
                <div className="flex flex-col items-center gap-2 text-slate-500 text-sm">
                  <Loader2 size={22} className="animate-spin" />
                  Loading…
                </div>
              ) : evidenceViewer.error ? (
                <div className="text-sm text-red-600">{evidenceViewer.error}</div>
              ) : evidenceViewer.url && isEvidenceImage(evidenceViewer.mimeType, evidenceViewer.fileName) ? (
                <img
                  src={evidenceViewer.url}
                  alt={evidenceViewer.fileName}
                  className="max-w-full max-h-[75vh] object-contain rounded shadow-sm bg-white"
                />
              ) : evidenceViewer.url && String(evidenceViewer.mimeType || '').includes('pdf') ? (
                <iframe
                  title={evidenceViewer.fileName}
                  src={evidenceViewer.url}
                  className="w-full h-[75vh] rounded border border-slate-200 bg-white"
                />
              ) : evidenceViewer.url ? (
                <div className="text-center text-sm text-slate-600 space-y-3">
                  <FileText size={36} className="mx-auto text-slate-400" />
                  <div>Preview not available for this file type.</div>
                  <a
                    href={evidenceViewer.url}
                    download={evidenceViewer.fileName}
                    className="inline-flex py-1.5 px-3 text-xs font-semibold rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                  >
                    Download file
                  </a>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
